import { Fragment, type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import JSZip from 'jszip';
import styled, { keyframes } from 'styled-components';
import Notification from '../../components/Notification/Notification';
import PageTemplate from '../../components/PageTemplate/PageTemplate';
import ManageValuesButton from '../ManagedOptions/ManageValuesButton';
import Tag from '../../components/Tag/Tag';
import { buildExportHtml } from '../../components/Download/Download';
import ScsReports from './ScsReports';
import config from '../../config';
import content from '../../content';
import { useAuthContext } from '../../auth/AuthContext';
import { useUiContext } from '../../context/UiContext';
import { useFormConfig } from '../../hooks/useFormConfig';
import { useFormData } from '../../hooks/useFormData';
import PsfaCdrImport from '../../components/form/PsfaCdrImport';
import {
  InlineExcelActionButton,
  UploadIcon,
} from '../../components/form/bulk-table/ExcelToolbar';
import ExcelUploadDialog from '../../components/form/bulk-table/ExcelUploadDialog';
import {
  convertExportHtmlToPdf,
  downloadAdminBulkExcelExport,
  deleteForm,
  deleteDataError,
  deleteFeedback,
  downloadExportExcel,
  downloadExportRenderedHtml,
  downloadAdminProgressStats,
  fetchAdminBulkExcelExportStatus,
  fetchSqlReport,
  fetchSqlReports,
  fetchSqlReportStatus,
  addFunctionMember,
  fetchFeedback,
  fetchDataErrors,
  fetchFunctionMembers,
  fetchAdminProgressStatsRows,
  fetchForm,
  fetchFormUsers,
  fetchPsfaAudit,
  approveForm,
  buildRewardCosReconciliationReport,
  buildRewardVoucherBulkUpload,
  fetchRewardCosAudit,
  fetchRewardTransferOwnershipCandidates,
  queryAthenaLookup,
  recordRewardQaDataErrorAudit,
  recordRewardVoucherBulkUpload,
  recordRewardVoucherIssued,
  removeFunctionMember,
  startAdminBulkExcelExport,
  startSqlReport,
  downloadSqlReport,
  transferRewardOwnership,
  updateSqlReport,
  validateSqlReport,
  updateForm,
} from '../../service/form';
import { Query, Status } from '../../types';
import type { FormConfig, PageList, Question } from '../../config/types';
import {
  canEditApprovalBeforeClose,
  extractConditionalQuestions,
  getConditionalChildRouting,
  getEnabledSubsections,
  hasIncompleteRequiredSubsections,
  getLockedSections,
  getLockedSubsections,
  getOverviewStatus,
  isSectionAvailable,
  isPageDisabled,
  isSubsectionNotRequired,
  isWorkflowOnlyPageName,
  isPageCompleted,
} from '../../utils/config-utils';
import { isAdminPreviewMode, parseOptionalBoolValue } from '../../utils/admin-preview';
import { resolveSelectedFormId, setSelectedFormIdInSession } from '../../utils/form-id';
import { getListAnswers, hasValue } from '../../utils/form-utils';
import { formatLondonDateTime, parseLondon, isPastLondon, londonNow } from '../../utils/londonTime';
import { DeclarationWrapper, SectionListWrapper } from './Wrappers';
import type {
  AdminBulkExportJob,
  ApprovalRequestRecord,
  DataErrorEntry,
  FeedbackEntry,
  FormAccessEntry,
  FunctionMemberEntry,
  FunctionMembersResponse,
  FormListItem,
  RewardCosAuditEntry,
  RewardTransferOwnershipCandidate,
  SqlReportDetail,
  SqlReportDefinition,
  SqlReportJob,
} from '../../service/types';
import type { AthenaLookupEnvironment } from '../../service/types';
import { ApiError } from '../../utils/api-error';

const MS_IN_DAY = 1000 * 60 * 60 * 24;
const IN_PROGRESS_KPI_WINDOW_DAYS = 30;
const REWARD_RR_POT_TOTAL = 324910;
const DATA_ERROR_PAGE_SIZE = 50;
const EMPTY_VALUE = '\u2014';
const REWARD_ADMIN_PAGE_SIZES = [25, 50, 100, 200] as const;
const DEFAULT_REWARD_ADMIN_PAGE_SIZE = 50;

type RewardAdminPageSize = (typeof REWARD_ADMIN_PAGE_SIZES)[number];
type OverviewPaginationItem = number | 'ellipsis';

export const buildOverviewPaginationItems = (
  currentPage: number,
  totalPages: number,
): OverviewPaginationItem[] => {
  if (totalPages <= 1) return [1];
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = Array.from(new Set([1, currentPage - 1, currentPage, currentPage + 1, totalPages]))
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((left, right) => left - right);

  return pages.flatMap((page, index) => {
    const previousPage = pages[index - 1];
    if (previousPage === undefined || page - previousPage === 1) return [page];
    if (page - previousPage === 2) return [previousPage + 1, page];
    return ['ellipsis' as const, page];
  });
};

type RewardCosAuditDialogState = {
  formId: string;
  identifier: string;
  awardType: string;
  audit: RewardCosAuditEntry[];
};

type PsfaAuditDialogState = {
  formId: string;
  identifier: string;
  audit: RewardCosAuditEntry[];
};

const ADMIN_AUDIT_SENT_TO_ACTIONS = new Set(['internal approval', 'parent approval']);

const shouldShowAdminAuditSentTo = (row: RewardCosAuditEntry) =>
  Boolean(
    row.sentTo &&
      (ADMIN_AUDIT_SENT_TO_ACTIONS.has(
        String(row.action || '')
          .trim()
          .toLowerCase(),
      ) ||
        /^Q[1-4] Awaiting manager sign-off$/i.test(String(row.action || '').trim())),
  );

type PsfaAccessDialogState = {
  identifier: string;
  users: FormAccessEntry[];
};

type RewardCosVoucherBulkUploadStatus = 'pending' | 'issued' | 'error';
type RewardCosVoucherStatusOverride = Partial<
  Pick<
    FormListItem,
    | 'rewardCosVoucherIssued'
    | 'rewardCosVoucherIssuedAt'
    | 'rewardCosVoucherBulkUploaded'
    | 'rewardCosVoucherBulkUploadStatus'
    | 'rewardCosVoucherBulkUploadIssuedAt'
    | 'rewardCosVoucherBulkUploadErrorAt'
    | 'rewardCosVoucherStatusDate'
  >
>;
type PsfaAdminKpiStatus =
  | 'inProgress'
  | 'internalApproval'
  | 'parentApproval'
  | 'psfaApproval'
  | 'complete';
type ScsAdminKpiQuarter = 'Q1' | 'Q2' | 'Q3' | 'Q4';
type ScsAdminKpiStatus = 'inProgress' | 'awaitingApproval' | 'approved' | 'approvedStaffMissing';

type OverviewTableSortColumn = 'primaryDate' | 'lastActive';
type OverviewTableSortDirection = 'asc' | 'desc';
type OverviewTableSort = {
  column: OverviewTableSortColumn;
  direction: OverviewTableSortDirection;
} | null;

const PSFA_ADMIN_KPI_DEFINITIONS: Array<{
  status: PsfaAdminKpiStatus;
  label: string;
  testId: string;
}> = [
  { status: 'inProgress', label: 'In Progress', testId: 'admin-kpi-in-progress' },
  {
    status: 'internalApproval',
    label: 'Internal Approval',
    testId: 'admin-kpi-internal-approval',
  },
  { status: 'parentApproval', label: 'Parent Approval', testId: 'admin-kpi-parent-approval' },
  { status: 'psfaApproval', label: 'PSFA Approval', testId: 'admin-kpi-psfa-approval' },
  { status: 'complete', label: 'Complete', testId: 'admin-kpi-complete' },
];

const SCS_ADMIN_KPI_QUARTERS: ScsAdminKpiQuarter[] = ['Q1', 'Q2', 'Q3', 'Q4'];
const SCS_REPORTING_QUARTER_OPTIONS = ['Quarter 1', 'Quarter 2', 'Quarter 3', 'Quarter 4'];
const SCS_ADMIN_KPI_STATUSES: Array<{
  status: ScsAdminKpiStatus;
  label: string;
}> = [
  { status: 'inProgress', label: 'Awaiting staff completion' },
  { status: 'awaitingApproval', label: 'Awaiting manager sign-off' },
  { status: 'approvedStaffMissing', label: 'Approved Staff input missing' },
  { status: 'approved', label: 'Complete' },
];

const getApprovalRequestRoute = (entry: ApprovalRequestRecord) =>
  String(entry.approvalRoute || entry.approval_route || '').trim();

const getLatestApprovalRequestRoute = (
  history: FormListItem['approvalRequestHistory'] = [],
): string => {
  for (let index = history.length - 1; index >= 0; index -= 1) {
    const route = getApprovalRequestRoute(history[index]);
    if (route) return route;
  }
  return '';
};

const getPsfaAdminKpiStatus = (form: FormListItem): PsfaAdminKpiStatus => {
  const state = String(form.formState || '')
    .trim()
    .toLowerCase();
  if (state === 'approved') return 'complete';
  if (state === 'review' || state === 'submitted') return 'psfaApproval';
  if (state === 'approval') {
    const approvalRoute = getLatestApprovalRequestRoute(form.approvalRequestHistory).toLowerCase();
    if (approvalRoute === 'parent approval') return 'parentApproval';
    return 'internalApproval';
  }
  return 'inProgress';
};

const getDataErrorEntryId = (entry: Pick<DataErrorEntry, 'submittedAt'>) =>
  String(entry.submittedAt || '').trim();

const getDataErrorOutcome = (entry: Pick<DataErrorEntry, 'outcome'>) =>
  entry.outcome === 'accepted' || entry.outcome === 'rejected' ? entry.outcome : undefined;

const hasActiveDataErrorEntries = (
  entries: DataErrorEntry[],
  decisions: Record<string, 'accepted' | 'rejected'> = {},
) => countActiveDataErrorEntries(entries, decisions) > 0;

const countActiveDataErrorEntries = (
  entries: DataErrorEntry[],
  decisions: Record<string, 'accepted' | 'rejected'> = {},
) =>
  entries.filter((entry) => {
    const submittedAt = getDataErrorEntryId(entry);
    return (
      Boolean(submittedAt) &&
      !String(entry.deletedAt || '').trim() &&
      !decisions[submittedAt] &&
      !getDataErrorOutcome(entry)
    );
  }).length;

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const RepeatableQuestionIndicator = ({ testId }: { testId: string }) => {
  const tooltipId = useId();
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const isTooltipVisible = isHovered || isFocused || isClicked;

  return (
    <span className="app-repeatable-question-indicator">
      <button
        type="button"
        className="app-repeatable-question-indicator__button"
        aria-label="This is a repeatable question"
        aria-describedby={isTooltipVisible ? tooltipId : undefined}
        data-testid={testId}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          setIsClicked(false);
        }}
        onClick={() => setIsClicked(true)}
      >
        <svg viewBox="0 0 20 16" aria-hidden="true" focusable="false">
          <line data-testid="repeatable-question-line" x1="2" y1="3" x2="18" y2="3" />
          <line data-testid="repeatable-question-line" x1="2" y1="8" x2="18" y2="8" />
          <line data-testid="repeatable-question-line" x1="2" y1="13" x2="18" y2="13" />
        </svg>
      </button>
      <span
        id={tooltipId}
        className="app-repeatable-question-indicator__tooltip"
        role="tooltip"
        hidden={!isTooltipVisible}
      >
        This is a repeatable question
      </span>
    </span>
  );
};

const PdfLoadingOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2200;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.88);
`;

const PdfLoadingSpinner = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
  margin: 0 auto;
`;

const PdfLoadingSpinnerRing = styled.div`
  position: absolute;
  inset: 0;
  border: 12px solid #b1b4b6;
  border-top-color: #1d70b8;
  border-radius: 50%;
  animation: ${spin} 0.9s linear infinite;
`;

const PdfLoadingSpinnerValue = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: #0b0c0c;
`;

const PdfLoadingLabel = styled.p`
  margin-top: 20px;
  margin-bottom: 0;
  text-align: center;
`;

const AdminDownloadButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'highlighted',
})<{ highlighted?: boolean }>`
  ${(props) =>
    props.highlighted
      ? `
    background: #1d70b8;
    box-shadow: 0 0 0 4px #ffdd00;
    color: #ffffff;

    &:hover:not(:disabled) {
      background: #003078;
      color: #ffffff;
    }
  `
      : ''}
`;

const SortHeaderButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  font-weight: inherit;
  text-align: left;
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 0.12em;

  &:focus {
    outline: 3px solid #ffdd00;
    outline-offset: 2px;
    box-shadow: 0 0 0 2px #0b0c0c;
  }
`;

const SortHeaderIndicator = styled.span`
  display: inline-block;
  min-width: 1em;
  font-size: 0.85em;
  line-height: 1;
`;

const AdminKpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const AdminKpiCard = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  height: 100%;
  border: 1px solid #b1b4b6;
  background: #ffffff;
  padding: 16px;
  container-type: inline-size;
`;

const AdminKpiLabel = styled.h3`
  margin: 0 0 8px;
`;

const AdminKpiFunctionLabel = styled(AdminKpiLabel)`
  font-size: calc(1.1875rem - 1pt);
`;

const AdminKpiValue = styled.p`
  margin: 0;
  font-size: 3rem;
  line-height: 1;
  font-weight: 700;
`;

const AdminKpiCostValue = styled(AdminKpiValue)`
  font-size: clamp(1.75rem, 18cqw, 3rem);
  overflow-wrap: anywhere;
`;

const AdminKpiPendingText = styled.p`
  margin: 8px 0 0;
  color: #505a5f;
  font-size: 0.8rem;
  line-height: 1.25;
  font-weight: 400;
`;

const AdminKpiBreakdown = styled.div`
  margin: 10px 0 0;
  color: #505a5f;
  font-size: 0.85rem;
  line-height: 1.35;

  li {
    padding-bottom: 0;
  }
`;

const AdminKpiBreakdownLine = styled.p`
  && {
    margin: 0;
  }
`;

const AdminKpiSubLabel = styled(AdminKpiPendingText)`
  margin: 0 0 8px;
`;

const AdminFeedbackBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background-color: rgba(11, 12, 12, 0.65);
`;

const AdminFeedbackDialog = styled.div`
  width: min(980px, 100%);
  max-height: min(80vh, 900px);
  overflow: auto;
  background: #ffffff;
  border: 3px solid #0b0c0c;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
`;

const SqlReportsDialog = styled(AdminFeedbackDialog)`
  width: min(640px, 100%);
`;

const SqlReportEditorDialog = styled(AdminFeedbackDialog)`
  width: min(1100px, 100%);
`;

const SqlReportActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  .govuk-button {
    margin-bottom: 0;
  }
`;

const SqlReportCheckingStatus = styled.p`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

const SqlReportCheckingSpinner = styled.span`
  width: 22px;
  height: 22px;
  box-sizing: border-box;
  border: 3px solid #b1b4b6;
  border-top-color: #1d70b8;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const SqlReportQueryEditorShell = styled.div`
  position: relative;
  width: 100%;
  background: #ffffff;
`;

const SqlReportQueryHighlightLayer = styled.pre`
  position: absolute;
  inset: 0;
  z-index: 0;
  box-sizing: border-box;
  width: 100%;
  min-height: 480px;
  margin: 0;
  padding: 5px;
  overflow: hidden;
  border: 2px solid transparent;
  color: transparent;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace;
  font-size: 16px;
  line-height: 1.45;
  tab-size: 2;
  pointer-events: none;
`;

const SqlReportPlaceholderHighlight = styled.mark`
  padding: 0;
  color: transparent;
  background: #ffdd00;
`;

const SqlReportQueryEditor = styled.textarea`
  position: relative;
  z-index: 1;
  display: block;
  margin-bottom: 0;
  background: transparent;
  min-height: 480px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace;
  font-size: 16px;
  line-height: 1.45;
  tab-size: 2;
`;

const SQL_REPORT_PLACEHOLDER_PATTERN = /(\{\{[^{}\r\n]+\}\})/g;
const SQL_REPORT_PLACEHOLDER_EXACT_PATTERN = /^\{\{[^{}\r\n]+\}\}$/;

type SqlReportQueryEditorWithHighlightsProps = {
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
};

const SqlReportQueryEditorWithHighlights = ({
  value,
  disabled,
  onChange,
}: SqlReportQueryEditorWithHighlightsProps) => {
  const highlightLayerRef = useRef<HTMLPreElement>(null);

  return (
    <SqlReportQueryEditorShell>
      <SqlReportQueryHighlightLayer ref={highlightLayerRef} aria-hidden="true">
        {value.split(SQL_REPORT_PLACEHOLDER_PATTERN).map((part, index) =>
          SQL_REPORT_PLACEHOLDER_EXACT_PATTERN.test(part) ? (
            <SqlReportPlaceholderHighlight
              // The query can legitimately contain the same placeholder more than once.
              key={`${index}-${part}`}
              data-testid="sql-report-placeholder-highlight"
            >
              {part}
            </SqlReportPlaceholderHighlight>
          ) : (
            part
          ),
        )}
        {value.endsWith('\n') ? '\n' : null}
      </SqlReportQueryHighlightLayer>
      <SqlReportQueryEditor
        className="govuk-textarea"
        id="sql-report-query"
        name="sql-report-query"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onScroll={(event) => {
          if (!highlightLayerRef.current) return;
          highlightLayerRef.current.scrollTop = event.currentTarget.scrollTop;
          highlightLayerRef.current.scrollLeft = event.currentTarget.scrollLeft;
        }}
        disabled={disabled}
        spellCheck={false}
      />
    </SqlReportQueryEditorShell>
  );
};

const SqlReportPlaceholderList = styled.dl`
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 8px 16px;
  margin: 0;

  dt,
  dd {
    margin: 0;
  }

  code {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace;
  }
`;

const AdminFeedbackHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
`;

const AdminFeedbackList = styled.div`
  display: grid;
  gap: 16px;
`;

const AdminFeedbackCard = styled.article`
  border: 1px solid #b1b4b6;
  padding: 16px;
  background: #ffffff;

  &[data-decision='accepted'] {
    border: 3px solid #00703c;
  }

  &[data-decision='rejected'] {
    border: 3px solid #d4351c;
  }
`;

const AdminFeedbackCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const DataErrorActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const DataErrorDecisionButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'variant',
})<{ variant: 'accept' | 'reject' }>`
  width: 42px;
  min-width: 42px;
  height: 42px;
  padding: 0;
  border: 2px solid ${(props) => (props.variant === 'accept' ? '#00703c' : '#d4351c')};
  border-radius: 0;
  color: #ffffff;
  background: ${(props) => (props.variant === 'accept' ? '#00703c' : '#d4351c')};
  font-size: 1.45rem;
  font-weight: 700;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${(props) => (props.variant === 'accept' ? '#005a30' : '#aa2a16')};
    border-color: ${(props) => (props.variant === 'accept' ? '#005a30' : '#aa2a16')};
  }

  &:focus {
    outline: 3px solid #ffdd00;
    outline-offset: 0;
    box-shadow: inset 0 0 0 2px #0b0c0c;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AdminActionButtons = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const MyFormsRowActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;

  .govuk-button {
    margin-bottom: 0;
  }
`;

const MyFormsDeleteButton = styled.button`
  && {
    width: 40px;
    min-width: 40px;
    height: 40px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0;
    background: #d4351c;
    color: #ffffff;
    box-shadow: none;
  }

  &&:hover:not(:disabled) {
    background: #aa2a16;
  }

  &&:focus {
    outline: 3px solid #ffdd00;
    outline-offset: 0;
    box-shadow: inset 0 0 0 2px #0b0c0c;
  }

  &&:disabled {
    opacity: 0.6;
  }
`;

const MyFormsDownloadDialog = styled.div`
  width: min(640px, 100%);
  max-height: min(80vh, 720px);
  overflow: auto;
  background: #ffffff;
  border: 3px solid #0b0c0c;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
`;

const MyFormsDownloadActionRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: flex-end;

  .govuk-button {
    margin-bottom: 0;
  }
`;

const MyFormsDownloadCancelRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;

  .govuk-button {
    margin-bottom: 0;
  }
`;

const RewardVoucherFieldRows = styled.dl`
  display: grid;
  grid-template-columns: minmax(160px, 220px) minmax(280px, 1fr);
  gap: 12px 16px;
  margin: 0 0 24px;
  align-items: center;

  dt {
    font-weight: 700;
  }

  dd {
    margin: 0;
  }
`;

const RewardVoucherValue = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 520px;
`;

const RewardVoucherValueInput = styled.input`
  flex: 1 1 auto;
  min-width: 0;
`;

const CopyValueButton = styled.button`
  min-width: 32px;
  width: 32px;
  height: 32px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const StatusTagDate = styled.span`
  font-size: 0.7em;
  line-height: 1.1;
`;

const toDate = (value?: string) => parseLondon(value ?? undefined);

const formatAuditDateTime = (value?: string) => formatLondonDateTime(value) || EMPTY_VALUE;

const PSFA_ACCESS_GROUPS = [
  { key: 'owners', label: 'Owners', roles: ['owner'] },
  { key: 'editors', label: 'Editors', roles: ['editor'] },
  { key: 'approvers', label: 'Approvers', roles: ['approver'] },
  { key: 'viewers', label: 'Viewers', roles: ['read_only', 'viewer', 'view'] },
  { key: 'other', label: 'Anyone else', roles: [] },
] as const;

const getPsfaAccessRole = (user: FormAccessEntry) =>
  String(user.role || '')
    .trim()
    .toLowerCase();

const getPsfaAccessGroupKey = (user: FormAccessEntry) => {
  const role = getPsfaAccessRole(user);
  const group = PSFA_ACCESS_GROUPS.find((item) => (item.roles as readonly string[]).includes(role));
  return group?.key || 'other';
};

const getPsfaAccessPerson = (user: FormAccessEntry) =>
  String(user.email || '').trim() || EMPTY_VALUE;

const getSortedPsfaAccessUsers = (users: FormAccessEntry[]) => {
  const groupIndex = new Map(PSFA_ACCESS_GROUPS.map((group, index) => [group.key, index]));
  return [...users].sort((a, b) => {
    const groupDelta =
      (groupIndex.get(getPsfaAccessGroupKey(a)) ?? 99) -
      (groupIndex.get(getPsfaAccessGroupKey(b)) ?? 99);
    if (groupDelta !== 0) return groupDelta;
    return getPsfaAccessPerson(a).localeCompare(getPsfaAccessPerson(b));
  });
};

const formatDate = (value?: string) => {
  const parsed = toDate(value);
  if (!parsed) return EMPTY_VALUE;
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(parsed);
};

const resolveRewardTransferAthenaEnvironment = (): AthenaLookupEnvironment => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const apiUrl = String(config.apiUrl || '').toLowerCase();
  return hostname.includes('preprod') || apiUrl.includes('preprod') ? 'preprod' : 'prod';
};

export const getApprovedStatusLabel = (_approvedAt?: string) => {
  return 'Approved';
};

const getStatusEnteredDate = (form: FormListItem, tenant?: string, label = '') => {
  if (
    tenant === 'scs' &&
    (label === 'Up to date' ||
      /^Q[1-4] Complete(?: staff completion missing)?$/.test(label) ||
      label === 'Q4 Submitted')
  ) {
    const approvedDate = formatDate(form.approvedAt);
    return approvedDate === EMPTY_VALUE ? '' : approvedDate;
  }
  const explicit = formatDate(form.statusEnteredAt);
  if (explicit !== EMPTY_VALUE) return explicit;

  const state = String(form.formState || '')
    .trim()
    .toLowerCase();
  if (state === 'approved' || state === 'submitted') {
    const approvedDate = formatDate(form.approvedAt);
    if (approvedDate !== EMPTY_VALUE) return approvedDate;
  }

  return '';
};

const renderStatusTag = (
  form: FormListItem,
  label: string,
  className: string,
  showDate: boolean,
  tenant?: string,
) => {
  const enteredDate = showDate ? getStatusEnteredDate(form, tenant, label) : '';
  if (!enteredDate) {
    return <span className={`govuk-tag ${className}`}>{label}</span>;
  }

  return (
    <span className={`govuk-tag ${className}`}>
      <StatusTagDate>{enteredDate}</StatusTagDate>
      <br />
      <span>{label}</span>
    </span>
  );
};

const formatDaysAgo = (value?: string, fallback?: string) => {
  const parsed = toDate(value || fallback);
  if (!parsed) return EMPTY_VALUE;
  const diffMs = Date.now() - parsed.getTime();
  const days = Math.max(0, Math.floor(diffMs / MS_IN_DAY));
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
};

const parseAllowUnlock = (allowUnlock?: number | string) => {
  if (typeof allowUnlock === 'number') {
    return { type: 'daysFromSubmit' as const, days: allowUnlock };
  }

  if (typeof allowUnlock === 'string') {
    const trimmed = allowUnlock.trim();

    if (/^formclose/i.test(trimmed)) {
      const match = trimmed.match(/formclose\s*(\+|-)\s*(\d+)/i);
      const offset = match ? Number(`${match[1]}${match[2]}`) : 0;
      return { type: 'formClose' as const, offset };
    }

    const numeric = Number(trimmed);
    if (!Number.isNaN(numeric)) {
      return { type: 'daysFromSubmit' as const, days: numeric };
    }
  }

  return null;
};

const computeUnlockDeadline = (
  allowUnlock: number | string | undefined,
  submittedAt?: string,
  formCloseAt?: string,
) => {
  const parsed = parseAllowUnlock(allowUnlock);
  if (!parsed) return null;

  if (parsed.type === 'daysFromSubmit') {
    const submitted = toDate(submittedAt);
    if (!submitted) return null;
    return new Date(submitted.getTime() + parsed.days * MS_IN_DAY);
  }

  const closeDate = toDate(formCloseAt);
  if (!closeDate) return null;
  return new Date(closeDate.getTime() + parsed.offset * MS_IN_DAY);
};

const hasExplicitDeadlineTime = (value?: string) =>
  /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(String(value || '').trim());

const getLondonDateParts = (date: Date) => {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const get = (type: string) => fmt.find((p) => p.type === type)?.value || '00';
  return {
    y: Number(get('year')),
    m: Number(get('month')),
    d: Number(get('day')),
  };
};

const canUpdateSubmittedForm = (
  form: FormListItem,
  allowUnlock: number | string | undefined,
  formCloseAt?: string,
) => {
  const role = String(form.role || '').toLowerCase();
  if (
    String(form.formState || '')
      .trim()
      .toLowerCase() !== 'submitted'
  )
    return false;
  if (form.hasApprover || !isEditableRole(role)) return false;

  const deadline = computeUnlockDeadline(allowUnlock, form.submittedAt, formCloseAt);
  if (!deadline) return false;

  if (hasExplicitDeadlineTime(formCloseAt)) {
    return londonNow().getTime() < deadline.getTime();
  }

  const today = getLondonDateParts(londonNow());
  const deadlineParts = getLondonDateParts(deadline);
  const todayStartUtc = Date.UTC(today.y, today.m - 1, today.d);
  const deadlineStartUtc = Date.UTC(deadlineParts.y, deadlineParts.m - 1, deadlineParts.d);
  const remainingDays = Math.max(0, Math.floor((deadlineStartUtc - todayStartUtc) / MS_IN_DAY));

  return remainingDays > 0;
};

const normalizeQuarterCode = (value?: string | null) => {
  const match = String(value || '')
    .trim()
    .toUpperCase()
    .match(/\bQ([1-4])\b/);
  return match?.[1] ? `Q${match[1]}` : '';
};

const nextScsQuarterAfterCompleted = (quarter: string) => {
  if (quarter === 'Q1') return 'Q2';
  if (quarter === 'Q2') return 'Q3';
  if (quarter === 'Q3') return 'Q4';
  if (quarter === 'Q4') return 'Q4';
  return 'Q1';
};

const SCS_QUARTER_ORDER: Record<string, number> = {
  Q1: 1,
  Q2: 2,
  Q3: 3,
  Q4: 4,
};

const getCurrentScsReportingYear = () => {
  const { y: year, m: month } = getLondonDateParts(londonNow());
  const reportingYearStart = month >= 4 ? year : year - 1;
  return `${reportingYearStart}-${String(reportingYearStart + 1).slice(-2)}`;
};

const getLatestAvailableScsQuarterForReportYear = (
  reportYear: string,
  sections: FormConfig['sections'],
) => {
  const formValues = {
    answers: {},
    reportPeriod: reportYear,
  };

  return sections.reduce((latestQuarter, section) => {
    const quarter = normalizeQuarterCode(section.quarterReview?.quarter);
    if (!quarter || !isSectionAvailable(section, formValues)) return latestQuarter;
    return SCS_QUARTER_ORDER[quarter] > (SCS_QUARTER_ORDER[latestQuarter] || 0)
      ? quarter
      : latestQuarter;
  }, '');
};

const getLatestAvailableScsQuarter = (form: FormListItem, sections: FormConfig['sections']) =>
  form.scsProgress?.currentQuarter ?? getLatestAvailableScsQuarterForReportYear(reportPeriodFromForm(form), sections);

const scsReportingQuarterOptionFromCode = (quarter: string) =>
  quarter ? `Quarter ${quarter.slice(1)}` : '';

const getApprovalRoundLabel = (form: FormListItem) => {
  const approvalRound = Number(form.approvalRound || 0);
  if (!Number.isFinite(approvalRound) || approvalRound <= 1) return '';
  const suffix =
    approvalRound % 100 >= 11 && approvalRound % 100 <= 13
      ? 'th'
      : approvalRound % 10 === 1
        ? 'st'
        : approvalRound % 10 === 2
          ? 'nd'
          : approvalRound % 10 === 3
            ? 'rd'
            : 'th';
  return `${approvalRound}${suffix} Approval`;
};

const getScsCompletedQuarter = (form: FormListItem) => {
  const statusMatch = String(form.approvalStatus || '')
    .trim()
    .match(/^Q([1-4])\s+Complete$/i);
  const reportYear = parseReportPeriodYear(reportPeriodFromForm(form));
  return [
    form.approvedQuarter,
    ...(form.approvedQuarters || []),
    statusMatch?.[1] ? `Q${statusMatch[1]}` : '',
    ...approvalPeriodsFromForm(form).filter(
      (period) => !reportYear || parseReportPeriodYear(period) === reportYear,
    ),
  ].reduce<string>((latest, value) => {
    const quarter = normalizeQuarterCode(value);
    return (SCS_QUARTER_ORDER[quarter] || 0) > (SCS_QUARTER_ORDER[latest] || 0)
      ? quarter
      : latest;
  }, '');
};

const getScsStatusLabel = (
  form: FormListItem,
  formCloseAt?: string,
  strictDeadline?: boolean,
  sections: FormConfig['sections'] = [],
) => {
  const state = String(form.formState || '')
    .trim()
    .toLowerCase();
  const role = String(form.role || '')
    .trim()
    .toLowerCase();
  const closedAt = toDate(formCloseAt);

  if (form.scsProgress?.onHold) return 'On Hold';
  if (state === 'expired') return 'Expired';
  if (strictDeadline && closedAt && closedAt.getTime() < Date.now()) return 'Expired';
  if (state === 'rejected') return 'Rejected';
  if (state === 'withdrawn') return 'Withdrawn';

  if (form.scsProgress) {
    const quarter = form.scsProgress.currentQuarter;
    const progress = form.scsProgress.quarters[quarter];
    if (!progress || role === 'admin') return form.scsProgress.status;
    if (progress.status === 'approved' && quarter !== 'Q4') return 'Up to date';
    if (progress.status === 'awaitingApproval') {
      return isApprovalActor(form) ? `${quarter} Sign-off required` : `${quarter} Awaiting sign-off`;
    }
    if (progress.status === 'inProgress' && (role === 'owner' || role === 'editor')) {
      return `${quarter} In Progress`;
    }
    return progress.label;
  }

  const completedQuarter = getScsCompletedQuarter(form);
  const latestAvailableQuarter = getLatestAvailableScsQuarter(form, sections);
  const pendingQuarter =
    state === 'approval'
      ? normalizeQuarterCode(form.pendingApprovalQuarter) || normalizeQuarterCode(form.approvalStatus)
      : '';
  const latestRelevantQuarter =
    (SCS_QUARTER_ORDER[pendingQuarter] || 0) > (SCS_QUARTER_ORDER[latestAvailableQuarter] || 0)
      ? pendingQuarter
      : latestAvailableQuarter;

  if (
    latestRelevantQuarter &&
    (form.staffCompletionMissingQuarters ?? form.staffCompletionRequiredQuarters ?? []).includes(
      latestRelevantQuarter,
    ) &&
    SCS_QUARTER_ORDER[completedQuarter] >= SCS_QUARTER_ORDER[latestRelevantQuarter]
  ) {
    return `${latestRelevantQuarter} Complete staff completion missing`;
  }

  if (state === 'active' || state === 'approval') {
    if (completedQuarter === 'Q4') return 'Q4 Submitted';
    if (
      completedQuarter &&
      latestRelevantQuarter &&
      SCS_QUARTER_ORDER[completedQuarter] >= SCS_QUARTER_ORDER[latestRelevantQuarter]
    ) {
      return 'Up to date';
    }
  }

  if (state === 'approval' && (!pendingQuarter || pendingQuarter === latestRelevantQuarter)) {
    const approvalRoundLabel = getApprovalRoundLabel(form);
    if (approvalRoundLabel) return approvalRoundLabel;
    if (!pendingQuarter) return 'Approval';
    if (isApprovalActor(form)) return `${pendingQuarter} Sign-off required`;
    if (role === 'owner' || role === 'editor') {
      return `${pendingQuarter} Awaiting sign-off`;
    }
    if (role === 'admin') return `${pendingQuarter} Awaiting manager sign-off`;
    return `${pendingQuarter} Approval`;
  }

  if (state === 'active' || state === 'approval') {
    const currentQuarter = latestRelevantQuarter || nextScsQuarterAfterCompleted(completedQuarter);
    return isApprovalActor(form) || role === 'admin'
      ? `${currentQuarter} Awaiting staff completion`
      : `${currentQuarter} In Progress`;
  }

  if (state === 'submitted' || state === 'approved' || state === 'exported' || state === 'review') {
    return 'Q4 Submitted';
  }

  return 'Q1 In Progress';
};

const isApprovalActor = (form: FormListItem) =>
  String(form.role || '').toLowerCase() === 'approver' || form.isAssignedApprover === true;

const isRewardInfoOnlyForm = (form: FormListItem) => form.rewardInfoOnly === true;

const isRewardCosVoucherForm = (form: FormListItem) => form.rewardCosVoucher === true;

const getRewardAwardTypeText = (form?: FormListItem | null) =>
  String(
    (form as any)?.rewardAwardType ||
      form?.rewardCosAwardType ||
      (form as any)?.rewardType ||
      (form as any)?.awardType ||
      '',
  ).trim();

const normalizeRewardAwardType = (form?: FormListItem | null) =>
  getRewardAwardTypeText(form).toLowerCase();

const isRewardInYearBonusType = (form?: FormListItem | null) => {
  const normalized = normalizeRewardAwardType(form);
  return normalized.includes('bonus') || normalized.includes('iyb');
};

const isRewardVoucherType = (form?: FormListItem | null) =>
  normalizeRewardAwardType(form).includes('voucher');

const getRewardAwardTypeLabel = (form?: FormListItem | null) => {
  if (isRewardInYearBonusType(form)) return 'In year bonus';
  if (isRewardVoucherType(form)) return 'Voucher';
  return getRewardAwardTypeText(form) || EMPTY_VALUE;
};

const isRewardCosInYearBonusForm = (form?: FormListItem | null) => isRewardInYearBonusType(form);

const compareDates = (
  left: Date | null | undefined,
  right: Date | null | undefined,
  direction: 'asc' | 'desc',
) => {
  if (left && right) {
    return direction === 'asc'
      ? left.getTime() - right.getTime()
      : right.getTime() - left.getTime();
  }
  if (left) return -1;
  if (right) return 1;
  return 0;
};

const getOverviewTableSortDate = (
  form: FormListItem,
  column: OverviewTableSortColumn,
  isRewardAdminView: boolean,
  isRewardCosAdminView: boolean,
) => {
  if (column === 'lastActive') {
    return toDate(form.lastActive) || toDate(form.createdAt);
  }
  if (isRewardCosAdminView) return toDate(form.approvedAt);
  return toDate(isRewardAdminView ? form.submittedAt : form.createdAt);
};

const getNextOverviewTableSort = (
  current: OverviewTableSort,
  column: OverviewTableSortColumn,
): OverviewTableSort => {
  if (!current || current.column !== column) return { column, direction: 'desc' };
  if (current.direction === 'desc') return { column, direction: 'asc' };
  return null;
};

const getOverviewTableHeaderSort = (
  current: OverviewTableSort,
  column: OverviewTableSortColumn,
) => {
  if (!current || current.column !== column) return 'none';
  return current.direction === 'desc' ? 'descending' : 'ascending';
};

const getOverviewTableSortIndicator = (
  current: OverviewTableSort,
  column: OverviewTableSortColumn,
) => {
  if (!current || current.column !== column) return '';
  return current.direction === 'desc' ? '▼' : '▲';
};

const getRewardPanelDecisionSortDate = (form: FormListItem) =>
  toDate(form.submittedAt) || toDate(form.createdAt) || toDate(form.lastActive);

const getRewardApprovedSortDate = (form: FormListItem) =>
  toDate(form.approvedAt) ||
  toDate(form.statusEnteredAt) ||
  toDate(form.lastActive) ||
  toDate(form.createdAt);

const getRewardCosSortDate = (form: FormListItem) =>
  toDate(form.rewardCosVoucherStatusDate) ||
  toDate(form.rewardCosVoucherIssuedAt) ||
  toDate(form.approvedAt) ||
  toDate(form.submittedAt) ||
  toDate(form.createdAt);

const compareRewardFunctionAdminForms = (a: FormListItem, b: FormListItem) => {
  const aPending = String(a.formState || '').toLowerCase() === 'approval';
  const bPending = String(b.formState || '').toLowerCase() === 'approval';
  if (aPending !== bPending) return aPending ? -1 : 1;

  const dateDiff = compareDates(
    aPending ? getRewardPanelDecisionSortDate(a) : getRewardApprovedSortDate(a),
    bPending ? getRewardPanelDecisionSortDate(b) : getRewardApprovedSortDate(b),
    aPending ? 'asc' : 'desc',
  );
  if (dateDiff !== 0) return dateDiff;
  return String(a.identifier || '').localeCompare(String(b.identifier || ''));
};

const getRewardCosVoucherBulkUploadStatus = (
  form?: FormListItem | null,
): RewardCosVoucherBulkUploadStatus | '' => {
  const status = String(form?.rewardCosVoucherBulkUploadStatus || '')
    .trim()
    .toLowerCase();
  return status === 'issued' || status === 'error' || status === 'pending' ? status : '';
};

const getRewardCosVoucherRowKey = (form?: FormListItem | null) =>
  [
    String(form?.formId || '').trim(),
    String(form?.rewardCosNomineeId || '')
      .trim()
      .toLowerCase(),
    String(form?.rewardCosAwardType || '')
      .trim()
      .toLowerCase(),
  ].join('|');

const isRewardCosVoucherBulkUploadPending = (form?: FormListItem | null) =>
  !!form &&
  !isRewardCosInYearBonusForm(form) &&
  form.rewardCosVoucherIssued !== true &&
  form.rewardCosVoucherBulkUploaded === true &&
  !['issued', 'error'].includes(getRewardCosVoucherBulkUploadStatus(form));

const isRewardCosVoucherBulkUploadIssued = (form?: FormListItem | null) =>
  !!form &&
  !isRewardCosInYearBonusForm(form) &&
  form.rewardCosVoucherBulkUploaded === true &&
  getRewardCosVoucherBulkUploadStatus(form) === 'issued';

const isRewardCosVoucherBulkUploadError = (form?: FormListItem | null) =>
  !!form &&
  !isRewardCosInYearBonusForm(form) &&
  form.rewardCosVoucherBulkUploaded === true &&
  getRewardCosVoucherBulkUploadStatus(form) === 'error';

const isRewardCosVoucherComplete = (form: FormListItem) =>
  isRewardCosInYearBonusForm(form)
    ? form.rewardCosVoucherIssued === true
    : form.rewardCosVoucherIssued === true || isRewardCosVoucherBulkUploadIssued(form);

const isRewardCosVoucherModalComplete = (form?: FormListItem | null) =>
  !!form && isRewardCosVoucherComplete(form);

const isRewardCosVoucherManualIssueChecked = (form?: FormListItem | null) =>
  !!form &&
  (isRewardCosInYearBonusForm(form)
    ? isRewardCosVoucherModalComplete(form)
    : form.rewardCosVoucherIssued === true);

const isRewardCosVoucherIssueRow = (form: FormListItem) =>
  isRewardCosVoucherForm(form) &&
  !isRewardCosInYearBonusForm(form) &&
  form.rewardCosVoucherIssued !== true &&
  (form.rewardCosVoucherBulkUploaded !== true || isRewardCosVoucherBulkUploadPending(form));

const getRewardCosAdminSortGroup = (form: FormListItem) => {
  if (isRewardCosVoucherComplete(form)) return 4;
  if (isRewardCosInYearBonusForm(form)) return 3;
  if (isRewardCosVoucherBulkUploadError(form)) return 2;
  if (isRewardCosVoucherBulkUploadPending(form)) return 1;
  return 0;
};

const compareRewardCosAdminForms = (a: FormListItem, b: FormListItem) => {
  const groupDiff = getRewardCosAdminSortGroup(a) - getRewardCosAdminSortGroup(b);
  if (groupDiff !== 0) return groupDiff;

  const dateDiff = compareDates(getRewardCosSortDate(a), getRewardCosSortDate(b), 'desc');
  if (dateDiff !== 0) return dateDiff;
  return String(a.identifier || '').localeCompare(String(b.identifier || ''));
};

const getRewardCosVoucherStatusLabel = (form: FormListItem) => {
  if (isRewardCosInYearBonusForm(form)) {
    return form.rewardCosVoucherIssued === true ? 'IYB Complete' : 'Confirm IYB';
  }
  if (form.rewardCosVoucherIssued === true) return 'Voucher manually issued';
  if (isRewardCosVoucherBulkUploadIssued(form)) return 'Issued via bulk upload';
  if (isRewardCosVoucherBulkUploadError(form)) return 'Bulk upload error';
  if (form.rewardCosVoucherBulkUploaded === true) return 'Voucher Bulk Upload';
  return 'Issue Voucher';
};

const getRewardCosVoucherStatusTagClass = (form: FormListItem) => {
  if (isRewardCosVoucherComplete(form)) return 'govuk-tag--green';
  if (isRewardCosVoucherBulkUploadError(form)) return 'govuk-tag--red';
  return 'govuk-tag--yellow';
};

const getRewardCosVoucherStatusDate = (form: FormListItem) =>
  formatDate(
    form.rewardCosVoucherStatusDate ||
      form.rewardCosVoucherIssuedAt ||
      form.rewardCosVoucherBulkUploadIssuedAt ||
      form.rewardCosVoucherBulkUploadErrorAt ||
      form.rewardCosVoucherBulkUploadedAt ||
      form.approvedAt,
  );

const getRewardCosActionLabel = (form: FormListItem) =>
  isRewardCosInYearBonusForm(form) ? 'View in year bonus details' : 'View voucher issue details';

const getRewardInfoOnlyStatusLabel = (form: FormListItem) => {
  const state = String(form.formState || '')
    .trim()
    .toLowerCase();
  return state === 'approval' ? 'Info: Awaiting Panel Decision' : 'Info: Awaiting Decision';
};

const getDefaultStatusLabel = (
  form: FormListItem,
  allowUnlock: number | string | undefined,
  hasAdminApprovalTarget: boolean,
  formCloseAt?: string,
  strictDeadline?: boolean,
  tenant?: string,
  sections: FormConfig['sections'] = [],
) => {
  const state = String(form.formState || '')
    .trim()
    .toLowerCase();
  const role = String(form.role || '').toLowerCase();
  const closedAt = toDate(formCloseAt);
  const approvalStatus = String(form.approvalStatus || '').trim();

  if (tenant === 'reward' && isRewardInfoOnlyForm(form)) {
    return getRewardInfoOnlyStatusLabel(form);
  }

  if (tenant === 'scs') {
    return getScsStatusLabel(form, formCloseAt, strictDeadline, sections);
  }

  if (state === 'approval') {
    const approvalRoundLabel = getApprovalRoundLabel(form);
    if (approvalRoundLabel) return approvalRoundLabel;
    return isApprovalActor(form) || (role === 'admin' && hasAdminApprovalTarget)
      ? 'Requires Approval'
      : role === 'owner' || role === 'editor'
        ? 'Awaiting Approval'
        : 'Approval';
  }
  if (state === 'review')
    return role === 'admin' ? 'Pending Review' : getApprovedStatusLabel(form.approvedAt);
  if (state === 'rejected') return 'Rejected';
  if (state === 'withdrawn') return 'Withdrawn';
  if (state === 'expired') return 'Expired';
  if (state === 'submitted')
    return form.hasApprover || hasAdminApprovalTarget
      ? getApprovedStatusLabel(form.approvedAt)
      : 'Submitted';
  if (state === 'approved') return getApprovedStatusLabel(form.approvedAt);
  if (state === 'exported') return 'Exported';
  if (strictDeadline && closedAt && closedAt.getTime() < Date.now()) return 'Expired';
  if (state === 'active' && approvalStatus) return approvalStatus;
  return 'In progress';
};

const getStatusLabel = (
  form: FormListItem,
  allowUnlock: number | string | undefined,
  hasAdminApprovalTarget: boolean,
  statusOverride: FormConfig['statusOverride'] | undefined,
  formCloseAt?: string,
  strictDeadline?: boolean,
  tenant?: string,
  sections: FormConfig['sections'] = [],
) => {
  const state = String(form.formState || '')
    .trim()
    .toLowerCase();
  const overrideLabel = statusOverride?.[state as keyof NonNullable<FormConfig['statusOverride']>];
  if (tenant === 'reward' && isRewardInfoOnlyForm(form)) {
    return getRewardInfoOnlyStatusLabel(form);
  }
  if (typeof overrideLabel === 'string' && overrideLabel.trim().length > 0) {
    const trimmedOverride = overrideLabel.trim();
    return trimmedOverride.toLowerCase() === 'approved'
      ? getApprovedStatusLabel(form.approvedAt)
      : trimmedOverride;
  }

  return getDefaultStatusLabel(
    form,
    allowUnlock,
    hasAdminApprovalTarget,
    formCloseAt,
    strictDeadline,
    tenant,
    sections,
  );
};

const getStatusTagClass = (label: string) => {
  const lower = label.toLowerCase();
  if (lower.startsWith('info:')) return 'govuk-tag--grey';
  if (lower.startsWith('submitted')) return 'govuk-tag--green';
  if (lower.startsWith('approved')) return 'govuk-tag--green';
  if (lower === 'up to date') return 'govuk-tag--green';
  if (lower.startsWith('in progress')) return 'govuk-tag--blue';
  if (/^q[1-4]\s+in progress$/.test(lower)) return 'govuk-tag--blue';
  if (/^q[1-4]\s+awaiting staff completion$/.test(lower)) return 'govuk-tag--grey';
  if (/^q[1-4]\s+awaiting manager sign-off$/.test(lower)) return 'govuk-tag--blue';
  if (lower.startsWith('approval') || lower.startsWith('requires approval'))
    return 'govuk-tag--blue';
  if (lower.startsWith('awaiting approval')) return 'govuk-tag--blue';
  if (
    lower.startsWith('pending approval') ||
    lower.startsWith('pending review') ||
    lower.startsWith('review')
  )
    return 'govuk-tag--blue';
  if (lower.startsWith('withdrawn')) return 'govuk-tag--blue';
  if (/^\d+(st|nd|rd|th)\s+approval$/.test(lower)) return 'govuk-tag--blue';
  if (/^q[1-4]\s+approval$/.test(lower)) return 'govuk-tag--green';
  if (/^q[1-4]\s+(awaiting sign-off|sign-off required)$/.test(lower)) return 'govuk-tag--green';
  if (/^q[1-4]\s+complete$/.test(lower)) return 'govuk-tag--green';
  if (/^q4\s+submitted$/.test(lower)) return 'govuk-tag--green';
  if (lower.startsWith('exported')) return 'govuk-tag--grey';
  if (lower.startsWith('expired')) return 'govuk-tag--red';
  if (lower.startsWith('rejected')) return 'govuk-tag--red';
  return 'govuk-tag--grey';
};

const getPsfaAdminStatusDisplay = (
  form: FormListItem,
  label: string,
  className: string,
  tenant?: string,
  isAdminView?: boolean,
) => {
  if (tenant !== 'psfa' || !isAdminView) {
    return { label, className };
  }

  const state = String(form.formState || '')
    .trim()
    .toLowerCase();

  if (state === 'approval') {
    return { label: 'Customer Approval', className: 'govuk-tag--blue' };
  }
  if (state === 'review') {
    return { label: 'PSFA Review', className: 'govuk-tag--yellow' };
  }
  if (state === 'submitted' || state === 'approved') {
    return { label: 'Complete', className: 'govuk-tag--green' };
  }

  return { label, className };
};

const NIL_FAST_TOGGLE_NO_IMAGE = '/images/nil_no.png';
const NIL_FAST_TOGGLE_YES_IMAGE = '/images/nil_yes.png';

export const getDisabledNilReturnStatus = (status: Status): Status =>
  status === Status.InProgress ? Status.InProgress : Status.NilReturn;

export const getListSubsectionStatus = (
  currentStatus: Status,
  hasResponses: boolean,
  pageHasDraft: boolean,
): Status => {
  if (currentStatus === Status.InProgress) return Status.InProgress;
  return hasResponses && !pageHasDraft ? Status.Completed : Status.InProgress;
};

export const shouldShowListInProgress = (
  currentStatus: Status,
  listHasIncomplete: boolean,
): boolean => currentStatus === Status.InProgress || listHasIncomplete;

export const getListPageResponseCount = (
  page: PageList,
  answers: Record<string, unknown>,
): number => {
  const extractedQuestions = extractConditionalQuestions(page.questions || []);
  const groupedAnswers = getListAnswers(answers, extractedQuestions);
  return Object.values(groupedAnswers).filter((entry) =>
    entry.some(({ answer }) => hasValue(answer)),
  ).length;
};

const isEditableRole = (role?: string) => {
  const normalized = String(role || '').toLowerCase();
  return normalized === 'owner' || normalized === 'editor';
};

const formatRoleLabel = (role?: string) => {
  const normalized = String(role || '')
    .trim()
    .toLowerCase();
  if (!normalized) return '-';
  if (normalized === 'read_only') return 'Read only';
  return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`;
};

const canEditApprovalFormBeforeClose = (
  form: FormListItem,
  approvalCloseOnQuarter?: FormConfig['approvalCloseOnQuarter'],
) =>
  canEditApprovalBeforeClose({
    approvalCloseOnQuarter,
    formState: form.formState,
    role: form.role,
    pendingApprovalQuarter: form.pendingApprovalQuarter,
  });

const getActionLabel = (
  form: FormListItem,
  allowUnlock: number | string | undefined,
  hasAdminApprovalTarget: boolean,
  formCloseAt?: string,
  strictDeadline?: boolean,
  approvalCloseOnQuarter?: FormConfig['approvalCloseOnQuarter'],
  tenant?: string,
) => {
  const state = form.formState;
  const role = String(form.role || '').toLowerCase();
  if (tenant === 'scs' && role === 'approver') return 'Review';
  const closedAt = toDate(formCloseAt);
  if (isRewardInfoOnlyForm(form)) return 'View Form';
  if (state === 'review') return role === 'admin' ? 'Review' : 'View';

  if (state === 'approval') {
    if (canEditApprovalFormBeforeClose(form, approvalCloseOnQuarter)) return 'Continue';
    return (isApprovalActor(form) && form.canViewApprovalAnswers !== false) ||
      (role === 'admin' && hasAdminApprovalTarget)
      ? 'Review'
      : 'View';
  }

  if (state === 'submitted' && role === 'admin' && hasAdminApprovalTarget) return 'Review';

  if (role === 'admin') return 'View Form';

  if (state === 'rejected') {
    if (strictDeadline && closedAt && closedAt.getTime() < Date.now()) return 'View';
    if (isEditableRole(role)) return 'Review';
    return 'View';
  }

  // Withdrawn: behaves like active, but we show a distinct status tag.
  if (state === 'withdrawn') {
    if (strictDeadline && closedAt && closedAt.getTime() < Date.now()) return 'View';
    if (!isEditableRole(role)) return 'View';
    return 'Continue';
  }

  if (state === 'submitted') {
    if (canUpdateSubmittedForm(form, allowUnlock, formCloseAt)) return 'View/Update';
    return 'View';
  }
  if (state === 'approved') return 'View';

  if (state === 'exported') return 'View';
  if (strictDeadline && closedAt && closedAt.getTime() < Date.now()) return 'View';

  if (!isEditableRole(role)) return 'View';
  return 'Continue';
};

const canUnapproveForm = (form: FormListItem) => {
  const state = String(form.formState || '')
    .trim()
    .toLowerCase();
  const role = String(form.role || '')
    .trim()
    .toLowerCase();
  if (role !== 'admin') return false;
  if (state === 'approved') return true;
  return state === 'submitted';
};

const getFormLink = (
  form: FormListItem,
  allowUnlock: number | string | undefined,
  hasAdminApprovalTarget: boolean,
  formCloseAt: string | undefined,
  tenant: string,
  strictDeadline?: boolean,
  approvalCloseOnQuarter?: FormConfig['approvalCloseOnQuarter'],
) => {
  const tenantBasePath = tenant ? `/${tenant}` : '';
  const role = String(form.role || '').toLowerCase();
  const reviewSearch = '?review=1';
  if (tenant === 'scs' && role === 'approver') {
    return `${tenantBasePath}/${config.urls.summary}${reviewSearch}`;
  }
  const adminSummarySearch = '?admin=1';
  const adminReviewSearch = '?admin=1&review=1';
  if (tenant === 'reward' && isRewardInfoOnlyForm(form)) {
    return `${tenantBasePath}/${config.urls.summary}${adminSummarySearch}`;
  }
  if (form.formState === 'review') {
    return role === 'admin'
      ? `${tenantBasePath}/${config.urls.summary}${adminReviewSearch}`
      : `${tenantBasePath}/${config.urls.summary}`;
  }
  if (form.formState === 'active' || form.formState === 'withdrawn') {
    if (!isEditableRole(role)) {
      return `${tenantBasePath}/${config.urls.summary}`;
    }
    return tenantBasePath || '/';
  }

  if (form.formState === 'approval') {
    if (canEditApprovalFormBeforeClose(form, approvalCloseOnQuarter)) return tenantBasePath || '/';
    return isApprovalActor(form) && form.canViewApprovalAnswers !== false
      ? `${tenantBasePath}/${config.urls.summary}${reviewSearch}`
      : role === 'admin' && hasAdminApprovalTarget
        ? `${tenantBasePath}/${config.urls.summary}${adminReviewSearch}`
        : `${tenantBasePath}/${config.urls.summary}`;
  }

  if (form.formState === 'submitted' && role === 'admin' && hasAdminApprovalTarget) {
    return `${tenantBasePath}/${config.urls.summary}${adminReviewSearch}`;
  }

  if (role === 'admin') return `${tenantBasePath}/${config.urls.summary}${adminSummarySearch}`;

  if (form.formState === 'rejected') {
    return `${tenantBasePath}/${config.urls.summary}`;
  }

  if (role === 'approver' || role === 'read_only') {
    return `${tenantBasePath}/${config.urls.summary}`;
  }
  if (form.formState === 'submitted') {
    if (canUpdateSubmittedForm(form, allowUnlock, formCloseAt)) {
      return tenantBasePath || '/';
    }
    return `${tenantBasePath}/${config.urls.summary}`;
  }
  if (form.formState === 'approved') {
    return `${tenantBasePath}/${config.urls.summary}`;
  }
  if (
    strictDeadline &&
    formCloseAt &&
    toDate(formCloseAt) &&
    toDate(formCloseAt)!.getTime() < Date.now()
  ) {
    return `${tenantBasePath}/${config.urls.summary}`;
  }
  return tenantBasePath || '/';
};

const withQueryParam = (path: string, key: string, value?: string | null) => {
  const cleanValue = String(value || '').trim();
  if (!cleanValue) return path;

  const [basePath, hashPart = ''] = path.split('#');
  const [pathname, searchPart = ''] = basePath.split('?');
  const searchParams = new URLSearchParams(searchPart);
  searchParams.set(key, cleanValue);

  const nextSearch = searchParams.toString();
  const hashSuffix = hashPart ? `#${hashPart}` : '';
  return `${pathname}${nextSearch ? `?${nextSearch}` : ''}${hashSuffix}`;
};

const withFormIdParam = (path: string, formId?: string) => {
  const formIdValue = String(formId || '').trim();
  if (!formIdValue) return path;

  const [basePath, hashPart = ''] = path.split('#');
  const [pathname, searchPart = ''] = basePath.split('?');
  const searchParams = new URLSearchParams(searchPart);
  searchParams.set('formId', formIdValue);

  const nextSearch = searchParams.toString();
  const hashSuffix = hashPart ? `#${hashPart}` : '';
  return `${pathname}${nextSearch ? `?${nextSearch}` : ''}${hashSuffix}`;
};

const normalizeReportPeriod = (value?: string | null) =>
  String(value || '')
    .replace(/,/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');

const ADMIN_DOWNLOAD_BASE_STATES = new Set(['submitted', 'approved', 'exported']);
const ADMIN_DOWNLOAD_AWAITING_APPROVAL_STATES = new Set(['approval', 'review']);
const ADMIN_DOWNLOAD_IN_PROGRESS_STATES = new Set(['active', 'updating']);

const toExportFilenameBase = (value: string, fallback = 'answers') => {
  const normalized = String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .trim();
  return normalized || fallback;
};

const getAdminDownloadableStates = (
  includeAwaitingApproval: boolean,
  includeInProgress: boolean,
) => {
  const states = new Set(ADMIN_DOWNLOAD_BASE_STATES);
  if (includeAwaitingApproval) {
    ADMIN_DOWNLOAD_AWAITING_APPROVAL_STATES.forEach((state) => states.add(state));
  }
  if (includeInProgress) {
    ADMIN_DOWNLOAD_IN_PROGRESS_STATES.forEach((state) => states.add(state));
  }
  return states;
};

const toDownloadTimestamp = (now = new Date()) => {
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');
  const hours = String(now.getUTCHours()).padStart(2, '0');
  const minutes = String(now.getUTCMinutes()).padStart(2, '0');
  const seconds = String(now.getUTCSeconds()).padStart(2, '0');
  return `${year}_${month}_${month}__${hours}_${minutes}_${seconds}`;
};

const buildDownloadFilename = ({
  tenant,
  formVersion,
  filename,
  extension,
}: {
  tenant: string;
  formVersion: string;
  filename: string;
  extension: string;
}) =>
  `${toDownloadTimestamp()}-${toExportFilenameBase(tenant, 'tenant')}-${toExportFilenameBase(
    formVersion,
    'unknown',
  )}-${toExportFilenameBase(filename, 'file')}.${toExportFilenameBase(extension, 'bin')}`;

export const buildAdminBulkZipEntryFilename = ({
  formId,
  index,
  extension,
  tenant,
  form,
  formCloseAt,
  strictDeadline,
  onFallback,
}: {
  formId: string;
  index: number;
  extension: string;
  tenant?: string;
  form?: FormListItem;
  formCloseAt?: string;
  strictDeadline?: boolean;
  onFallback?: (message: string) => void;
}) => {
  if (tenant === 'scs') {
    const fallbackFilename = `${toExportFilenameBase(formId, 'form')}.${toExportFilenameBase(
      extension,
      'bin',
    )}`;
    if (!form) {
      onFallback?.(`Missing SCS filename metadata for form [${formId}]`);
      return fallbackFilename;
    }

    const reportYear = parseReportPeriodYear(reportPeriodFromForm(form));
    const status = getScsStatusLabel(form, formCloseAt, strictDeadline);
    const reportQuarter = parseReportPeriodQuarter(reportPeriodFromForm(form));
    const reportQuarterNumber = reportQuarter.match(/\b([1-4])\b/)?.[1];
    const activeQuarter =
      (status === 'Up to date' ? getScsCompletedQuarter(form) : '') ||
      normalizeQuarterCode(form.pendingApprovalQuarter) ||
      normalizeQuarterCode(status) ||
      (reportQuarterNumber ? `Q${reportQuarterNumber}` : '');
    const identifier = String(form.identifier || '')
      .split(',', 1)[0]
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^A-Za-z0-9_-]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '');
    const statusWithoutSpaces = status.replace(/\s+/g, '').replace(/[^A-Za-z0-9_-]/g, '');

    if (!reportYear || !activeQuarter || !identifier || !statusWithoutSpaces) {
      onFallback?.(`Incomplete SCS filename metadata for form [${formId}]`);
      return fallbackFilename;
    }

    return `${reportYear}_${activeQuarter}_${identifier}_${statusWithoutSpaces}.${toExportFilenameBase(
      extension,
      'bin',
    )}`;
  }

  const sequence = String(index + 1).padStart(4, '0');
  const formIdPart = toExportFilenameBase(formId, 'form').slice(0, 48);
  return `${sequence}-${formIdPart}.${toExportFilenameBase(extension, 'bin')}`;
};

const resolveDownloadFormVersionLabelFromRows = (
  rows: Array<Record<string, unknown>>,
  field = 'version',
) => {
  const versions = new Set(
    rows.map((row) => String(row?.[field] || '').trim()).filter((value) => value.length > 0),
  );
  if (versions.size === 1) return Array.from(versions)[0];
  if (versions.size > 1) return 'mixed';
  return 'unknown';
};

const parseReportPeriodQuarter = (value?: string | null): string => {
  const text = normalizeReportPeriod(value);
  if (!text) return '';

  const quarterMatch = text.match(/\b(?:quarter|q)\s*([1-4])\b/i);
  if (!quarterMatch?.[1]) return '';
  return `Quarter ${quarterMatch[1]}`;
};

const parseReportPeriodYear = (value?: string | null): string => {
  const text = normalizeReportPeriod(value);
  if (!text) return '';

  const yearMatch = text.match(/\b(20\d{2})\s*[-/]\s*(\d{2}|\d{4})\b/);
  if (!yearMatch) return '';
  const start = yearMatch[1];
  const endRaw = yearMatch[2];
  const end = endRaw.length === 4 ? endRaw.slice(-2) : endRaw;
  return `${start}-${end}`;
};

const reportPeriodFromIdentifier = (identifier?: string | null) => {
  const text = String(identifier || '').trim();
  if (!text) return '';
  const segments = text.split(',');
  if (segments.length >= 2) {
    return normalizeReportPeriod(segments.slice(1).join(','));
  }

  // Legacy/admin identifiers are not always comma-delimited.
  const quarter = parseReportPeriodQuarter(text);
  const year = parseReportPeriodYear(text);
  if (!quarter && !year) return '';
  return `${quarter}${quarter && year ? ' ' : ''}${year}`.trim();
};

const reportPeriodFromForm = (form: FormListItem) => {
  const configured = normalizeReportPeriod(form.reportPeriod || form.report_period || '');
  if (configured) return configured;
  return reportPeriodFromIdentifier(form.identifier);
};

const approvalPeriodsFromForm = (form: FormListItem) =>
  Array.from(
    new Set(
      (Array.isArray(form.approvalPeriods) ? form.approvalPeriods : [])
        .map((value) => normalizeReportPeriod(value))
        .filter((value) => value.length > 0),
    ),
  );

const scsReportQuartersFromForm = (
  form: FormListItem,
  selectedReportYear: string,
  formCloseAt: string | undefined,
  strictDeadline: boolean | undefined,
  sections: FormConfig['sections'],
) => {
  const quarters = new Set<string>();
  const addQuarter = (value?: string | null) => {
    const quarter = parseReportPeriodQuarter(value);
    if (quarter) quarters.add(quarter);
  };

  addQuarter(getScsStatusLabel(form, formCloseAt, strictDeadline, sections));
  addQuarter(form.pendingApprovalQuarter);
  addQuarter(form.approvalStatus);
  addQuarter(form.approvedQuarter);
  addQuarter(getScsCompletedQuarter(form));
  Object.entries(form.scsProgress?.quarters || {}).forEach(([quarter, value]) => {
    if (value.available) addQuarter(quarter);
  });
  form.managerCompletedQuarters?.forEach(addQuarter);
  approvalPeriodsFromForm(form).forEach((period) => {
    if (selectedReportYear && parseReportPeriodYear(period) !== selectedReportYear) return;
    addQuarter(period);
  });

  return SCS_REPORTING_QUARTER_OPTIONS.filter((quarter) => quarters.has(quarter));
};

const departmentFromIdentifier = (identifier?: string | null) => {
  const text = String(identifier || '').trim();
  if (!text) return '';
  const segments = text.split(',');
  return String(segments[0] || '').trim();
};

const normalizeDepartmentFilterValue = (value?: unknown) =>
  String(value || '')
    .split('|', 1)[0]
    .trim();

const departmentFromForm = (form: FormListItem, tenant?: string) => {
  const department = normalizeDepartmentFilterValue(form.department);
  if (tenant === 'psfa') return department;
  return department || departmentFromIdentifier(form.identifier);
};

const organisationFromForm = (form: FormListItem) =>
  normalizeDepartmentFilterValue(form.organisation);

type AdminFilterField = 'department' | 'organisation';

const adminFilterValueFromForm = (
  form: FormListItem,
  tenant?: string,
  field: AdminFilterField = 'department',
) =>
  tenant === 'psfa' && field === 'organisation'
    ? organisationFromForm(form)
    : departmentFromForm(form, tenant);

const adminFilterOptionsFromForms = (
  forms: FormListItem[],
  tenant?: string,
  field: AdminFilterField = 'department',
  includeAdminForms = false,
) =>
  Array.from(
    new Set(
      forms
        .filter(
          (form) =>
            includeAdminForms ||
            String(form.role || '')
              .trim()
              .toLowerCase() !== 'admin',
        )
        .map((form) => adminFilterValueFromForm(form, tenant, field))
        .filter((value) => value.length > 0),
    ),
  ).sort((a, b) => a.localeCompare(b));

const sortReportQuarters = (a: string, b: string) => {
  const aQuarter = Number(a.match(/\b([1-4])\b/)?.[1] || 0);
  const bQuarter = Number(b.match(/\b([1-4])\b/)?.[1] || 0);
  if (aQuarter && bQuarter && aQuarter !== bQuarter) return aQuarter - bQuarter;
  return a.localeCompare(b);
};

const hasNonEmptyIdentifier = (identifier?: string | null) =>
  String(identifier || '').trim().length > 0;

const getRewardTeamNomineeNames = (form: FormListItem) =>
  Array.isArray(form.rewardTeamNomineeNames)
    ? form.rewardTeamNomineeNames
        .map((name) => String(name || '').trim())
        .filter((name) => name.length > 0)
    : [];

const getRewardNomineeNameFromIdentifier = (identifier?: string | null) => {
  const cleaned = String(identifier || '')
    .replace(/\u00c2/g, '')
    .trim();
  if (!cleaned) return EMPTY_VALUE;

  const amountDelimiterIndex = cleaned.search(/,\s*\u00a3\s*\d/);
  return (amountDelimiterIndex >= 0 ? cleaned.slice(0, amountDelimiterIndex) : cleaned).trim();
};

const normalizeAdminSearchText = (value?: unknown) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const rewardAdminFormMatchesSearch = (
  form: FormListItem,
  statusLabel: string,
  normalizedSearchTerm: string,
) => {
  if (!normalizedSearchTerm) return true;
  return [
    form.formId,
    form.identifier,
    form.rewardNominatorName,
    statusLabel,
    form.formState,
    form.rewardCosNomineeName,
    form.rewardCosNomineeId,
    form.rewardCosNomineeEmail,
    ...getRewardTeamNomineeNames(form),
  ].some((value) => normalizeAdminSearchText(value).includes(normalizedSearchTerm));
};

const scsAdminFormMatchesSearch = (
  form: FormListItem,
  statusLabel: string,
  normalizedSearchTerm: string,
) => {
  if (!normalizedSearchTerm) return true;
  return [form.identifier, reportPeriodFromForm(form), statusLabel, form.formState].some((value) =>
    normalizeAdminSearchText(value).includes(normalizedSearchTerm),
  );
};

const REWARD_ADMIN_ROLE_SESSION_KEY = 'overview:rewardAdminRole';
const REWARD_PENDING_APPROVAL_ONLY_SESSION_KEY = 'overview:rewardPendingApprovalOnly';
const REWARD_FUNCTION_ADMIN_ALL_VALUE = '__administrator__';
const REWARD_TYPE_FILTER_ALL_VALUE = '__all_reward_types__';
const REWARD_TYPE_FILTER_VOUCHER_VALUE = 'voucher';
const REWARD_TYPE_FILTER_IYB_VALUE = 'in-year-bonus';
const REWARD_FUNCTION_AWAITING_STATUS = 'awaiting panel decision';
const REWARD_FUNCTION_APPROVED_STATUS = 'approved';
const REWARD_FUNCTION_REJECTED_STATUS = 'rejected';
const REWARD_COS_AWAITING_ACTION_STATUSES = new Set([
  'bulk upload error',
  'confirm iyb',
  'issue voucher',
  'voucher bulk upload',
]);
const REWARD_COS_COMPLETE_STATUSES = new Set([
  'issued via bulk upload',
  'voucher manually issued',
  'iyb complete',
]);
type RewardAdminRole = 'function' | 'cos';

const getStoredRewardAdminRole = (): RewardAdminRole => {
  if (typeof window === 'undefined') return 'function';
  return window.sessionStorage.getItem(REWARD_ADMIN_ROLE_SESSION_KEY) === 'cos'
    ? 'cos'
    : 'function';
};

const setStoredRewardAdminRole = (role: RewardAdminRole) => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(REWARD_ADMIN_ROLE_SESSION_KEY, role);
};

const getStoredRewardPendingApprovalOnly = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.sessionStorage.getItem(REWARD_PENDING_APPROVAL_ONLY_SESSION_KEY) === 'true';
};

const setStoredRewardPendingApprovalOnly = (enabled: boolean) => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(
    REWARD_PENDING_APPROVAL_ONLY_SESSION_KEY,
    enabled ? 'true' : 'false',
  );
};

const normalizeRewardFunction = (value?: string | null) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const countUppercaseLetters = (value: string) => (value.match(/[A-Z]/g) || []).length;

const parseRewardSpendAmount = (value: unknown) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const cleaned = String(value || '')
    .replace(/[£,\s]/g, '')
    .trim();
  if (!cleaned) return 0;
  const amount = Number(cleaned);
  return Number.isFinite(amount) ? amount : 0;
};

const formatRewardSpendAmount = (value: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: value % 1 === 0 ? 0 : 2,
  }).format(value);

const formatRewardTableAmount = (value: unknown) => {
  const match = String(value ?? '').match(/\d[\d,]*(?:\.\d{1,2})?/);
  if (!match) return EMPTY_VALUE;
  const amount = Number(match[0].replace(/,/g, ''));
  return Number.isFinite(amount) ? formatRewardSpendAmount(amount) : EMPTY_VALUE;
};

const getRewardIdentifierAmount = (identifier?: string | null) =>
  String(identifier || '').match(/\u00a3\s*(\d[\d,]*(?:\.\d{1,2})?)/)?.[1];

const getRewardTableAmount = (form: FormListItem, isRewardCosAdminView: boolean) => {
  if (isRewardCosAdminView) {
    return formatRewardTableAmount(form.rewardCosAwardAmount ?? form.rewardCosNominationAmount);
  }
  return formatRewardTableAmount(
    form.rewardSpendAmount ?? getRewardIdentifierAmount(form.identifier),
  );
};

const getRewardTableAwardTypeLabel = (form: FormListItem) => {
  const label = isRewardInYearBonusType(form) ? 'Bonus' : getRewardAwardTypeLabel(form);
  return label === EMPTY_VALUE ? label : `${label}:`;
};

const getCurrentRewardFinancialYearStart = () => {
  const { y, m } = getLondonDateParts(londonNow());
  return m >= 4 ? y : y - 1;
};

const getRewardFinancialYearStart = (value?: string) => {
  const date = toDate(value);
  if (!date) return null;
  const { y, m } = getLondonDateParts(date);
  return m >= 4 ? y : y - 1;
};

const getRewardKpiFinancialYearStart = (form: FormListItem) => {
  const bucket = String(form.rewardSpendBucket || '')
    .trim()
    .toLowerCase();
  const state = String(form.formState || '')
    .trim()
    .toLowerCase();
  const isApproved =
    bucket === 'approved' || (!bucket && ['approved', 'submitted'].includes(state));
  return getRewardFinancialYearStart(isApproved ? form.approvedAt : form.submittedAt);
};

const formatRewardFinancialYear = (startYear: number) =>
  `${startYear}/${String((startYear + 1) % 100).padStart(2, '0')}`;

const reportPeriodLabelFromForm = (form: FormListItem) => {
  const raw = reportPeriodFromForm(form);
  if (!raw) return EMPTY_VALUE;
  const quarter = parseReportPeriodQuarter(raw);
  const year = parseReportPeriodYear(raw);
  if (quarter && year) return `${quarter} ${year}`;
  return raw;
};

const buildAdminDownloadErrorMessage = (baseMessage: string, error: unknown): string => {
  const base = String(baseMessage || '').trim() || 'Unable to download right now.';
  const fromApiError = error instanceof ApiError ? String(error.detail || '').trim() : '';
  const fromMessage =
    !fromApiError && error && typeof error === 'object' && 'message' in error
      ? String((error as { message?: unknown }).message || '').trim()
      : '';
  const detail = fromApiError || fromMessage;
  if (!detail) return base;

  const normalizedBase = base.replace(/\.+$/, '').trim().toLowerCase();
  const normalizedDetail = detail.replace(/\.+$/, '').trim().toLowerCase();
  if (!normalizedDetail || normalizedDetail === normalizedBase) return base;
  return `${base} Cause: ${detail}`;
};

const waitForAdminBulkExcelExportCompletion = async (
  token: string,
  jobId: string,
): Promise<AdminBulkExportJob> => {
  const maxAttempts = 75;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const job = await fetchAdminBulkExcelExportStatus(token, jobId);
    if (job.status === 'completed') return job;
    if (job.status === 'failed') {
      throw new ApiError(
        content.errorApi,
        String(job.error_message || '').trim() || 'Failed to generate export file',
      );
    }
    if (job.status !== 'queued' && job.status !== 'running') {
      throw new ApiError(content.errorApi, `Unsupported export job status: ${job.status}`);
    }

    const pollDelaySeconds =
      typeof job.poll_after_seconds === 'number' &&
      Number.isFinite(job.poll_after_seconds) &&
      job.poll_after_seconds > 0
        ? job.poll_after_seconds
        : 2;

    await new Promise<void>((resolve) => {
      window.setTimeout(resolve, pollDelaySeconds * 1000);
    });
  }

  throw new ApiError(content.errorApi, 'Export is still processing. Try again later.');
};

const waitForSqlReportCompletion = async (token: string, jobId: string): Promise<SqlReportJob> => {
  const maxAttempts = 75;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const job = await fetchSqlReportStatus(token, jobId);
    if (job.status === 'completed') return job;
    if (job.status === 'failed') {
      throw new ApiError(
        content.errorApi,
        String(job.error_message || '').trim() || 'Failed to generate SQL report',
      );
    }
    if (job.status !== 'queued' && job.status !== 'running') {
      throw new ApiError(content.errorApi, `Unsupported SQL report job status: ${job.status}`);
    }

    await new Promise<void>((resolve) => {
      window.setTimeout(resolve, 2000);
    });
  }

  throw new ApiError(content.errorApi, 'SQL report is still processing. Try again later.');
};

type AdminBulkDownloadFormat = 'excel' | 'hybrid' | 'pdf' | 'html';

const ADMIN_BULK_FILE_EXPORT_CONCURRENCY = 10;

const mapAdminBulkFilesWithConcurrency = async <T, R>(
  items: T[],
  worker: (item: T, index: number) => Promise<R>,
): Promise<R[]> => {
  const results = new Array<R>(items.length);
  let nextIndex = 0;

  const runNext = async () => {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      results[currentIndex] = await worker(items[currentIndex], currentIndex);
    }
  };

  const workerCount = Math.min(ADMIN_BULK_FILE_EXPORT_CONCURRENCY, items.length);
  await Promise.all(Array.from({ length: workerCount }, () => runNext()));
  return results;
};

const getMyFormsDownloadFormats = (): Array<'html' | 'pdf' | 'excel'> => ['html', 'pdf', 'excel'];

const canDeleteMyFormsDraft = (form: FormListItem) => {
  const state = String(form.formState || '')
    .trim()
    .toLowerCase();
  const role = String(form.role || '')
    .trim()
    .toLowerCase();
  return (state === 'active' || state === 'updating') && (!role || role === 'owner');
};

export default function Overview() {
  const [cdrUploadFiles, setCdrUploadFiles] = useState<File[]>();
  const [cdrFiles, setCdrFiles] = useState<File[]>();
  const { isLoading } = useUiContext();
  const { fetchJWT } = useAuthContext();
  const { formConfig, enabledSubsections, tenant } = useFormConfig();
  const tenantBasePath = tenant ? `/${tenant}` : '';
  const { sections } = formConfig;
  const location = useLocation();
  const isTestSubmissionMode =
    parseOptionalBoolValue(new URLSearchParams(location.search || '').get('test')) === true;
  const navigate = useNavigate();
  const [createFormError, setCreateFormError] = useState('');
  const [adminDownloadError, setAdminDownloadError] = useState('');
  const [activeAdminDownload, setActiveAdminDownload] = useState<string>('');
  const [adminHtmlDownloadProgress, setAdminHtmlDownloadProgress] = useState<number | null>(null);
  const [adminPdfDisplayProgress, setAdminPdfDisplayProgress] = useState(0);
  const [selectedAdminDownloadFormat, setSelectedAdminDownloadFormat] = useState('');
  const [reportPeriodFilterError, setReportPeriodFilterError] = useState('');
  const [myFormsDownloadForm, setMyFormsDownloadForm] = useState<FormListItem | null>(null);
  const [selectedMyFormsDownloadFormat, setSelectedMyFormsDownloadFormat] = useState('');
  const [myFormsDownloadError, setMyFormsDownloadError] = useState('');
  const [isMyFormsDownloadSubmitting, setIsMyFormsDownloadSubmitting] = useState(false);
  const [myFormsDeleteForm, setMyFormsDeleteForm] = useState<FormListItem | null>(null);
  const [myFormsDeleteError, setMyFormsDeleteError] = useState('');
  const [isMyFormsDeleting, setIsMyFormsDeleting] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [isFeedbackLoading, setIsFeedbackLoading] = useState(false);
  const [hasFeedbackEntries, setHasFeedbackEntries] = useState(false);
  const [hasLoadedFeedbackAvailability, setHasLoadedFeedbackAvailability] = useState(false);
  const [feedbackEntries, setFeedbackEntries] = useState<FeedbackEntry[]>([]);
  const [feedbackAdminError, setFeedbackAdminError] = useState('');
  const [deletingFeedbackAt, setDeletingFeedbackAt] = useState('');
  const [isSqlReportsModalOpen, setIsSqlReportsModalOpen] = useState(false);
  const [isSqlReportsLoading, setIsSqlReportsLoading] = useState(false);
  const [isSqlReportRunning, setIsSqlReportRunning] = useState(false);
  const [sqlReportDefinitions, setSqlReportDefinitions] = useState<SqlReportDefinition[]>([]);
  const [selectedSqlReportName, setSelectedSqlReportName] = useState('');
  const [selectedSqlReportVersion, setSelectedSqlReportVersion] = useState('');
  const [sqlReportError, setSqlReportError] = useState('');
  const [isSqlReportEditorOpen, setIsSqlReportEditorOpen] = useState(false);
  const [isSqlReportEditorLoading, setIsSqlReportEditorLoading] = useState(false);
  const [isSqlReportSaving, setIsSqlReportSaving] = useState(false);
  const [sqlReportDetail, setSqlReportDetail] = useState<SqlReportDetail | null>(null);
  const [sqlReportDraft, setSqlReportDraft] = useState('');
  const [sqlReportEditorError, setSqlReportEditorError] = useState('');
  const [isDataErrorModalOpen, setIsDataErrorModalOpen] = useState(false);
  const [isDataErrorLoading, setIsDataErrorLoading] = useState(false);
  const [hasDataErrorEntries, setHasDataErrorEntries] = useState(false);
  const [hasLoadedDataErrorAvailability, setHasLoadedDataErrorAvailability] = useState(false);
  const [dataErrorEntries, setDataErrorEntries] = useState<DataErrorEntry[]>([]);
  const [dataErrorAdminError, setDataErrorAdminError] = useState('');
  const [deletingDataErrorAt, setDeletingDataErrorAt] = useState('');
  const [showHistoricDataErrors, setShowHistoricDataErrors] = useState(false);
  const [dataErrorPageIndex, setDataErrorPageIndex] = useState(0);
  const [rewardQaDataErrorNotes, setRewardQaDataErrorNotes] = useState<Record<string, string>>({});
  const [rewardQaDataErrorDecisions, setRewardQaDataErrorDecisions] = useState<
    Record<string, 'accepted' | 'rejected'>
  >({});
  const [recordingRewardQaDataErrorAt, setRecordingRewardQaDataErrorAt] = useState('');
  const [isFunctionMembersModalOpen, setIsFunctionMembersModalOpen] = useState(false);
  const [selectedFunctionMemberFunction, setSelectedFunctionMemberFunction] = useState('');
  const [functionMemberEmail, setFunctionMemberEmail] = useState('');
  const [functionMemberFeedback, setFunctionMemberFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [selectedReportQuarter, setSelectedReportQuarter] = useState('');
  const [selectedReportYear, setSelectedReportYear] = useState('');
  const [selectedScsStatus, setSelectedScsStatus] = useState('');
  const scsAdminDefaultReportYearRef = useRef('');
  const scsAdminReportDefaultsAppliedRef = useRef(false);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedOrganisation, setSelectedOrganisation] = useState('');
  const [overviewTableSort, setOverviewTableSort] = useState<OverviewTableSort>(null);
  const [rewardAdminPageSize, setRewardAdminPageSize] = useState<RewardAdminPageSize>(
    DEFAULT_REWARD_ADMIN_PAGE_SIZE,
  );
  const [rewardAdminPage, setRewardAdminPage] = useState(1);
  const [selectedRewardAdminRole, setSelectedRewardAdminRole] = useState<RewardAdminRole>(
    tenant === 'reward' ? getStoredRewardAdminRole : 'function',
  );
  const [selectedRewardFunctionFilter, setSelectedRewardFunctionFilter] = useState(
    REWARD_FUNCTION_ADMIN_ALL_VALUE,
  );
  const [selectedRewardTypeFilter, setSelectedRewardTypeFilter] = useState(
    REWARD_TYPE_FILTER_ALL_VALUE,
  );
  const [selectedRewardFinancialYear, setSelectedRewardFinancialYear] = useState(
    getCurrentRewardFinancialYearStart,
  );
  const [rewardAdminSearchTerm, setRewardAdminSearchTerm] = useState('');
  const [scsAdminSearchTerm, setScsAdminSearchTerm] = useState('');
  const [showAdminInProgressForms, setShowAdminInProgressForms] = useState(false);
  const [hideRewardApprovedForms, setHideRewardApprovedForms] = useState(
    tenant === 'reward' ? getStoredRewardPendingApprovalOnly : false,
  );
  const [isAdminDownloadModalOpen, setIsAdminDownloadModalOpen] = useState(false);
  const [adminDownloadReportYear, setAdminDownloadReportYear] = useState('');
  const [adminDownloadReportQuarter, setAdminDownloadReportQuarter] = useState('');
  const [adminDownloadScsStatus, setAdminDownloadScsStatus] = useState('');
  const [includeAdminAwaitingApprovalDownloads, setIncludeAdminAwaitingApprovalDownloads] =
    useState(false);
  const [includeAdminInProgressDownloads, setIncludeAdminInProgressDownloads] = useState(false);
  const [unapproveForm, setUnapproveForm] = useState<FormListItem | null>(null);
  const [unapproveReason, setUnapproveReason] = useState('');
  const [unapproveError, setUnapproveError] = useState('');
  const [isUnapproving, setIsUnapproving] = useState(false);
  const [rewardVoucherIssueForm, setRewardVoucherIssueForm] = useState<FormListItem | null>(null);
  const [rewardVoucherIssueChecked, setRewardVoucherIssueChecked] = useState(false);
  const [rewardVoucherIssueError, setRewardVoucherIssueError] = useState('');
  const [isRewardVoucherIssueSaving, setIsRewardVoucherIssueSaving] = useState(false);
  const [isRewardVoucherPdfDownloading, setIsRewardVoucherPdfDownloading] = useState(false);
  const [rewardCosAuditDialog, setRewardCosAuditDialog] =
    useState<RewardCosAuditDialogState | null>(null);
  const [isRewardCosAuditLoading, setIsRewardCosAuditLoading] = useState(false);
  const [rewardCosAuditError, setRewardCosAuditError] = useState('');
  const [psfaAuditDialog, setPsfaAuditDialog] = useState<PsfaAuditDialogState | null>(null);
  const [psfaAccessDialog, setPsfaAccessDialog] = useState<PsfaAccessDialogState | null>(null);
  const [isPsfaAuditLoading, setIsPsfaAuditLoading] = useState(false);
  const [psfaAuditLoadingFormId, setPsfaAuditLoadingFormId] = useState('');
  const [psfaAuditError, setPsfaAuditError] = useState('');
  const [isPsfaAccessLoading, setIsPsfaAccessLoading] = useState(false);
  const [psfaAccessLoadingFormId, setPsfaAccessLoadingFormId] = useState('');
  const [psfaAccessError, setPsfaAccessError] = useState('');
  const [isRewardTransferOwnershipModalOpen, setIsRewardTransferOwnershipModalOpen] =
    useState(false);
  const [isRewardTransferOwnershipLoading, setIsRewardTransferOwnershipLoading] = useState(false);
  const [rewardTransferOwnershipRows, setRewardTransferOwnershipRows] = useState<
    RewardTransferOwnershipCandidate[]
  >([]);
  const [rewardTransferFunctionOptions, setRewardTransferFunctionOptions] = useState<string[]>([]);
  const [rewardTransferOwnershipError, setRewardTransferOwnershipError] = useState('');
  const [rewardTransferEditingFormId, setRewardTransferEditingFormId] = useState('');
  const [rewardTransferSelectedFunction, setRewardTransferSelectedFunction] = useState('');
  const [rewardTransferSavingFormId, setRewardTransferSavingFormId] = useState('');
  const [isVoucherBulkUploadModalOpen, setIsVoucherBulkUploadModalOpen] = useState(false);
  const [voucherBulkUploadChecked, setVoucherBulkUploadChecked] = useState(false);
  const [voucherBulkUploadError, setVoucherBulkUploadError] = useState('');
  const [isVoucherBulkUploadDownloading, setIsVoucherBulkUploadDownloading] = useState(false);
  const [isVoucherBulkUploadSaving, setIsVoucherBulkUploadSaving] = useState(false);
  const [isRewardCosReconciliationDownloading, setIsRewardCosReconciliationDownloading] =
    useState(false);
  const [fastToggleBusyBySubsection, setFastToggleBusyBySubsection] = useState<
    Record<string, boolean>
  >({});
  const [fastToggleAnswerOverrides, setFastToggleAnswerOverrides] = useState<
    Record<string, string | null>
  >({});
  const [nilSectionBusy, setNilSectionBusy] = useState(false);
  const [nilSectionError, setNilSectionError] = useState('');
  const [rewardCosVoucherOverrides, setRewardCosVoucherOverrides] = useState<
    Record<string, RewardCosVoucherStatusOverride>
  >({});
  const [rewardCosVoucherSortOrder, setRewardCosVoucherSortOrder] = useState<
    Record<string, number>
  >({});
  const [suppressOverviewLoading, setSuppressOverviewLoading] = useState(false);
  const myFormsDownloadSelectRef = useRef<HTMLSelectElement>(null);
  const myFormsDownloadFormats = getMyFormsDownloadFormats();

  const title = 'title' in formConfig ? String(formConfig.title) : undefined;
  const subTitle = 'subTitle' in formConfig ? String(formConfig.subTitle || '') : undefined;
  const cleanedSubTitle = subTitle?.trim();
  const lowerSubTitle = cleanedSubTitle?.toLowerCase();
  const showSubTitle =
    cleanedSubTitle && lowerSubTitle !== 'summary page' && lowerSubTitle !== 'summary'
      ? cleanedSubTitle
      : undefined;
  const showSections = formConfig.showSections === undefined ? true : formConfig.showSections;
  let index = 0;

  const { data, refetch } = useFormData();
  const queryClient = useQueryClient();
  const answers = useMemo(() => {
    const currentAnswers = { ...(data?.answers || {}) };
    for (const [field, value] of Object.entries(fastToggleAnswerOverrides)) {
      if (value === null) {
        delete currentAnswers[field];
      } else {
        currentAnswers[field] = value;
      }
    }
    return currentAnswers;
  }, [data?.answers, fastToggleAnswerOverrides]);
  const effectiveData = useMemo(() => (data ? { ...data, answers } : data), [answers, data]);
  const forms = useMemo(() => {
    const sourceForms = data?.forms || [];
    if (Object.keys(rewardCosVoucherOverrides).length === 0) return sourceForms;
    return sourceForms.map((form) => {
      const override = rewardCosVoucherOverrides[getRewardCosVoucherRowKey(form)];
      return override ? { ...form, ...override } : form;
    });
  }, [data?.forms, rewardCosVoucherOverrides]);
  const isAnyFastToggleBusy =
    nilSectionBusy || Object.values(fastToggleBusyBySubsection).some((busy) => busy);
  const currentSearchParams = new URLSearchParams(location?.search || '');
  const requestedAdminView = parseOptionalBoolValue(currentSearchParams.get('admin'));
  const requestedMyFormsView = parseOptionalBoolValue(currentSearchParams.get('my_forms'));
  const selectedApprovalGroupParam = currentSearchParams.get('g') || '';
  const roleSet = new Set<string>();
  const dataRole = String(data?.role || '')
    .trim()
    .toLowerCase();
  if (dataRole) roleSet.add(dataRole);
  if (Array.isArray(data?.sessionRoles)) {
    data.sessionRoles.forEach((role) => {
      const normalized = String(role || '')
        .trim()
        .toLowerCase();
      if (normalized) roleSet.add(normalized);
    });
  }
  forms.forEach((form) => {
    const normalized = String(form?.role || '')
      .trim()
      .toLowerCase();
    if (normalized) roleSet.add(normalized);
  });
  const hasAdminAccess = data?.hasAdminAccess === true || roleSet.has('admin');
  const hasMyFormsAccess =
    data?.hasMyFormsAccess === true || Array.from(roleSet).some((role) => role !== 'admin');
  const hasUnknownAdminAccessOnAdminRoute =
    requestedAdminView === true && (data === undefined || data === null);
  const inferredAdminView =
    requestedAdminView === true
      ? true
      : requestedAdminView === false
        ? false
        : typeof data?.adminView === 'boolean'
          ? data.adminView
          : !hasMyFormsAccess;
  const isAdminView = hasUnknownAdminAccessOnAdminRoute || (hasAdminAccess && inferredAdminView);
  const showAdminUsersColumn = (tenant === 'psfa' || tenant === 'scs') && isAdminView;
  const hasRewardCosApproverRole = tenant === 'reward' && data?.rewardHasCosApproverRole === true;
  const hasRewardFunctionApproverRole =
    tenant === 'reward' &&
    (data?.rewardHasFunctionApproverRole === true ||
      (data?.rewardHasCosApproverRole !== true && isAdminView));
  const effectiveRewardAdminRole: 'function' | 'cos' =
    hasRewardCosApproverRole && !hasRewardFunctionApproverRole
      ? 'cos'
      : hasRewardCosApproverRole && selectedRewardAdminRole === 'cos'
        ? 'cos'
        : 'function';
  useEffect(() => {
    if (tenant !== 'reward' || !isAdminView || effectiveRewardAdminRole !== 'cos') {
      setRewardCosVoucherSortOrder((current) => (Object.keys(current).length === 0 ? current : {}));
      return;
    }

    const sortedKeys = [...(data?.forms || [])]
      .filter(isRewardCosVoucherForm)
      .sort(compareRewardCosAdminForms)
      .map(getRewardCosVoucherRowKey)
      .filter((key) => key !== '||');

    setRewardCosVoucherSortOrder((current) => {
      const existingKeys = sortedKeys
        .filter((key) => typeof current[key] === 'number')
        .sort((a, b) => current[a] - current[b]);
      const newKeys = sortedKeys.filter((key) => typeof current[key] !== 'number');
      const next: Record<string, number> = {};
      [...existingKeys, ...newKeys].forEach((key, index) => {
        next[key] = index;
      });

      const currentKeys = Object.keys(current);
      const nextKeys = Object.keys(next);
      const isSame =
        currentKeys.length === nextKeys.length &&
        nextKeys.every((key) => current[key] === next[key]);
      return isSame ? current : next;
    });
  }, [data?.forms, effectiveRewardAdminRole, isAdminView, tenant]);
  const shouldLoadFunctionMembers =
    tenant === 'reward' && isAdminView && effectiveRewardAdminRole === 'function';
  const functionMembersQuery = useQuery<FunctionMembersResponse, Error>({
    queryKey: [Query.FunctionMembers, selectedFunctionMemberFunction],
    enabled: shouldLoadFunctionMembers,
    queryFn: async () => {
      const token = (await fetchJWT({ redirectOnFail: false })).idToken;
      if (!token) {
        throw new Error('Not authenticated');
      }
      return fetchFunctionMembers(token, selectedFunctionMemberFunction || undefined);
    },
    staleTime: 0,
    retry: false,
  });
  const functionMemberFunctions = useMemo(
    () => functionMembersQuery.data?.functions || [],
    [functionMembersQuery.data?.functions],
  );
  const selectedFunctionForMembers =
    selectedFunctionMemberFunction ||
    functionMembersQuery.data?.selectedFunction ||
    functionMemberFunctions[0] ||
    '';
  const functionMembers = functionMembersQuery.data?.members || [];
  const showFunctionMembersButton =
    shouldLoadFunctionMembers &&
    !functionMembersQuery.isError &&
    functionMemberFunctions.length > 0;
  useEffect(() => {
    if (selectedFunctionMemberFunction || !functionMembersQuery.data?.selectedFunction) return;
    setSelectedFunctionMemberFunction(functionMembersQuery.data.selectedFunction);
  }, [functionMembersQuery.data?.selectedFunction, selectedFunctionMemberFunction]);
  const addFunctionMemberMutation = useMutation({
    mutationFn: async () => {
      const token = (await fetchJWT({ redirectOnFail: false })).idToken;
      if (!token) {
        throw new Error('Not authenticated');
      }
      return addFunctionMember(token, {
        email: functionMemberEmail,
        functionName: selectedFunctionForMembers,
      });
    },
    onSuccess: async () => {
      setFunctionMemberFeedback({ type: 'success', message: 'Function member added' });
      setFunctionMemberEmail('');
      await queryClient.invalidateQueries({ queryKey: [Query.FunctionMembers] });
    },
    onError: (error) => {
      const detail =
        error instanceof ApiError ? error.detail || error.message : 'Unable to add function member';
      setFunctionMemberFeedback({ type: 'error', message: detail });
    },
  });
  const removeFunctionMemberMutation = useMutation({
    mutationFn: async (member: FunctionMemberEntry) => {
      const token = (await fetchJWT({ redirectOnFail: false })).idToken;
      if (!token) {
        throw new Error('Not authenticated');
      }
      return removeFunctionMember(token, {
        username: member.username,
        functionName: selectedFunctionForMembers,
      });
    },
    onSuccess: async () => {
      setFunctionMemberFeedback({ type: 'success', message: 'Function member removed' });
      await queryClient.invalidateQueries({ queryKey: [Query.FunctionMembers] });
    },
    onError: (error) => {
      const detail =
        error instanceof ApiError
          ? error.detail || error.message
          : 'Unable to remove function member';
      setFunctionMemberFeedback({ type: 'error', message: detail });
    },
  });
  const formState = (data as any)?.formState as string | undefined;
  const approvalMode = formConfig.approval;
  const requiresApproval = approvalMode === 'always';
  const isApproverRole = String(data?.role || '').toLowerCase() === 'approver';
  const isAssignedApproverRole = (data as any)?.isAssignedApprover === true;
  const isApprovalActorRole = isApproverRole || isAssignedApproverRole;
  const isReadOnlyRole = String(data?.role || '').toLowerCase() === 'read_only';
  const formCloseAt = (formConfig as any)?.formCloseAt ?? (data as any)?.formCloseAt;
  const strictDeadline =
    (formConfig as any)?.strictDeadline ?? (data as any)?.strictDeadline ?? false;
  const hasAdminApprovalTarget =
    typeof formConfig.approvalFixedEmail === 'string' &&
    formConfig.approvalFixedEmail.trim().toLowerCase() === 'admin';
  const awaitingSignOffCount = forms.reduce((count, form) => {
    const closeAt = formConfig.formCloseAt ?? form.formCloseAt;
    const statusLabel = getStatusLabel(
      form,
      formConfig.allowUnlock,
      hasAdminApprovalTarget,
      formConfig.statusOverride,
      closeAt,
      strictDeadline,
      tenant,
      sections,
    );
    const isAwaitingSignOff =
      tenant === 'scs'
        ? /^Q[1-4]\s+Sign-off required$/i.test(statusLabel.trim())
        : statusLabel.trim().toLowerCase() === 'requires approval';
    return isAwaitingSignOff ? count + 1 : count;
  }, 0);
  const awaitingSignOffMessage =
    awaitingSignOffCount === 1
      ? 'You have 1 form awaiting sign-off. Please click on the review button next to the form below.'
      : `You have ${awaitingSignOffCount} forms awaiting sign-off. Please click on the review button next to each form below.`;
  const sortedForms = useMemo(() => {
    if (tenant === 'reward' && isAdminView) {
      if (effectiveRewardAdminRole === 'cos') {
        return [...forms].sort((a, b) => {
          const aOrder = rewardCosVoucherSortOrder[getRewardCosVoucherRowKey(a)];
          const bOrder = rewardCosVoucherSortOrder[getRewardCosVoucherRowKey(b)];
          if (typeof aOrder === 'number' && typeof bOrder === 'number' && aOrder !== bOrder) {
            return aOrder - bOrder;
          }
          if (typeof aOrder === 'number') return -1;
          if (typeof bOrder === 'number') return 1;
          return compareRewardCosAdminForms(a, b);
        });
      }

      return [...forms].sort(compareRewardFunctionAdminForms);
    }

    const getPriority = (form: (typeof forms)[number]) => {
      const state = String(form.formState || '').toLowerCase();
      const role = String(form.role || '').toLowerCase();
      if (state === 'approval' && isApprovalActor(form)) return 0;
      if (state === 'review' && role === 'admin') return 1;
      if (state === 'approval') return 2;
      if (state === 'review') return 3;
      return 4;
    };

    return [...forms].sort((a, b) => {
      const priorityDiff = getPriority(a) - getPriority(b);
      if (priorityDiff !== 0) return priorityDiff;
      const aDate = toDate(a.lastActive) || toDate(a.createdAt);
      const bDate = toDate(b.lastActive) || toDate(b.createdAt);
      if (aDate && bDate) return bDate.getTime() - aDate.getTime();
      if (aDate) return -1;
      if (bDate) return 1;
      return 0;
    });
  }, [effectiveRewardAdminRole, forms, isAdminView, rewardCosVoucherSortOrder, tenant]);
  const configQuestions = useMemo(
    () =>
      sections.flatMap((section) =>
        section.subsections.flatMap((subsection) =>
          subsection.pages.flatMap((page) =>
            'questions' in page && Array.isArray(page.questions)
              ? extractConditionalQuestions(page.questions)
              : [],
          ),
        ),
      ),
    [sections],
  );
  const departmentQuestion = useMemo(
    () =>
      configQuestions.find((question) => {
        const name = String(question.name || '').trim();
        const title = String(question.title || '')
          .trim()
          .toLowerCase();
        return (
          name === 'department' ||
          (tenant === 'psfa' &&
            (name === 'submitter-department' ||
              title === 'please select the department you are reporting for'))
        );
      }),
    [configQuestions, tenant],
  );
  const organisationQuestion = useMemo(
    () =>
      tenant === 'psfa'
        ? configQuestions.find((question) => {
            const name = String(question.name || '').trim();
            const title = String(question.title || '')
              .trim()
              .toLowerCase();
            return (
              name === 'sub_organisation' ||
              name === 'submitter-organisation' ||
              title === 'please select your organisation'
            );
          })
        : undefined,
    [configQuestions, tenant],
  );

  const hasReportPeriodQuestion = useMemo(
    () => configQuestions.some((question) => question.questionType === 'reportPeriod'),
    [configQuestions],
  );
  const hasQuarterReviewSections = useMemo(
    () => sections.some((section) => Boolean(section.quarterReview?.quarter)),
    [sections],
  );
  const useScsReportingYearFilter = tenant === 'scs' && isAdminView;
  const showReportingPeriodColumn = hasReportPeriodQuestion;
  const useQuarterReviewApprovalFilter =
    hasQuarterReviewSections && isAdminView && !useScsReportingYearFilter;
  const filterPeriodsFromForm = useMemo(
    () => (form: FormListItem) => {
      if (useQuarterReviewApprovalFilter) {
        return approvalPeriodsFromForm(form);
      }
      const raw = reportPeriodFromForm(form);
      return raw ? [raw] : [];
    },
    [useQuarterReviewApprovalFilter],
  );
  const hasActiveReportFilter =
    hasReportPeriodQuestion &&
    (useScsReportingYearFilter
      ? Boolean(selectedReportYear)
      : Boolean(selectedReportQuarter || selectedReportYear));
  const formMatchesSelectedReportFilter = useMemo(
    () => (form: FormListItem) => {
      if (!hasActiveReportFilter) return true;
      return filterPeriodsFromForm(form).some((raw) => {
        const quarter = parseReportPeriodQuarter(raw);
        const year = parseReportPeriodYear(raw);
        return (
          (useScsReportingYearFilter ||
            !selectedReportQuarter ||
            quarter === selectedReportQuarter) &&
          (!selectedReportYear || year === selectedReportYear)
        );
      });
    },
    [
      filterPeriodsFromForm,
      hasActiveReportFilter,
      selectedReportQuarter,
      selectedReportYear,
      useScsReportingYearFilter,
    ],
  );
  const adminFilterSourceForms = useMemo(() => {
    if (tenant !== 'psfa' || !isAdminView) return sortedForms;

    return sortedForms.filter((form) => {
      if (showAdminInProgressForms) return true;

      const closeAt = (formConfig as any)?.formCloseAt ?? form.formCloseAt;
      const label = getDefaultStatusLabel(
        form,
        formConfig.allowUnlock,
        hasAdminApprovalTarget,
        closeAt,
        strictDeadline,
        tenant,
        sections,
      );
      return !label.toLowerCase().endsWith('in progress');
    });
  }, [
    formConfig,
    hasAdminApprovalTarget,
    isAdminView,
    sections,
    showAdminInProgressForms,
    sortedForms,
    strictDeadline,
    tenant,
  ]);
  const showDepartmentFilter = Boolean(departmentQuestion) || (tenant === 'psfa' && isAdminView);
  const showOrganisationFilter =
    Boolean(organisationQuestion) || (tenant === 'psfa' && isAdminView);
  const departmentOptions = useMemo(() => {
    if (!showDepartmentFilter) return [];
    return adminFilterOptionsFromForms(
      adminFilterSourceForms.filter((form) => {
        if (!formMatchesSelectedReportFilter(form)) return false;
        if (tenant === 'psfa' && selectedOrganisation) {
          return adminFilterValueFromForm(form, tenant, 'organisation') === selectedOrganisation;
        }
        return true;
      }),
      tenant,
      'department',
      tenant === 'psfa' && isAdminView,
    );
  }, [
    adminFilterSourceForms,
    formMatchesSelectedReportFilter,
    isAdminView,
    selectedOrganisation,
    showDepartmentFilter,
    tenant,
  ]);
  const organisationOptions = useMemo(() => {
    if (!showOrganisationFilter) return [];
    return adminFilterOptionsFromForms(
      adminFilterSourceForms.filter((form) => {
        if (!formMatchesSelectedReportFilter(form)) return false;
        if (selectedDepartment) {
          return adminFilterValueFromForm(form, tenant, 'department') === selectedDepartment;
        }
        return true;
      }),
      tenant,
      'organisation',
      tenant === 'psfa' && isAdminView,
    );
  }, [
    adminFilterSourceForms,
    formMatchesSelectedReportFilter,
    isAdminView,
    selectedDepartment,
    showOrganisationFilter,
    tenant,
  ]);
  const departmentFilteredForms = useMemo(() => {
    if (!selectedDepartment && !selectedOrganisation) return sortedForms;
    return sortedForms.filter((form) => {
      if (
        selectedDepartment &&
        adminFilterValueFromForm(form, tenant, 'department') !== selectedDepartment
      ) {
        return false;
      }
      if (
        tenant === 'psfa' &&
        selectedOrganisation &&
        adminFilterValueFromForm(form, tenant, 'organisation') !== selectedOrganisation
      ) {
        return false;
      }
      return true;
    });
  }, [selectedDepartment, selectedOrganisation, sortedForms, tenant]);
  const reportPeriodOptions = useMemo(() => {
    const optionsByKey = new Map<
      string,
      {
        raw: string;
        quarter: string;
        year: string;
      }
    >();

    departmentFilteredForms.forEach((form) => {
      filterPeriodsFromForm(form).forEach((raw) => {
        if (!raw) return;
        const quarter = parseReportPeriodQuarter(raw);
        const year = parseReportPeriodYear(raw);
        if (!quarter && !year) return;

        const key = normalizeReportPeriod(raw).toLowerCase();
        if (optionsByKey.has(key)) return;
        optionsByKey.set(key, { raw, quarter, year });
      });
    });

    return Array.from(optionsByKey.values());
  }, [departmentFilteredForms, filterPeriodsFromForm]);
  const reportQuarterOptions = useMemo(
    () =>
      Array.from(
        new Set(
          reportPeriodOptions.map((option) => option.quarter).filter((value) => value.length > 0),
        ),
      ).sort(sortReportQuarters),
    [reportPeriodOptions],
  );
  const reportYearOptions = useMemo(() => {
    const values = new Set(
      reportPeriodOptions
        .filter(
          (option) =>
            useScsReportingYearFilter ||
            !selectedReportQuarter ||
            option.quarter === selectedReportQuarter,
        )
        .map((option) => option.year)
        .filter((value) => value.length > 0),
    );
    return Array.from(values).sort((a, b) => b.localeCompare(a));
  }, [reportPeriodOptions, selectedReportQuarter, useScsReportingYearFilter]);
  const selectedReportFilters = useMemo(() => {
    if (!hasActiveReportFilter) return [];
    const matchedFilters = Array.from(
      new Set(
        reportPeriodOptions
          .filter(
            (option) =>
              (useScsReportingYearFilter ||
                !selectedReportQuarter ||
                option.quarter === selectedReportQuarter) &&
              (!selectedReportYear || option.year === selectedReportYear),
          )
          .map((option) => option.raw),
      ),
    );
    if (matchedFilters.length > 0) return matchedFilters;
    return [];
  }, [
    hasActiveReportFilter,
    reportPeriodOptions,
    selectedReportQuarter,
    selectedReportYear,
    useScsReportingYearFilter,
  ]);
  const selectedDepartmentFilters = useMemo(
    () => (selectedDepartment ? [selectedDepartment] : []),
    [selectedDepartment],
  );
  const allowUnapprove = tenant === 'psfa' || formConfig.AllowUnapprove === true;
  const isRewardTenant = tenant === 'reward';
  const isRewardAdminView = isAdminView && isRewardTenant;
  const canManageRewardQaDataErrors = isRewardTenant && effectiveRewardAdminRole === 'cos';
  const shouldLoadDataErrorAvailability = !isRewardTenant || canManageRewardQaDataErrors;
  const dataErrorNewEntryCount = countActiveDataErrorEntries(
    dataErrorEntries,
    rewardQaDataErrorDecisions,
  );
  const showDataErrorButton =
    shouldLoadDataErrorAvailability &&
    hasLoadedDataErrorAvailability &&
    ((canManageRewardQaDataErrors && isRewardTenant) || hasDataErrorEntries);
  const dataErrorButtonLabel = `View Data Errors (${dataErrorNewEntryCount})`;
  const showSqlReportsButton = tenant === 'psfa' && isAdminView;
  const selectedSqlReport = sqlReportDefinitions.find(
    (report) => report.report_name === selectedSqlReportName,
  );
  const selectedSqlReportVersions = [...(selectedSqlReport?.form_versions || [])].sort(
    (left, right) => right - left,
  );
  const showRewardTransferOwnershipButton =
    isRewardAdminView && effectiveRewardAdminRole === 'cos' && hasRewardCosApproverRole;
  const rewardFunctionFilterOptions = useMemo(() => {
    const canBuildFunctionOptions =
      effectiveRewardAdminRole === 'function' &&
      (data?.rewardIsAdministrator === true || hasRewardFunctionApproverRole);
    const canBuildCosOptions = effectiveRewardAdminRole === 'cos' && hasRewardCosApproverRole;
    if (!isRewardAdminView || (!canBuildFunctionOptions && !canBuildCosOptions)) {
      return [];
    }

    const options = new Map<string, string>();
    if (effectiveRewardAdminRole === 'function') {
      data?.rewardSpendByFinancialYear?.forEach(({ rewardFunction }) => {
        options.set(normalizeRewardFunction(rewardFunction), rewardFunction);
      });
    }
    departmentFilteredForms.forEach((form) => {
      const state = String(form.formState || '')
        .trim()
        .toLowerCase();
      if (state === 'active') return;
      if (effectiveRewardAdminRole === 'cos') {
        if (!isRewardCosVoucherForm(form)) return;
      } else if (isRewardCosVoucherForm(form)) {
        return;
      }

      const rawRewardFunctions =
        effectiveRewardAdminRole === 'function' && form.rewardSpendAllocations?.length
          ? form.rewardSpendAllocations.map((allocation) => allocation.rewardFunction)
          : [form.rewardFunction];
      rawRewardFunctions.forEach((value) => {
        const rawRewardFunction = String(value || '').trim();
        if (!rawRewardFunction) return;

        const key = normalizeRewardFunction(rawRewardFunction);
        const current = options.get(key);
        if (
          !current ||
          rawRewardFunction.length > current.length ||
          (rawRewardFunction.length === current.length &&
            countUppercaseLetters(rawRewardFunction) > countUppercaseLetters(current))
        ) {
          options.set(key, rawRewardFunction);
        }
      });
    });

    return Array.from(options.values()).sort((a, b) => a.localeCompare(b));
  }, [
    data?.rewardIsAdministrator,
    data?.rewardSpendByFinancialYear,
    departmentFilteredForms,
    effectiveRewardAdminRole,
    hasRewardCosApproverRole,
    hasRewardFunctionApproverRole,
    isRewardAdminView,
  ]);
  const showRewardFunctionFilterSelect =
    rewardFunctionFilterOptions.length > 0 &&
    (effectiveRewardAdminRole === 'cos' ||
      data?.rewardIsAdministrator === true ||
      rewardFunctionFilterOptions.length > 1);
  const rewardFunctionAllOptionLabel =
    effectiveRewardAdminRole === 'cos'
      ? 'All functions'
      : data?.rewardIsAdministrator === true
        ? 'Administrator (shows all functions)'
        : 'All assigned functions';
  useEffect(() => {
    if (!showRewardFunctionFilterSelect) {
      if (selectedRewardFunctionFilter !== REWARD_FUNCTION_ADMIN_ALL_VALUE) {
        setSelectedRewardFunctionFilter(REWARD_FUNCTION_ADMIN_ALL_VALUE);
      }
      return;
    }

    if (selectedRewardFunctionFilter === REWARD_FUNCTION_ADMIN_ALL_VALUE) return;

    const selectedKey = normalizeRewardFunction(selectedRewardFunctionFilter);
    const selectedOptionExists = rewardFunctionFilterOptions.some(
      (option) => normalizeRewardFunction(option) === selectedKey,
    );
    if (!selectedOptionExists) {
      setSelectedRewardFunctionFilter(REWARD_FUNCTION_ADMIN_ALL_VALUE);
    }
  }, [rewardFunctionFilterOptions, selectedRewardFunctionFilter, showRewardFunctionFilterSelect]);
  const rewardFunctionScopedForms = useMemo(() => {
    if (
      !showRewardFunctionFilterSelect ||
      selectedRewardFunctionFilter === REWARD_FUNCTION_ADMIN_ALL_VALUE
    ) {
      return departmentFilteredForms;
    }

    const selectedKey = normalizeRewardFunction(selectedRewardFunctionFilter);
    return departmentFilteredForms.filter((form) => {
      if (effectiveRewardAdminRole === 'function' && form.rewardSpendAllocations?.length) {
        return form.rewardSpendAllocations.some(
          (allocation) => normalizeRewardFunction(allocation.rewardFunction) === selectedKey,
        );
      }
      return normalizeRewardFunction(form.rewardFunction) === selectedKey;
    });
  }, [
    departmentFilteredForms,
    effectiveRewardAdminRole,
    selectedRewardFunctionFilter,
    showRewardFunctionFilterSelect,
  ]);
  const showRewardTypeFilterSelect = showRewardFunctionFilterSelect;
  useEffect(() => {
    if (!showRewardTypeFilterSelect && selectedRewardTypeFilter !== REWARD_TYPE_FILTER_ALL_VALUE) {
      setSelectedRewardTypeFilter(REWARD_TYPE_FILTER_ALL_VALUE);
    }
  }, [selectedRewardTypeFilter, showRewardTypeFilterSelect]);
  const rewardAdminScopedForms = useMemo(() => {
    if (!showRewardTypeFilterSelect || selectedRewardTypeFilter === REWARD_TYPE_FILTER_ALL_VALUE) {
      return rewardFunctionScopedForms;
    }
    if (selectedRewardTypeFilter === REWARD_TYPE_FILTER_IYB_VALUE) {
      return rewardFunctionScopedForms.filter((form) => isRewardInYearBonusType(form));
    }
    if (selectedRewardTypeFilter === REWARD_TYPE_FILTER_VOUCHER_VALUE) {
      return rewardFunctionScopedForms.filter((form) => isRewardVoucherType(form));
    }
    return rewardFunctionScopedForms;
  }, [rewardFunctionScopedForms, selectedRewardTypeFilter, showRewardTypeFilterSelect]);
  const myFormsOnly = useMemo(
    () =>
      sortedForms.filter((form) => {
        const role = String(form.role || '')
          .trim()
          .toLowerCase();
        if (role === 'admin') return false;
        return true;
      }),
    [sortedForms],
  );
  const adminFormsOnly = useMemo(
    () =>
      rewardAdminScopedForms.filter((form) => {
        const closeAt = (formConfig as any)?.formCloseAt ?? form.formCloseAt;
        const state = String(form.formState || '')
          .trim()
          .toLowerCase();
        const defaultLabel = getDefaultStatusLabel(
          form,
          formConfig.allowUnlock,
          hasAdminApprovalTarget,
          closeAt,
          strictDeadline,
          tenant,
          sections,
        );
        const label = isRewardTenant
          ? getStatusLabel(
              form,
              formConfig.allowUnlock,
              hasAdminApprovalTarget,
              formConfig.statusOverride,
              closeAt,
              strictDeadline,
              tenant,
              sections,
            )
          : defaultLabel;
        if (isRewardTenant) {
          if (state === 'active') return false;
          if (effectiveRewardAdminRole === 'cos') return isRewardCosVoucherForm(form);
          if (isRewardCosVoucherForm(form)) return false;
          if (!hideRewardApprovedForms) return true;
          return label.trim().toLowerCase() === 'awaiting panel decision';
        }
        if (showAdminInProgressForms) return true;
        return !label.toLowerCase().endsWith('in progress');
      }),
    [
      effectiveRewardAdminRole,
      rewardAdminScopedForms,
      formConfig,
      hasAdminApprovalTarget,
      hideRewardApprovedForms,
      isRewardTenant,
      sections,
      showAdminInProgressForms,
      strictDeadline,
      tenant,
    ],
  );
  const baseFormsForView = useMemo(
    () => (isAdminView ? adminFormsOnly : myFormsOnly),
    [adminFormsOnly, isAdminView, myFormsOnly],
  );
  const adminKpiForms = useMemo(() => {
    if (!isAdminView) return [];
    const kpiSourceForms = isRewardAdminView ? adminFormsOnly : rewardAdminScopedForms;
    if (!hasActiveReportFilter) return kpiSourceForms;
    if (selectedReportFilters.length === 0) return [];
    const selectedSet = new Set(selectedReportFilters.map((value) => normalizeReportPeriod(value)));
    return kpiSourceForms.filter((form) =>
      filterPeriodsFromForm(form).some((value) => selectedSet.has(normalizeReportPeriod(value))),
    );
  }, [
    adminFormsOnly,
    rewardAdminScopedForms,
    filterPeriodsFromForm,
    hasActiveReportFilter,
    isAdminView,
    isRewardAdminView,
    selectedReportFilters,
  ]);
  const rewardFinancialYearOptions = useMemo(() => {
    const years = new Set<number>([getCurrentRewardFinancialYearStart()]);
    adminKpiForms.forEach((form) => {
      const year = getRewardKpiFinancialYearStart(form);
      if (year !== null) years.add(year);
    });
    data?.rewardSpendByFinancialYear?.forEach(({ financialYear }) => years.add(financialYear));
    return Array.from(years).sort((left, right) => right - left);
  }, [adminKpiForms, data?.rewardSpendByFinancialYear]);
  const rewardFinancialYearKpiForms = useMemo(() => {
    if (!isRewardAdminView || effectiveRewardAdminRole !== 'function') return adminKpiForms;
    return adminKpiForms.filter(
      (form) => getRewardKpiFinancialYearStart(form) === selectedRewardFinancialYear,
    );
  }, [adminKpiForms, effectiveRewardAdminRole, isRewardAdminView, selectedRewardFinancialYear]);
  const scsReportQuarterOptions = useMemo(() => {
    if (tenant !== 'scs' || !isAdminView) return [];

    const availableQuarters = new Set<string>();
    adminKpiForms.forEach((form) => {
      const closeAt = formConfig.formCloseAt ?? form.formCloseAt;
      scsReportQuartersFromForm(
        form,
        selectedReportYear,
        closeAt,
        strictDeadline,
        sections,
      ).forEach((quarter) => availableQuarters.add(quarter));
    });

    return SCS_REPORTING_QUARTER_OPTIONS.filter((quarter) => availableQuarters.has(quarter));
  }, [
    adminKpiForms,
    formConfig.formCloseAt,
    isAdminView,
    sections,
    selectedReportYear,
    strictDeadline,
    tenant,
  ]);
  const scsStatusOptions = useMemo(() => {
    if (tenant !== 'scs' || !isAdminView) return [];

    const availableStatuses = new Set<string>();
    adminKpiForms.forEach((form) => {
      const closeAt = formConfig.formCloseAt ?? form.formCloseAt;
      if (
        selectedReportQuarter &&
        !scsReportQuartersFromForm(
          form,
          selectedReportYear,
          closeAt,
          strictDeadline,
          sections,
        ).includes(selectedReportQuarter)
      ) {
        return;
      }

      const status = getScsStatusLabel(form, closeAt, strictDeadline, sections).trim();
      if (status) availableStatuses.add(status);
    });

    return Array.from(availableStatuses).sort((left, right) =>
      left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' }),
    );
  }, [
    adminKpiForms,
    formConfig.formCloseAt,
    isAdminView,
    sections,
    selectedReportQuarter,
    selectedReportYear,
    strictDeadline,
    tenant,
  ]);
  const psfaAdminKpiCounts = useMemo(() => {
    const counts: Record<PsfaAdminKpiStatus, number> = {
      inProgress: 0,
      internalApproval: 0,
      parentApproval: 0,
      psfaApproval: 0,
      complete: 0,
    };
    adminKpiForms.forEach((form) => {
      counts[getPsfaAdminKpiStatus(form)] += 1;
    });
    return counts;
  }, [adminKpiForms]);
  const psfaAdminKpiCards = useMemo(
    () =>
      PSFA_ADMIN_KPI_DEFINITIONS.map((definition) => ({
        ...definition,
        count: psfaAdminKpiCounts[definition.status],
      })),
    [psfaAdminKpiCounts],
  );
  const scsAdminKpiCards = useMemo(() => {
    if (tenant !== 'scs' || !isAdminView) return [];

    const cardsByQuarter = SCS_ADMIN_KPI_QUARTERS.map((quarter) => {
      const counts: Record<ScsAdminKpiStatus, number> = {
        inProgress: 0,
        awaitingApproval: 0,
        approved: 0,
        approvedStaffMissing: 0,
      };

      adminKpiForms.forEach((form) => {
        if (form.scsProgress) {
          const status = form.scsProgress.quarters[quarter]?.status;
          if (status && Object.hasOwn(counts, status)) {
            counts[status as ScsAdminKpiStatus] += 1;
          }
          return;
        }
        const closeAt = formConfig.formCloseAt ?? form.formCloseAt;
        const statusLabel = getScsStatusLabel(form, closeAt, strictDeadline, sections);
        if (statusLabel === `${quarter} Awaiting staff completion`) {
          counts.inProgress += 1;
        }

        const state = String(form.formState || '')
          .trim()
          .toLowerCase();
        if (
          state === 'approval' &&
          (statusLabel === `${quarter} Awaiting manager sign-off` ||
            statusLabel === `${quarter} Awaiting sign-off` ||
            statusLabel === `${quarter} Sign-off required` ||
            statusLabel === `${quarter} Approval` ||
            statusLabel === getApprovalRoundLabel(form)) &&
          (normalizeQuarterCode(form.pendingApprovalQuarter) ||
            normalizeQuarterCode(form.approvalStatus)) === quarter
        ) {
          counts.awaitingApproval += 1;
        }

        const hasApprovedQuarter =
          form.managerCompletedQuarters?.includes(quarter) ??
          approvalPeriodsFromForm(form).some(
            (period) =>
              parseReportPeriodQuarter(period) === `Quarter ${quarter.slice(1)}` &&
              (!selectedReportYear || parseReportPeriodYear(period) === selectedReportYear),
          );
        if (hasApprovedQuarter) {
          if (
            (form.staffCompletionMissingQuarters ?? form.staffCompletionRequiredQuarters)?.includes(
              quarter,
            )
          ) {
            counts.approvedStaffMissing += 1;
          } else {
            counts.approved += 1;
          }
        }
      });

      return {
        quarter,
        counts,
        cards: SCS_ADMIN_KPI_STATUSES.map(({ status, label }) => ({
          status,
          label: `${quarter} ${label}`,
          count: counts[status],
          testId: `admin-kpi-scs-${quarter.toLowerCase()}-${status
            .replace(/([A-Z])/g, '-$1')
            .toLowerCase()}`,
        })),
      };
    });

    return cardsByQuarter
      .filter(({ quarter, counts }) => {
        const matchesSelectedQuarter =
          !selectedReportQuarter || `Quarter ${quarter.slice(1)}` === selectedReportQuarter;
        if (!matchesSelectedQuarter) return false;
        if (selectedReportQuarter) return true;
        return (
          !selectedReportYear ||
          counts.inProgress > 0 ||
          counts.awaitingApproval > 0 ||
          counts.approvedStaffMissing > 0 ||
          counts.approved > 0
        );
      })
      .flatMap(({ cards }) => cards);
  }, [
    adminKpiForms,
    formConfig.formCloseAt,
    isAdminView,
    sections,
    selectedReportQuarter,
    selectedReportYear,
    strictDeadline,
    tenant,
  ]);
  const inProgressKpiCount = useMemo(
    () =>
      adminKpiForms.filter((form) => {
        if (!hasNonEmptyIdentifier(form.identifier)) return false;

        const hasPopulatedAnswers = form.hasPopulatedAnswers === true;
        if (!hasPopulatedAnswers) return false;

        const lastActivity = toDate(form.lastActive);
        if (!lastActivity) return false;
        const ageInDays = (Date.now() - lastActivity.getTime()) / MS_IN_DAY;
        if (ageInDays > IN_PROGRESS_KPI_WINDOW_DAYS) return false;

        const closeAt = formConfig.formCloseAt ?? form.formCloseAt;
        const label = getDefaultStatusLabel(
          form,
          formConfig.allowUnlock,
          hasAdminApprovalTarget,
          closeAt,
          strictDeadline,
          tenant,
          sections,
        );
        return label.toLowerCase().endsWith('in progress');
      }).length,
    [adminKpiForms, formConfig, hasAdminApprovalTarget, sections, strictDeadline, tenant],
  );
  const rewardStatusKpiRows = useMemo(() => {
    if (!isRewardAdminView) return [];

    const normalizedSearchTerm = normalizeAdminSearchText(rewardAdminSearchTerm);
    return rewardFinancialYearKpiForms
      .filter((form) => {
        const state = String(form.formState || '')
          .trim()
          .toLowerCase();
        return state !== 'active';
      })
      .map((form) => {
        const closeAt = formConfig.formCloseAt ?? form.formCloseAt;
        const statusLabel =
          effectiveRewardAdminRole === 'cos' && isRewardCosVoucherForm(form)
            ? getRewardCosVoucherStatusLabel(form)
            : getStatusLabel(
                form,
                formConfig.allowUnlock,
                hasAdminApprovalTarget,
                formConfig.statusOverride,
                closeAt,
                strictDeadline,
                tenant,
                sections,
              );
        return {
          form,
          statusLabel,
          normalizedStatusLabel: statusLabel.trim().toLowerCase(),
        };
      })
      .filter(({ form, statusLabel }) =>
        rewardAdminFormMatchesSearch(form, statusLabel, normalizedSearchTerm),
      );
  }, [
    effectiveRewardAdminRole,
    formConfig.allowUnlock,
    formConfig.formCloseAt,
    formConfig.statusOverride,
    hasAdminApprovalTarget,
    isRewardAdminView,
    rewardAdminSearchTerm,
    rewardFinancialYearKpiForms,
    sections,
    strictDeadline,
    tenant,
  ]);
  const completeKpiCount = useMemo(() => {
    if (isRewardAdminView) {
      if (effectiveRewardAdminRole === 'cos') {
        return rewardStatusKpiRows.filter(({ normalizedStatusLabel }) =>
          REWARD_COS_COMPLETE_STATUSES.has(normalizedStatusLabel),
        ).length;
      }
      return rewardStatusKpiRows.filter(
        ({ normalizedStatusLabel }) => normalizedStatusLabel === REWARD_FUNCTION_APPROVED_STATUS,
      ).length;
    }

    return adminKpiForms.filter((form) => {
      const state = String(form.formState || '')
        .trim()
        .toLowerCase();
      return state === 'submitted' || state === 'approved';
    }).length;
  }, [adminKpiForms, effectiveRewardAdminRole, isRewardAdminView, rewardStatusKpiRows]);
  const awaitingApprovalKpiCount = useMemo(() => {
    if (isRewardAdminView) {
      if (effectiveRewardAdminRole === 'cos') {
        return rewardStatusKpiRows.filter(({ normalizedStatusLabel }) =>
          REWARD_COS_AWAITING_ACTION_STATUSES.has(normalizedStatusLabel),
        ).length;
      }
      return rewardStatusKpiRows.filter(
        ({ normalizedStatusLabel }) => normalizedStatusLabel === REWARD_FUNCTION_AWAITING_STATUS,
      ).length;
    }

    return adminKpiForms.filter(
      (form) =>
        String(form.formState || '')
          .trim()
          .toLowerCase() === 'approval',
    ).length;
  }, [adminKpiForms, effectiveRewardAdminRole, isRewardAdminView, rewardStatusKpiRows]);
  const rejectedKpiCount = useMemo(
    () =>
      isRewardAdminView && effectiveRewardAdminRole === 'function'
        ? rewardStatusKpiRows.filter(
            ({ normalizedStatusLabel }) =>
              normalizedStatusLabel === REWARD_FUNCTION_REJECTED_STATUS,
          ).length
        : 0,
    [effectiveRewardAdminRole, isRewardAdminView, rewardStatusKpiRows],
  );
  const rewardYearSpend = useMemo(
    () => (data?.rewardSpendByFinancialYear || []).filter(
      (total) => total.financialYear === selectedRewardFinancialYear,
    ),
    [data?.rewardSpendByFinancialYear, selectedRewardFinancialYear],
  );
  const rewardOverallPendingSpend = rewardYearSpend.reduce(
    (total, entry) => total + parseRewardSpendAmount(entry.pending), 0,
  );
  const rewardApprovedSpendTotal = rewardYearSpend.reduce(
    (total, entry) => total + parseRewardSpendAmount(entry.approved), 0,
  );
  const rewardPaidSpendTotal = rewardYearSpend.reduce(
    (total, entry) => total + parseRewardSpendAmount(entry.paid),
    0,
  );
  const rewardPendingPaymentTotal = rewardApprovedSpendTotal - rewardPaidSpendTotal;
  const rewardPotRemaining = REWARD_RR_POT_TOTAL - rewardApprovedSpendTotal;
  const rewardFunctionSpendCards = rewardYearSpend
    .filter((entry) =>
      !showRewardFunctionFilterSelect ||
      selectedRewardFunctionFilter === REWARD_FUNCTION_ADMIN_ALL_VALUE ||
      normalizeRewardFunction(entry.rewardFunction) === normalizeRewardFunction(selectedRewardFunctionFilter),
    )
    .map((entry) => ({
      rewardFunction: entry.rewardFunction,
      approved: parseRewardSpendAmount(entry.approved),
      pending: parseRewardSpendAmount(entry.pending),
      paid: parseRewardSpendAmount(entry.paid),
    }))
    .sort((a, b) => a.rewardFunction.localeCompare(b.rewardFunction));
  const showPsfaAdminKpis = tenant === 'psfa' && isAdminView && !isRewardAdminView;
  const showScsAdminKpis = tenant === 'scs' && isAdminView;
  const adminDepartmentFilterLabel = 'Department';
  const adminDepartmentFilterPlaceholder = 'Select department';
  const adminOrganisationFilterLabel = 'Organisation';
  const adminOrganisationFilterPlaceholder = 'Select organisation';
  const showInProgressKpi = !isRewardAdminView && !showPsfaAdminKpis && !showScsAdminKpis;
  const showAwaitingApprovalKpi =
    !showPsfaAdminKpis &&
    !showScsAdminKpis &&
    (isRewardAdminView || formConfig.approval === 'always');
  const awaitingApprovalKpiLabel =
    isRewardAdminView && effectiveRewardAdminRole === 'cos'
      ? 'Awaiting action'
      : 'Awaiting Approval';
  const completeKpiLabel =
    isRewardAdminView && effectiveRewardAdminRole === 'function'
      ? 'Approved forms'
      : isRewardAdminView
        ? 'Completed forms'
        : 'Complete';
  const showRejectedKpi = isRewardAdminView && effectiveRewardAdminRole === 'function';
  const showRewardSpendKpis = tenant === 'reward' && effectiveRewardAdminRole === 'function';
  const showRewardPotKpi = showRewardSpendKpis && data?.rewardIsAdministrator === true;
  const showRewardRoleSelect =
    isRewardAdminView && hasRewardCosApproverRole && hasRewardFunctionApproverRole;
  const isRewardCosAdminView = isRewardAdminView && effectiveRewardAdminRole === 'cos';
  const showAdminAuditColumn = showAdminUsersColumn || isRewardAdminView;
  const showIdentifierColumn =
    typeof formConfig.identifier === 'string' && formConfig.identifier.trim().length > 0;
  const showLastActiveColumn = !isRewardAdminView;
  const adminRoleFormId = useMemo(() => {
    const explicit = String(data?.adminFormId || '').trim();
    if (explicit) return explicit;
    const fallback = forms.find(
      (form) =>
        String(form.role || '')
          .trim()
          .toLowerCase() === 'admin',
    );
    return String(fallback?.formId || '').trim();
  }, [data?.adminFormId, forms]);
  const scsAdminDownloadFilterSourceForms = useMemo(() => {
    if (tenant !== 'scs' || !isAdminView) return [];
    return rewardAdminScopedForms.filter((form) => {
      const formId = String(form.formId || '').trim();
      return formId && formId !== adminRoleFormId;
    });
  }, [adminRoleFormId, isAdminView, rewardAdminScopedForms, tenant]);
  const scsAdminDownloadYearForms = useMemo(() => {
    if (!adminDownloadReportYear) return scsAdminDownloadFilterSourceForms;
    return scsAdminDownloadFilterSourceForms.filter((form) =>
      filterPeriodsFromForm(form).some(
        (period) => parseReportPeriodYear(period) === adminDownloadReportYear,
      ),
    );
  }, [adminDownloadReportYear, filterPeriodsFromForm, scsAdminDownloadFilterSourceForms]);
  const scsAdminDownloadQuarterOptions = useMemo(() => {
    const availableQuarters = new Set<string>();
    scsAdminDownloadYearForms.forEach((form) => {
      const closeAt = formConfig.formCloseAt ?? form.formCloseAt;
      scsReportQuartersFromForm(
        form,
        adminDownloadReportYear,
        closeAt,
        strictDeadline,
        sections,
      ).forEach((quarter) => availableQuarters.add(quarter));
    });
    return SCS_REPORTING_QUARTER_OPTIONS.filter((quarter) => availableQuarters.has(quarter));
  }, [
    adminDownloadReportYear,
    formConfig.formCloseAt,
    scsAdminDownloadYearForms,
    sections,
    strictDeadline,
  ]);
  const scsAdminDownloadQuarterForms = useMemo(() => {
    if (!adminDownloadReportQuarter) return scsAdminDownloadYearForms;
    return scsAdminDownloadYearForms.filter((form) => {
      const closeAt = formConfig.formCloseAt ?? form.formCloseAt;
      return scsReportQuartersFromForm(
        form,
        adminDownloadReportYear,
        closeAt,
        strictDeadline,
        sections,
      ).includes(adminDownloadReportQuarter);
    });
  }, [
    adminDownloadReportQuarter,
    adminDownloadReportYear,
    formConfig.formCloseAt,
    scsAdminDownloadYearForms,
    sections,
    strictDeadline,
  ]);
  const scsAdminDownloadStatusOptions = useMemo(() => {
    const availableStatuses = new Set<string>();
    scsAdminDownloadQuarterForms.forEach((form) => {
      const closeAt = formConfig.formCloseAt ?? form.formCloseAt;
      const status = getScsStatusLabel(form, closeAt, strictDeadline, sections).trim();
      if (status) availableStatuses.add(status);
    });
    return Array.from(availableStatuses).sort((left, right) =>
      left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' }),
    );
  }, [formConfig.formCloseAt, scsAdminDownloadQuarterForms, sections, strictDeadline]);
  const scsAdminDownloadForms = useMemo(() => {
    if (tenant !== 'scs' || !isAdminView) return [];
    return baseFormsForView.filter((form) => {
      const formId = String(form.formId || '').trim();
      if (!formId || formId === adminRoleFormId) return false;
      if (
        adminDownloadReportYear &&
        !filterPeriodsFromForm(form).some(
          (period) => parseReportPeriodYear(period) === adminDownloadReportYear,
        )
      ) {
        return false;
      }

      const closeAt = formConfig.formCloseAt ?? form.formCloseAt;
      if (
        adminDownloadReportQuarter &&
        !scsReportQuartersFromForm(
          form,
          adminDownloadReportYear,
          closeAt,
          strictDeadline,
          sections,
        ).includes(adminDownloadReportQuarter)
      ) {
        return false;
      }
      if (
        adminDownloadScsStatus &&
        getScsStatusLabel(form, closeAt, strictDeadline, sections) !== adminDownloadScsStatus
      ) {
        return false;
      }
      return true;
    });
  }, [
    adminDownloadReportQuarter,
    adminDownloadReportYear,
    adminDownloadScsStatus,
    adminRoleFormId,
    baseFormsForView,
    filterPeriodsFromForm,
    formConfig.formCloseAt,
    isAdminView,
    sections,
    strictDeadline,
    tenant,
  ]);
  const scsAdminDownloadReportFilters = useMemo(() => {
    if (tenant !== 'scs' || !adminDownloadReportYear) return [];
    return Array.from(
      new Set(
        reportPeriodOptions
          .filter((option) => option.year === adminDownloadReportYear)
          .map((option) => option.raw),
      ),
    );
  }, [adminDownloadReportYear, reportPeriodOptions, tenant]);
  const adminDownloadReportFilters =
    tenant === 'scs' ? scsAdminDownloadReportFilters : selectedReportFilters;
  const includeAdminDownloadAwaitingApproval =
    tenant === 'scs' || includeAdminAwaitingApprovalDownloads;
  const includeAdminDownloadInProgress = tenant === 'scs' || includeAdminInProgressDownloads;
  useEffect(() => {
    if (
      tenant === 'scs' &&
      isAdminDownloadModalOpen &&
      adminDownloadReportQuarter &&
      !scsAdminDownloadQuarterOptions.includes(adminDownloadReportQuarter)
    ) {
      setAdminDownloadReportQuarter('');
    }
  }, [
    adminDownloadReportQuarter,
    isAdminDownloadModalOpen,
    scsAdminDownloadQuarterOptions,
    tenant,
  ]);
  useEffect(() => {
    if (
      tenant === 'scs' &&
      isAdminDownloadModalOpen &&
      adminDownloadScsStatus &&
      !scsAdminDownloadStatusOptions.includes(adminDownloadScsStatus)
    ) {
      setAdminDownloadScsStatus('');
    }
  }, [adminDownloadScsStatus, isAdminDownloadModalOpen, scsAdminDownloadStatusOptions, tenant]);
  const visibleForms = useMemo(() => {
    const reportFilteredForms = (() => {
      if (!isAdminView) return baseFormsForView;
      if (!hasActiveReportFilter) return baseFormsForView;
      if (selectedReportFilters.length === 0) return [];
      const selectedSet = new Set(
        selectedReportFilters.map((value) => normalizeReportPeriod(value)),
      );
      return baseFormsForView.filter((form) =>
        filterPeriodsFromForm(form).some((value) => selectedSet.has(normalizeReportPeriod(value))),
      );
    })();
    const quarterFilteredForms =
      showScsAdminKpis && selectedReportQuarter
        ? reportFilteredForms.filter((form) => {
            const closeAt = formConfig.formCloseAt ?? form.formCloseAt;
            return scsReportQuartersFromForm(
              form,
              selectedReportYear,
              closeAt,
              strictDeadline,
              sections,
            ).includes(selectedReportQuarter);
          })
        : reportFilteredForms;
    const statusFilteredForms =
      showScsAdminKpis && selectedScsStatus
        ? quarterFilteredForms.filter((form) => {
            const closeAt = formConfig.formCloseAt ?? form.formCloseAt;
            return getScsStatusLabel(form, closeAt, strictDeadline, sections) === selectedScsStatus;
          })
        : quarterFilteredForms;

    const normalizedRewardSearchTerm = normalizeAdminSearchText(rewardAdminSearchTerm);
    if (isRewardAdminView && normalizedRewardSearchTerm) {
      return statusFilteredForms.filter((form) => {
        if (isRewardCosVoucherForm(form)) {
          return rewardAdminFormMatchesSearch(
            form,
            getRewardCosVoucherStatusLabel(form),
            normalizedRewardSearchTerm,
          );
        }

        const closeAt = formConfig.formCloseAt ?? form.formCloseAt;
        const statusLabel = getStatusLabel(
          form,
          formConfig.allowUnlock,
          hasAdminApprovalTarget,
          formConfig.statusOverride,
          closeAt,
          strictDeadline,
          tenant,
          sections,
        );
        return rewardAdminFormMatchesSearch(form, statusLabel, normalizedRewardSearchTerm);
      });
    }

    const normalizedScsSearchTerm = normalizeAdminSearchText(scsAdminSearchTerm);
    if (showScsAdminKpis && normalizedScsSearchTerm) {
      return statusFilteredForms.filter((form) => {
        const closeAt = formConfig.formCloseAt ?? form.formCloseAt;
        const statusLabel = getScsStatusLabel(form, closeAt, strictDeadline, sections);
        return scsAdminFormMatchesSearch(form, statusLabel, normalizedScsSearchTerm);
      });
    }

    return statusFilteredForms;
  }, [
    baseFormsForView,
    filterPeriodsFromForm,
    formConfig.allowUnlock,
    formConfig.formCloseAt,
    formConfig.statusOverride,
    hasActiveReportFilter,
    hasAdminApprovalTarget,
    isAdminView,
    isRewardAdminView,
    rewardAdminSearchTerm,
    scsAdminSearchTerm,
    selectedReportQuarter,
    selectedReportFilters,
    selectedReportYear,
    selectedScsStatus,
    sections,
    showScsAdminKpis,
    strictDeadline,
    tenant,
  ]);
  const visibleTableForms = useMemo(() => {
    if (!overviewTableSort) return visibleForms;
    return visibleForms
      .map((form, index) => ({ form, index }))
      .sort((left, right) => {
        const dateDiff = compareDates(
          getOverviewTableSortDate(
            left.form,
            overviewTableSort.column,
            isRewardAdminView,
            isRewardCosAdminView,
          ),
          getOverviewTableSortDate(
            right.form,
            overviewTableSort.column,
            isRewardAdminView,
            isRewardCosAdminView,
          ),
          overviewTableSort.direction,
        );
        if (dateDiff !== 0) return dateDiff;
        return left.index - right.index;
      })
      .map(({ form }) => form);
  }, [isRewardAdminView, isRewardCosAdminView, overviewTableSort, visibleForms]);
  const rewardAdminTotalPages = isRewardAdminView
    ? Math.max(1, Math.ceil(visibleTableForms.length / rewardAdminPageSize))
    : 1;
  const rewardAdminPaginationItems = useMemo(
    () => buildOverviewPaginationItems(rewardAdminPage, rewardAdminTotalPages),
    [rewardAdminPage, rewardAdminTotalPages],
  );
  const paginatedVisibleTableForms = useMemo(() => {
    if (!isRewardAdminView) return visibleTableForms;
    const startIndex = (rewardAdminPage - 1) * rewardAdminPageSize;
    return visibleTableForms.slice(startIndex, startIndex + rewardAdminPageSize);
  }, [isRewardAdminView, rewardAdminPage, rewardAdminPageSize, visibleTableForms]);
  const rewardAdminFirstResult =
    visibleTableForms.length === 0 ? 0 : (rewardAdminPage - 1) * rewardAdminPageSize + 1;
  const rewardAdminLastResult = Math.min(
    rewardAdminPage * rewardAdminPageSize,
    visibleTableForms.length,
  );
  useEffect(() => {
    if (!isRewardAdminView) return;
    setRewardAdminPage(1);
  }, [
    effectiveRewardAdminRole,
    hideRewardApprovedForms,
    isRewardAdminView,
    overviewTableSort,
    rewardAdminSearchTerm,
    selectedRewardFunctionFilter,
    selectedRewardTypeFilter,
  ]);
  useEffect(() => {
    setRewardAdminPage((currentPage) => Math.min(currentPage, rewardAdminTotalPages));
  }, [rewardAdminTotalPages]);
  const voucherBulkUploadRows = useMemo(() => {
    if (!isAdminView || tenant !== 'reward' || effectiveRewardAdminRole !== 'cos') return [];
    return visibleForms.filter(isRewardCosVoucherIssueRow);
  }, [effectiveRewardAdminRole, isAdminView, tenant, visibleForms]);
  const voucherBulkUploadEntries = useMemo(
    () =>
      voucherBulkUploadRows.map((form) => ({
        formId: form.formId,
        nomineeId: String(form.rewardCosNomineeId || ''),
        awardType: form.rewardCosAwardType,
      })),
    [voucherBulkUploadRows],
  );
  const showVoucherBulkUploadButton =
    tenant === 'reward' && isAdminView && effectiveRewardAdminRole === 'cos';
  const scsFilteredFormIds = useMemo(
    () =>
      tenant === 'scs'
        ? visibleForms
            .map((form) => String(form.formId || '').trim())
            .filter((formId) => formId && formId !== adminRoleFormId)
        : [],
    [adminRoleFormId, tenant, visibleForms],
  );

  const adminDownloadFormsInScope = useMemo(() => {
    if (!isAdminView) return [];
    if (tenant !== 'scs' && hasReportPeriodQuestion && adminDownloadReportFilters.length === 0) {
      return [];
    }

    const downloadableStates = getAdminDownloadableStates(
      includeAdminDownloadAwaitingApproval,
      includeAdminDownloadInProgress,
    );
    const selectedSet = new Set(
      adminDownloadReportFilters.map((value) => normalizeReportPeriod(value)),
    );

    const sourceForms = tenant === 'scs' ? scsAdminDownloadForms : departmentFilteredForms;
    return sourceForms.filter((form) => {
      const formId = String(form.formId || '').trim();
      if (!formId || (adminRoleFormId && formId === adminRoleFormId)) return false;

      const formState = String(form.formState || '')
        .trim()
        .toLowerCase();
      if (!downloadableStates.has(formState)) return false;

      if (selectedSet.size === 0) return true;
      return filterPeriodsFromForm(form).some((value) =>
        selectedSet.has(normalizeReportPeriod(value)),
      );
    });
  }, [
    adminRoleFormId,
    adminDownloadReportFilters,
    departmentFilteredForms,
    filterPeriodsFromForm,
    hasReportPeriodQuestion,
    includeAdminDownloadAwaitingApproval,
    includeAdminDownloadInProgress,
    isAdminView,
    scsAdminDownloadForms,
    tenant,
  ]);
  const adminDownloadCount = adminDownloadFormsInScope.length;
  const filteredResponseHint = useMemo(() => {
    const count = visibleForms.length;
    if (tenant === 'scs') return `${count} ${count === 1 ? 'item' : 'items'} selected`;
    if (count === 0) return 'No matches found';
    if (count === 1) return 'Showing 1 response';
    return `Showing ${count} responses`;
  }, [tenant, visibleForms.length]);
  const submissionDeadlineBannerText = formConfig.submissionDeadlineBannerText;
  const selectedFormId = resolveSelectedFormId(tenant, location?.search || '');
  const isPreviewMode = isAdminPreviewMode({
    search: location?.search || '',
    selectedFormId,
    hasAdminAccess: data?.hasAdminAccess,
    role: data?.role,
    sessionRoles: data?.sessionRoles,
    forms: data?.forms,
    allowPendingAccessCheck: true,
  });
  const adminPreviewPath = withFormIdParam(
    `${tenantBasePath}?admin=1`,
    adminRoleFormId || undefined,
  );
  const returnToFormsPath = `${location.pathname}${location.search}${location.hash}`;
  const hashTarget = location.hash ? location.hash.replace('#', '') : '';
  const isFormsOnlyPayloadForSelectedForm =
    Boolean(selectedFormId) &&
    !data?.formId &&
    forms.length > 0 &&
    Object.keys(answers).length === 0;
  const showMyForms =
    requestedMyFormsView === true ||
    (isAdminView
      ? !selectedFormId
      : !selectedFormId || forms.length > 1 || isFormsOnlyPayloadForSelectedForm);

  useEffect(() => {
    if (suppressOverviewLoading && !isLoading && !isAnyFastToggleBusy) {
      setSuppressOverviewLoading(false);
    }
  }, [isAnyFastToggleBusy, isLoading, suppressOverviewLoading]);

  useEffect(() => {
    const availableQuarterOptions = useScsReportingYearFilter
      ? scsReportQuarterOptions
      : reportQuarterOptions;
    if (selectedReportQuarter && !availableQuarterOptions.includes(selectedReportQuarter)) {
      setSelectedReportQuarter('');
    }
  }, [
    reportQuarterOptions,
    scsReportQuarterOptions,
    selectedReportQuarter,
    useScsReportingYearFilter,
  ]);

  useEffect(() => {
    if (selectedScsStatus && !scsStatusOptions.includes(selectedScsStatus)) {
      setSelectedScsStatus('');
    }
  }, [scsStatusOptions, selectedScsStatus]);

  useEffect(() => {
    if (!isAdminView) return;

    if (useScsReportingYearFilter) {
      if (scsAdminReportDefaultsAppliedRef.current || scsAdminDefaultReportYearRef.current) {
        if (selectedReportYear && !reportYearOptions.includes(selectedReportYear)) {
          setSelectedReportYear('');
        }
        return;
      }

      if (reportYearOptions.length === 0) return;
      const currentReportYear = getCurrentScsReportingYear();
      if (!reportYearOptions.includes(currentReportYear)) {
        scsAdminReportDefaultsAppliedRef.current = true;
        setSelectedReportYear('');
        setSelectedReportQuarter('');
        return;
      }

      scsAdminDefaultReportYearRef.current = currentReportYear;
      setSelectedReportYear(currentReportYear);
      return;
    }

    scsAdminDefaultReportYearRef.current = '';
    scsAdminReportDefaultsAppliedRef.current = false;
    if (reportYearOptions.length === 0) return;
    if (!selectedReportYear || !reportYearOptions.includes(selectedReportYear)) {
      setSelectedReportYear(reportYearOptions[0]);
    }
  }, [isAdminView, reportYearOptions, selectedReportYear, useScsReportingYearFilter]);

  useEffect(() => {
    if (!useScsReportingYearFilter || scsAdminReportDefaultsAppliedRef.current) return;

    const defaultReportYear = scsAdminDefaultReportYearRef.current;
    if (!defaultReportYear || selectedReportYear !== defaultReportYear) return;

    const availableQuarter = getLatestAvailableScsQuarterForReportYear(defaultReportYear, sections);
    const quarterOption = scsReportingQuarterOptionFromCode(availableQuarter);
    setSelectedReportQuarter(
      quarterOption && scsReportQuarterOptions.includes(quarterOption) ? quarterOption : '',
    );
    scsAdminReportDefaultsAppliedRef.current = true;
  }, [scsReportQuarterOptions, sections, selectedReportYear, useScsReportingYearFilter]);

  useEffect(() => {
    if (selectedReportQuarter || selectedReportYear) {
      setReportPeriodFilterError('');
    }
  }, [selectedReportQuarter, selectedReportYear]);

  useEffect(() => {
    if (selectedDepartment && !departmentOptions.includes(selectedDepartment)) {
      setSelectedDepartment('');
    }
  }, [departmentOptions, selectedDepartment]);

  useEffect(() => {
    if (selectedOrganisation && !organisationOptions.includes(selectedOrganisation)) {
      setSelectedOrganisation('');
    }
  }, [organisationOptions, selectedOrganisation]);

  useEffect(() => {
    if (overviewTableSort?.column === 'lastActive' && !showLastActiveColumn) {
      setOverviewTableSort(null);
    }
  }, [overviewTableSort, showLastActiveColumn]);

  useEffect(() => {
    const isBulkPdfActive =
      activeAdminDownload === 'bulk-pdf' &&
      adminHtmlDownloadProgress !== null &&
      adminHtmlDownloadProgress < 100;

    if (!isBulkPdfActive) {
      setAdminPdfDisplayProgress(0);
      return;
    }

    const intervalId = window.setInterval(() => {
      setAdminPdfDisplayProgress((currentValue) => {
        const nextValue = Math.min(currentValue + 1, 99);
        return adminHtmlDownloadProgress > nextValue ? adminHtmlDownloadProgress : nextValue;
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [activeAdminDownload, adminHtmlDownloadProgress]);

  // Scroll to subsection anchor when returning to overview
  useEffect(() => {
    if (!hashTarget) return;
    const el = document.getElementById(hashTarget);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [hashTarget]);

  const formatDeadline = (value?: string) => formatLondonDateTime(value);

  const formattedDeadline = formatDeadline(formCloseAt);
  const isOverdue = Boolean(formCloseAt) && isPastLondon(formCloseAt ?? undefined);
  const isStrictDeadlineExpired = strictDeadline && (Boolean(data?.expired) || isOverdue);
  const isScsTenant = tenant === 'scs';
  const scsBannerForm = { ...data, formId: selectedFormId || data?.formId || '' };
  const activeScsQuarter = getLatestAvailableScsQuarter(scsBannerForm, sections);
  const isActiveScsQuarterAwaitingApproval =
    isScsTenant &&
    !isStrictDeadlineExpired &&
    (String(data?.formState || '').toLowerCase() === 'approval' ||
      (String(data?.formState || '').toLowerCase() === 'active' &&
        Boolean(data?.pendingApprovalQuarter))) &&
    !!activeScsQuarter &&
    (normalizeQuarterCode(data?.pendingApprovalQuarter) ||
      normalizeQuarterCode(data?.approvalStatus)) === activeScsQuarter &&
    (SCS_QUARTER_ORDER[getScsCompletedQuarter(scsBannerForm)] || 0) <
      SCS_QUARTER_ORDER[activeScsQuarter];
  const scsApproverEmail = String(
    data?.approvalApproverEmail || data?.answers?.['person-details-manager-email'] || '',
  ).trim();
  const showPerformanceCaptureBanner =
    isScsTenant &&
    ['owner', 'editor'].includes(String(data?.role || '').toLowerCase()) &&
    (isActiveScsQuarterAwaitingApproval ||
      getScsStatusLabel(scsBannerForm, formCloseAt, strictDeadline, sections) === 'Up to date');
  const deadlineBannerClassName = [
    isOverdue ? 'govuk-notification-banner--error' : undefined,
    isScsTenant ? 'scs-deadline-banner' : undefined,
  ]
    .filter(Boolean)
    .join(' ');

  const progressData = useMemo(
    () => getOverviewStatus(sections, enabledSubsections, effectiveData),
    [sections, enabledSubsections, effectiveData],
  );
  const hasIncompleteSubsections = useMemo(
    () => hasIncompleteRequiredSubsections(sections, progressData),
    [sections, progressData],
  );

  const lockedSections = useMemo(
    () => getLockedSections(sections, enabledSubsections, effectiveData),
    [sections, enabledSubsections, effectiveData],
  );
  const lockedSubsections = useMemo(
    () => getLockedSubsections(sections, enabledSubsections, effectiveData),
    [sections, enabledSubsections, effectiveData],
  );

  const hasVisiblePagesForSubsection = (
    subsection: (typeof sections)[number]['subsections'][number],
  ) => {
    if (isSubsectionNotRequired(subsection, effectiveData)) return true;
    const subsectionPages = enabledSubsections[subsection.slug]?.pages || subsection.pages || [];
    const visiblePages = subsectionPages.filter((page) => !isWorkflowOnlyPageName(page.name));
    if (subsection.dynamicPages) {
      return subsectionPages.length > 0;
    }
    return visiblePages.length > 0;
  };

  const visibleSections = useMemo(
    () =>
      sections.filter(
        (section) =>
          lockedSections[section.slug] === true ||
          section.subsections.some(
            (subsection) =>
              lockedSubsections[subsection.slug] === true ||
              hasVisiblePagesForSubsection(subsection),
          ),
      ),
    [sections, enabledSubsections, lockedSections, lockedSubsections, progressData, effectiveData],
  );

  const handleStartNewForm = async () => {
    if (!tenant) return;
    if (isStrictDeadlineExpired) return;
    setCreateFormError('');
    try {
      const token = (await fetchJWT()).idToken;
      if (!token) {
        throw new Error('No auth token');
      }
      const created = await fetchForm(token, { new_form: 'true' });
      const newFormId = typeof created?.formId === 'string' ? String(created.formId).trim() : '';
      if (!newFormId) {
        throw new Error('No form id returned');
      }
      setSelectedFormIdInSession(tenant, newFormId);
      navigate(tenantBasePath || '/');
    } catch (error) {
      console.error('Failed to create new form', error);
      setCreateFormError('Error: failed to start a new form, try again later');
    }
  };

  const getSectionNilTargets = (
    section: (typeof sections)[number],
    currentAnswers: typeof answers,
  ) => {
    if (lockedSections[section.slug]) return [];
    const targets = [];
    for (const subsection of section.subsections) {
      const nilPage = subsection.pages.find((page) => page.type === 'nilreturn' && page.fastToggle);
      const condition = nilPage?.disabled?.[0];
      const field = condition?.field;
      if (
        !field ||
        condition.condition !== 'equals' ||
        condition.value !== 'No' ||
        lockedSubsections[subsection.slug] ||
        subsection.dynamicPages ||
        !hasVisiblePagesForSubsection(subsection) ||
        isSubsectionNotRequired(subsection, effectiveData)
      )
        return [];
      const hasResponses = subsection.pages.some(
        (page) =>
          'questions' in page &&
          extractConditionalQuestions(page.questions || []).some(({ name }) =>
            Object.entries(currentAnswers).some(
              ([key, value]) =>
                (key === name || key.endsWith(`-${name}`)) &&
                hasValue(value) &&
                !(key === field && value === 'No'),
            ),
          ),
      );
      if (hasResponses) return [];
      targets.push({ subsection, field });
    }
    return targets;
  };

  const handleNilFastToggle = async (
    subsectionSlug: string,
    subsectionPages: (typeof sections)[number]['subsections'][number]['pages'],
    controllingField: string,
    isNilReturnSelected: boolean,
  ) => {
    const controllerPage = subsectionPages.find(
      (page) =>
        'questions' in page &&
        Array.isArray(page.questions) &&
        extractConditionalQuestions(page.questions).some(
          (question) => question.name === controllingField,
        ),
    );

    if (!controllerPage) return false;

    const currentOverrideExists = Object.hasOwn(fastToggleAnswerOverrides, controllingField);
    const previousOverride = currentOverrideExists
      ? fastToggleAnswerOverrides[controllingField]
      : undefined;
    const nextValue = isNilReturnSelected ? '' : 'No';
    const previousFormQueryData = queryClient.getQueriesData({
      queryKey: [Query.FormData],
    });

    const applyAnswerToCachedFormData = (value: string) => {
      queryClient.setQueriesData({ queryKey: [Query.FormData] }, (previous) => {
        if (!previous || typeof previous !== 'object') return previous;
        const previousRecord = previous as Record<string, unknown>;
        const previousAnswers =
          previousRecord.answers && typeof previousRecord.answers === 'object'
            ? (previousRecord.answers as Record<string, unknown>)
            : {};
        const nextAnswers = { ...previousAnswers };
        if (value === '') {
          delete nextAnswers[controllingField];
        } else {
          nextAnswers[controllingField] = value;
        }
        return {
          ...previousRecord,
          answers: nextAnswers,
        };
      });
    };

    setFastToggleAnswerOverrides((current) => ({
      ...current,
      [controllingField]: nextValue ? nextValue : null,
    }));
    applyAnswerToCachedFormData(nextValue);
    setSuppressOverviewLoading(true);
    setFastToggleBusyBySubsection((current) => ({
      ...current,
      [subsectionSlug]: true,
    }));

    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');

      await updateForm(
        token,
        {
          'subsection-id': subsectionSlug,
          'page-id': controllerPage.name,
          'is-save-unfinished': false,
          answers: {
            [controllingField]: nextValue,
          },
        },
        selectedFormId ? { form_id: selectedFormId } : undefined,
      );
      await refetch();
      return true;
    } catch (error) {
      console.error('Failed to toggle nil return value', error);
      previousFormQueryData.forEach(([queryKey, queryData]) => {
        queryClient.setQueryData(queryKey, queryData);
      });
      setFastToggleAnswerOverrides((current) => {
        const next = { ...current };
        if (currentOverrideExists) {
          next[controllingField] = previousOverride ?? null;
        } else {
          delete next[controllingField];
        }
        return next;
      });
      return false;
    } finally {
      setFastToggleBusyBySubsection((current) => ({
        ...current,
        [subsectionSlug]: false,
      }));
    }
  };

  const handleSectionNilReturn = async (
    section: (typeof sections)[number],
    clearNilReturn: boolean,
  ) => {
    setNilSectionBusy(true);
    setNilSectionError('');
    try {
      const fresh = await refetch();
      if (fresh.error || !fresh.data) throw new Error('Unable to check current answers');
      const targets = getSectionNilTargets(section, fresh.data.answers || {});
      if (!targets.length) throw new Error('Section now contains answers');
      for (const { subsection, field } of targets) {
        if ((fresh.data.answers?.[field] === 'No') !== clearNilReturn) continue;
        if (
          !(await handleNilFastToggle(subsection.slug, subsection.pages, field, clearNilReturn))
        ) {
          throw new Error('Nil return save failed');
        }
      }
    } catch (error) {
      console.error('Failed to toggle section nil return', error);
      setNilSectionError(
        clearNilReturn
          ? `Could not reset all of ${section.title} to Not started. Check the page statuses and clear any remaining nil returns individually. Saved changes have been kept.`
          : `Could not set all of ${section.title} to nil return. Check the page statuses and try again. Saved nil returns have been kept.`,
      );
    } finally {
      setNilSectionBusy(false);
    }
  };

  const renderSectionNilReturn = (section: (typeof sections)[number]) => {
    if (
      tenant !== 'psfa' ||
      isPreviewMode ||
      isApprovalActorRole ||
      !['owner', 'editor', 'admin'].includes(String(data?.role || '').toLowerCase()) ||
      !['active', 'rejected'].includes(String(formState || '').toLowerCase()) ||
      isPastLondon(formCloseAt)
    )
      return null;
    const targets = getSectionNilTargets(section, answers);
    if (!targets.length) return null;
    const allNil = targets.every(({ field }) => answers[field] === 'No');
    return (
      <button
        type="button"
        className="app-task-list__fast-toggle"
        aria-label={
          allNil ? `Reset ${section.title} to Not started` : `Mark ${section.title} as nil return`
        }
        aria-pressed={allNil}
        disabled={isAnyFastToggleBusy}
        onClick={() => void handleSectionNilReturn(section, allNil)}
        data-testid={`overview-section-nil-${section.slug}`}
      >
        <img
          src={allNil ? NIL_FAST_TOGGLE_YES_IMAGE : NIL_FAST_TOGGLE_NO_IMAGE}
          alt={allNil ? 'Nil return selected' : 'Set nil return'}
        />
      </button>
    );
  };

  const triggerFileDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleOpenSqlReportsModal = async () => {
    setSqlReportError('');
    setSelectedSqlReportName('');
    setSelectedSqlReportVersion('');
    setIsSqlReportEditorOpen(false);
    setSqlReportDetail(null);
    setSqlReportDraft('');
    setSqlReportEditorError('');
    setIsSqlReportsModalOpen(true);
    setIsSqlReportsLoading(true);
    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');
      const response = await fetchSqlReports(token);
      setSqlReportDefinitions(response.reports);
    } catch (error) {
      console.error('Failed to load SQL reports', error);
      setSqlReportDefinitions([]);
      setSqlReportError(
        buildAdminDownloadErrorMessage('Unable to load SQL reports right now.', error),
      );
    } finally {
      setIsSqlReportsLoading(false);
    }
  };

  const handleCloseSqlReportsModal = () => {
    if (isSqlReportRunning || isSqlReportSaving) return;
    setIsSqlReportsModalOpen(false);
    setSelectedSqlReportName('');
    setSelectedSqlReportVersion('');
    setSqlReportError('');
    setIsSqlReportEditorOpen(false);
    setSqlReportDetail(null);
    setSqlReportDraft('');
    setSqlReportEditorError('');
  };

  const handleSqlReportSelection = (reportName: string) => {
    setSelectedSqlReportName(reportName);
    setSqlReportError('');
    const selectedReport = sqlReportDefinitions.find((report) => report.report_name === reportName);
    const latestVersion = Math.max(...(selectedReport?.form_versions || []));
    setSelectedSqlReportVersion(Number.isFinite(latestVersion) ? String(latestVersion) : '');
  };

  const handleEditSqlReport = async () => {
    if (!selectedSqlReportName) {
      setSqlReportError('Please select a report');
      return;
    }

    setSqlReportError('');
    setSqlReportEditorError('');
    setIsSqlReportEditorOpen(true);
    setIsSqlReportEditorLoading(true);
    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');
      const report = await fetchSqlReport(token, selectedSqlReportName);
      setSqlReportDetail(report);
      setSqlReportDraft(report.sql_query);
    } catch (error) {
      console.error('Failed to load SQL report', error);
      setSqlReportDetail(null);
      setSqlReportDraft('');
      setSqlReportEditorError(
        buildAdminDownloadErrorMessage('Unable to load SQL report right now.', error),
      );
    } finally {
      setIsSqlReportEditorLoading(false);
    }
  };

  const handleCancelSqlReportEdit = () => {
    if (isSqlReportSaving) return;
    setIsSqlReportEditorOpen(false);
    setSqlReportDetail(null);
    setSqlReportDraft('');
    setSqlReportEditorError('');
  };

  const handleSaveSqlReport = async () => {
    if (isSqlReportSaving || !sqlReportDetail) return;
    if (!sqlReportDraft.trim()) {
      setSqlReportEditorError('Enter a SQL query');
      return;
    }

    setSqlReportEditorError('');
    setIsSqlReportSaving(true);
    let validationCompleted = false;
    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');
      const formVersion = Number(selectedSqlReportVersion);
      if (!Number.isInteger(formVersion) || !sqlReportDetail.form_versions.includes(formVersion)) {
        throw new Error('Please select a form version');
      }
      const validationJob = await validateSqlReport(token, {
        reportName: sqlReportDetail.report_name,
        formVersion,
        sqlQuery: sqlReportDraft,
      });
      const completedValidation = await waitForSqlReportCompletion(token, validationJob.job_id);
      validationCompleted = true;
      const updatedReport = await updateSqlReport(token, {
        reportName: sqlReportDetail.report_name,
        sqlQuery: sqlReportDraft,
        revision: sqlReportDetail.revision,
        validationJobId: completedValidation.job_id,
      });
      setSqlReportDefinitions((current) =>
        current.map((report) =>
          report.report_name === updatedReport.report_name
            ? {
                ...report,
                revision: updatedReport.revision,
                updated_at: updatedReport.updated_at,
                updated_by: updatedReport.updated_by,
              }
            : report,
        ),
      );
      setIsSqlReportEditorOpen(false);
      setSqlReportDetail(null);
      setSqlReportDraft('');
      setSqlReportEditorError('');
    } catch (error) {
      console.error('Failed to save SQL report', error);
      setSqlReportEditorError(
        buildAdminDownloadErrorMessage(
          validationCompleted
            ? 'Unable to save SQL report right now.'
            : 'Unable to validate SQL query.',
          error,
        ),
      );
    } finally {
      setIsSqlReportSaving(false);
    }
  };

  const handleRunSqlReport = async () => {
    const formVersion = Number(selectedSqlReportVersion);
    if (!selectedSqlReportName) {
      setSqlReportError('Please select a report');
      return;
    }
    if (!Number.isInteger(formVersion) || !selectedSqlReportVersions.includes(formVersion)) {
      setSqlReportError('Please select a form version');
      return;
    }

    setSqlReportError('');
    setIsSqlReportRunning(true);
    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');
      const startedJob = await startSqlReport(token, {
        reportName: selectedSqlReportName,
        formVersion,
      });
      const completedJob = await waitForSqlReportCompletion(token, startedJob.job_id);
      const download = await downloadSqlReport(token, completedJob.job_id);
      const filename = download.filename;
      if (!filename) throw new Error('SQL report download filename is missing');
      triggerFileDownload(download.blob, filename);
      setIsSqlReportsModalOpen(false);
      setSelectedSqlReportName('');
      setSelectedSqlReportVersion('');
    } catch (error) {
      console.error('SQL report failed', error);
      setSqlReportError(
        buildAdminDownloadErrorMessage('Unable to run SQL report right now.', error),
      );
    } finally {
      setIsSqlReportRunning(false);
    }
  };

  const resetMyFormsDownloadState = () => {
    setMyFormsDownloadForm(null);
    setSelectedMyFormsDownloadFormat('');
    setMyFormsDownloadError('');
  };

  const closeMyFormsDownloadModal = () => {
    if (isMyFormsDownloadSubmitting) return;
    resetMyFormsDownloadState();
  };

  const handleOpenMyFormsDownloadModal = (form: FormListItem) => {
    setMyFormsDownloadForm(form);
    setSelectedMyFormsDownloadFormat('');
    setMyFormsDownloadError('');
  };

  const resetMyFormsDeleteState = () => {
    setMyFormsDeleteForm(null);
    setMyFormsDeleteError('');
  };

  const closeMyFormsDeleteModal = () => {
    if (isMyFormsDeleting) return;
    resetMyFormsDeleteState();
  };

  const handleOpenMyFormsDeleteModal = (form: FormListItem) => {
    setMyFormsDeleteForm(form);
    setMyFormsDeleteError('');
  };

  const handleOpenRewardVoucherIssueModal = (form: FormListItem) => {
    setRewardVoucherIssueForm(form);
    setRewardVoucherIssueChecked(isRewardCosVoucherManualIssueChecked(form));
    setRewardVoucherIssueError('');
    setRewardCosAuditDialog(null);
    setRewardCosAuditError('');
  };

  const closeRewardVoucherIssueModal = () => {
    if (isRewardVoucherIssueSaving || isRewardVoucherPdfDownloading || isRewardCosAuditLoading)
      return;
    setRewardVoucherIssueForm(null);
    setRewardVoucherIssueChecked(false);
    setRewardVoucherIssueError('');
    setRewardCosAuditDialog(null);
    setRewardCosAuditError('');
  };

  const handleCopyRewardVoucherValue = async (label: string, value: string) => {
    const text = String(value || '').trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error(`Unable to copy ${label}`, error);
      setRewardVoucherIssueError(`Unable to copy ${label}.`);
    }
  };

  const downloadRewardVoucherNominationPdf = async () => {
    const form = rewardVoucherIssueForm;
    const formId = String(form?.formId || '').trim();
    if (!form || !formId) {
      setRewardVoucherIssueError('Unable to download this nomination right now.');
      return;
    }

    setRewardVoucherIssueError('');
    setIsRewardVoucherPdfDownloading(true);
    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');

      const formResponse = await fetchForm(token, { form_id: formId });
      const answers =
        formResponse?.answers && typeof formResponse.answers === 'object'
          ? (formResponse.answers as Record<string, unknown>)
          : {};
      const drafts =
        formResponse?.drafts && typeof formResponse.drafts === 'object'
          ? (formResponse.drafts as Record<string, string[]>)
          : {};
      const enabledSubsectionsForExport = getEnabledSubsections(formConfig, formResponse);
      const submittedAt =
        typeof formResponse?.submittedAt === 'string' && formResponse.submittedAt.trim()
          ? formResponse.submittedAt
          : String(form.submittedAt || '').trim() || undefined;
      const html = buildExportHtml(
        tenant,
        formConfig?.title,
        submittedAt,
        formConfig?.sections || [],
        answers,
        drafts,
        enabledSubsectionsForExport,
        formId,
        formResponse?.rewardPanelDecision,
        formResponse?.rewardPanelNotes,
        formResponse,
      );
      const formVersion = formResponse?.formVersion || (formResponse as any)?.form_version;
      const filenameBase = toExportFilenameBase(
        `${formConfig?.title || 'nomination'}-${formId}`,
        'nomination',
      );
      const { blob, filename } = await convertExportHtmlToPdf(token, {
        html,
        filename: filenameBase,
        formVersion,
      });
      triggerFileDownload(blob, filename || `${filenameBase}.pdf`);
    } catch (error) {
      console.error(`COS nomination PDF download failed for form [${formId}]`, error);
      setRewardVoucherIssueError(
        buildAdminDownloadErrorMessage('Unable to download this nomination right now.', error),
      );
    } finally {
      setIsRewardVoucherPdfDownloading(false);
    }
  };

  const openRewardCosAuditModal = async () => {
    const form = rewardVoucherIssueForm;
    const formId = String(form?.formId || '').trim();
    const nomineeId = String(form?.rewardCosNomineeId || '').trim();
    if (!formId || !nomineeId) {
      setRewardCosAuditError('Unable to load audit right now.');
      return;
    }

    setRewardCosAuditError('');
    setIsRewardCosAuditLoading(true);
    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');
      const response = await fetchRewardCosAudit(token, {
        formId,
        nomineeId,
        awardType: form?.rewardCosAwardType,
        identifier: form?.identifier,
      });
      setRewardCosAuditDialog({
        formId,
        identifier: response.identifier || form?.identifier || EMPTY_VALUE,
        awardType: response.awardType || form?.rewardCosAwardType || EMPTY_VALUE,
        audit: Array.isArray(response.audit) ? response.audit : [],
      });
    } catch (error) {
      console.error(
        `Reward COS audit load failed for form [${formId}] nominee [${nomineeId}]`,
        error,
      );
      setRewardCosAuditError(
        buildAdminDownloadErrorMessage('Unable to load audit right now.', error),
      );
    } finally {
      setIsRewardCosAuditLoading(false);
    }
  };

  const openPsfaAuditModal = async (form: FormListItem) => {
    const formId = String(form?.formId || '').trim();
    if (!formId) {
      setPsfaAuditError('Unable to load audit right now.');
      return;
    }

    setPsfaAuditError('');
    setPsfaAccessDialog(null);
    setPsfaAuditLoadingFormId(formId);
    setIsPsfaAuditLoading(true);
    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');
      const response = await fetchPsfaAudit(token, {
        formId,
        identifier: form?.identifier,
      });
      setPsfaAuditDialog({
        formId,
        identifier: response.identifier || form?.identifier || EMPTY_VALUE,
        audit: Array.isArray(response.audit) ? response.audit : [],
      });
    } catch (error) {
      console.error(`Admin audit load failed for form [${formId}]`, error);
      setPsfaAuditError(buildAdminDownloadErrorMessage('Unable to load audit right now.', error));
    } finally {
      setIsPsfaAuditLoading(false);
      setPsfaAuditLoadingFormId('');
    }
  };

  const openPsfaAccessModal = async (form: FormListItem) => {
    const formId = String(form?.formId || '').trim();
    if (!formId) {
      setPsfaAccessError('Unable to load users right now.');
      return;
    }

    setPsfaAccessError('');
    setPsfaAuditDialog(null);
    setPsfaAuditError('');
    setPsfaAccessLoadingFormId(formId);
    setIsPsfaAccessLoading(true);
    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');
      const response = await fetchFormUsers(token, { form_id: formId });
      setPsfaAccessDialog({
        identifier: form?.identifier || EMPTY_VALUE,
        users: Array.isArray(response.access) ? getSortedPsfaAccessUsers(response.access) : [],
      });
    } catch (error) {
      console.error(`PSFA users load failed for form [${formId}]`, error);
      setPsfaAccessError(buildAdminDownloadErrorMessage('Unable to load users right now.', error));
    } finally {
      setIsPsfaAccessLoading(false);
      setPsfaAccessLoadingFormId('');
    }
  };

  const loadRewardTransferFunctionOptions = async (token: string) => {
    const response = await queryAthenaLookup(token, {
      lookupType: 'staffDirectorates',
      environment: resolveRewardTransferAthenaEnvironment(),
      maxRows: 50,
    });
    const options = new Map<string, string>();
    response.rows.forEach((row) => {
      const rawFunction = String(row?.directorate || '').trim();
      if (!rawFunction) return;
      const key = normalizeRewardFunction(rawFunction);
      if (!options.has(key)) options.set(key, rawFunction);
    });
    return Array.from(options.values()).sort((a, b) => a.localeCompare(b));
  };

  const handleOpenRewardTransferOwnershipModal = async () => {
    setIsRewardTransferOwnershipModalOpen(true);
    setIsRewardTransferOwnershipLoading(true);
    setRewardTransferOwnershipError('');
    setRewardTransferEditingFormId('');
    setRewardTransferSelectedFunction('');
    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');
      const [candidateResponse, functionOptions] = await Promise.all([
        fetchRewardTransferOwnershipCandidates(token),
        loadRewardTransferFunctionOptions(token),
      ]);
      setRewardTransferOwnershipRows(candidateResponse.candidates || []);
      setRewardTransferFunctionOptions(functionOptions);
    } catch (error) {
      console.error('Reward transfer ownership load failed', error);
      setRewardTransferOwnershipRows([]);
      setRewardTransferFunctionOptions([]);
      setRewardTransferOwnershipError('Unable to load transfer ownership records right now.');
    } finally {
      setIsRewardTransferOwnershipLoading(false);
    }
  };

  const handleCloseRewardTransferOwnershipModal = () => {
    if (rewardTransferSavingFormId || isRewardTransferOwnershipLoading) return;
    setIsRewardTransferOwnershipModalOpen(false);
    setRewardTransferOwnershipError('');
    setRewardTransferEditingFormId('');
    setRewardTransferSelectedFunction('');
  };

  const handleCancelRewardTransfer = () => {
    if (rewardTransferSavingFormId) return;
    setRewardTransferEditingFormId('');
    setRewardTransferSelectedFunction('');
    setRewardTransferOwnershipError('');
  };

  const handleConfirmRewardTransfer = async (row: RewardTransferOwnershipCandidate) => {
    const formId = String(row.formId || '').trim();
    const newFunction = String(rewardTransferSelectedFunction || '').trim();
    const currentFunction = String(row.currentFunction || '').trim();
    if (!formId || !newFunction) {
      setRewardTransferOwnershipError('Select a new function before transferring ownership.');
      return;
    }
    if (normalizeRewardFunction(newFunction) === normalizeRewardFunction(currentFunction)) {
      setRewardTransferOwnershipError('Select a different function before transferring ownership.');
      return;
    }

    setRewardTransferOwnershipError('');
    setRewardTransferSavingFormId(formId);
    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');
      await transferRewardOwnership(token, {
        formId,
        newFunction,
      });
      setRewardTransferOwnershipRows((current) =>
        current.filter((candidate) => String(candidate.formId || '').trim() !== formId),
      );
      setRewardTransferEditingFormId('');
      setRewardTransferSelectedFunction('');
      await refetch();
    } catch (error) {
      console.error(`Reward transfer ownership failed for form [${formId}]`, error);
      setRewardTransferOwnershipError('Unable to transfer ownership right now.');
    } finally {
      setRewardTransferSavingFormId('');
    }
  };

  const applyRewardCosVoucherOverrideToCachedFormData = (
    targetForm: FormListItem,
    override: RewardCosVoucherStatusOverride,
  ) => {
    const targetRowKey = getRewardCosVoucherRowKey(targetForm);
    queryClient.setQueriesData({ queryKey: [Query.FormData] }, (previous) => {
      if (!previous || typeof previous !== 'object') return previous;
      const previousRecord = previous as Record<string, unknown>;
      const previousForms = Array.isArray(previousRecord.forms)
        ? (previousRecord.forms as FormListItem[])
        : [];
      if (previousForms.length === 0) return previous;
      return {
        ...previousRecord,
        forms: previousForms.map((candidate) =>
          getRewardCosVoucherRowKey(candidate) === targetRowKey
            ? { ...candidate, ...override }
            : candidate,
        ),
      };
    });
  };

  const saveRewardVoucherIssued = async () => {
    const form = rewardVoucherIssueForm;
    const formId = String(form?.formId || '').trim();
    const nomineeId = String(form?.rewardCosNomineeId || '').trim();
    const isInYearBonus = isRewardCosInYearBonusForm(form);
    if (!form || !formId || !nomineeId) {
      setRewardVoucherIssueError(
        isInYearBonus
          ? 'Unable to save this in year bonus right now.'
          : 'Unable to save this voucher right now.',
      );
      return;
    }
    const rowKey = getRewardCosVoucherRowKey(form);
    const manualIssueChanged =
      rewardVoucherIssueChecked !== isRewardCosVoucherManualIssueChecked(form);
    if (!manualIssueChanged) return;

    if (!rewardVoucherIssueChecked && String(form?.formState || '').toLowerCase() !== 'submitted') {
      setRewardVoucherIssueError(
        isInYearBonus
          ? 'Only submitted in year bonuses can be unconfirmed.'
          : 'Only submitted vouchers can be unissued.',
      );
      return;
    }

    const currentOverrideExists = Object.hasOwn(rewardCosVoucherOverrides, rowKey);
    const previousOverride = currentOverrideExists ? rewardCosVoucherOverrides[rowKey] : undefined;
    const previousFormQueryData = queryClient.getQueriesData({
      queryKey: [Query.FormData],
    });
    const statusDate = new Date().toISOString();
    const nextOverride: RewardCosVoucherStatusOverride = {
      rewardCosVoucherIssued: rewardVoucherIssueChecked,
      rewardCosVoucherIssuedAt: rewardVoucherIssueChecked ? statusDate : undefined,
      rewardCosVoucherStatusDate: statusDate,
    };

    setRewardVoucherIssueError('');
    setCreateFormError('');
    setRewardCosVoucherOverrides((current) => ({
      ...current,
      [rowKey]: nextOverride,
    }));
    applyRewardCosVoucherOverrideToCachedFormData(form, nextOverride);
    setRewardVoucherIssueForm(null);
    setRewardVoucherIssueChecked(false);
    setRewardCosAuditDialog(null);
    setRewardCosAuditError('');
    setSuppressOverviewLoading(true);
    setIsRewardVoucherIssueSaving(true);
    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');
      await recordRewardVoucherIssued(token, {
        formId,
        nomineeId,
        awardType: form?.rewardCosAwardType,
        voucherIssued: rewardVoucherIssueChecked,
      });
      setRewardVoucherIssueError('');
    } catch (error) {
      console.error(`Voucher issue save failed for form [${formId}] nominee [${nomineeId}]`, error);
      previousFormQueryData.forEach(([queryKey, queryData]) => {
        queryClient.setQueryData(queryKey, queryData);
      });
      setRewardCosVoucherOverrides((current) => {
        const next = { ...current };
        if (currentOverrideExists) {
          next[rowKey] = previousOverride ?? {};
        } else {
          delete next[rowKey];
        }
        return next;
      });
      setCreateFormError(
        buildAdminDownloadErrorMessage(
          isInYearBonus
            ? 'Unable to save this in year bonus right now.'
            : 'Unable to save this voucher right now.',
          error,
        ),
      );
    } finally {
      setIsRewardVoucherIssueSaving(false);
    }
  };

  const saveRewardCosBulkUploadOutcome = async (
    form: FormListItem,
    voucherBulkUploadStatus: 'issued' | 'error',
  ) => {
    const formId = String(form?.formId || '').trim();
    const nomineeId = String(form?.rewardCosNomineeId || '').trim();
    const rowKey = getRewardCosVoucherRowKey(form);
    if (!formId || !nomineeId) {
      setCreateFormError('Unable to save this voucher right now.');
      return;
    }

    const currentOverrideExists = Object.hasOwn(rewardCosVoucherOverrides, rowKey);
    const previousOverride = currentOverrideExists ? rewardCosVoucherOverrides[rowKey] : undefined;
    const previousFormQueryData = queryClient.getQueriesData({
      queryKey: [Query.FormData],
    });
    const statusDate =
      form.rewardCosVoucherStatusDate ||
      (voucherBulkUploadStatus === 'issued'
        ? form.rewardCosVoucherBulkUploadIssuedAt
        : form.rewardCosVoucherBulkUploadErrorAt) ||
      new Date().toISOString();
    const nextOverride: RewardCosVoucherStatusOverride = {
      rewardCosVoucherBulkUploaded: true,
      rewardCosVoucherBulkUploadStatus: voucherBulkUploadStatus,
      rewardCosVoucherStatusDate: statusDate,
    };
    if (voucherBulkUploadStatus === 'issued') {
      nextOverride.rewardCosVoucherBulkUploadIssuedAt = statusDate;
    } else {
      nextOverride.rewardCosVoucherBulkUploadErrorAt = statusDate;
    }

    setCreateFormError('');
    setRewardCosVoucherOverrides((current) => ({
      ...current,
      [rowKey]: nextOverride,
    }));
    applyRewardCosVoucherOverrideToCachedFormData(form, nextOverride);
    setSuppressOverviewLoading(true);
    setIsRewardVoucherIssueSaving(true);
    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');
      await recordRewardVoucherIssued(token, {
        formId,
        nomineeId,
        awardType: form?.rewardCosAwardType,
        voucherBulkUploadStatus,
      });
    } catch (error) {
      console.error(
        `Voucher bulk upload status save failed for form [${formId}] nominee [${nomineeId}]`,
        error,
      );
      previousFormQueryData.forEach(([queryKey, queryData]) => {
        queryClient.setQueryData(queryKey, queryData);
      });
      setRewardCosVoucherOverrides((current) => {
        const next = { ...current };
        if (currentOverrideExists) {
          next[rowKey] = previousOverride ?? {};
        } else {
          delete next[rowKey];
        }
        return next;
      });
      setCreateFormError(
        buildAdminDownloadErrorMessage('Unable to save this voucher right now.', error),
      );
    } finally {
      setIsRewardVoucherIssueSaving(false);
    }
  };

  const closeVoucherBulkUploadModal = () => {
    if (isVoucherBulkUploadDownloading || isVoucherBulkUploadSaving) return;
    setIsVoucherBulkUploadModalOpen(false);
    setVoucherBulkUploadChecked(false);
    setVoucherBulkUploadError('');
  };

  const handleOpenVoucherBulkUploadModal = () => {
    setVoucherBulkUploadChecked(false);
    setVoucherBulkUploadError('');
    setIsVoucherBulkUploadModalOpen(true);
  };

  const downloadRewardVoucherBulkUpload = async () => {
    if (voucherBulkUploadEntries.length === 0) {
      setVoucherBulkUploadError('No issue voucher rows are available for bulk upload.');
      return;
    }

    setVoucherBulkUploadError('');
    setIsVoucherBulkUploadDownloading(true);
    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');

      const result = await buildRewardVoucherBulkUpload(token, voucherBulkUploadEntries);
      if (!result.url) {
        throw new Error('No download link returned');
      }
      window.location.assign(result.url);
    } catch (error) {
      console.error('Voucher bulk upload download failed', error);
      setVoucherBulkUploadError(
        buildAdminDownloadErrorMessage('Unable to download the voucher bulk upload form.', error),
      );
    } finally {
      setIsVoucherBulkUploadDownloading(false);
    }
  };

  const saveRewardVoucherBulkUpload = async () => {
    if (!voucherBulkUploadChecked) {
      setVoucherBulkUploadError('Tick the checkbox to mark these responses as complete.');
      return;
    }
    if (voucherBulkUploadEntries.length === 0) {
      setVoucherBulkUploadError('No issue voucher rows are available for bulk upload.');
      return;
    }

    setVoucherBulkUploadError('');
    setIsVoucherBulkUploadSaving(true);
    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');

      await recordRewardVoucherBulkUpload(token, voucherBulkUploadEntries);
      await queryClient.invalidateQueries({ queryKey: [Query.FormData] });
      await refetch();
      setIsVoucherBulkUploadModalOpen(false);
      setVoucherBulkUploadChecked(false);
      setVoucherBulkUploadError('');
    } catch (error) {
      console.error('Voucher bulk upload save failed', error);
      setVoucherBulkUploadError(
        buildAdminDownloadErrorMessage('Unable to mark these responses as complete.', error),
      );
    } finally {
      setIsVoucherBulkUploadSaving(false);
    }
  };

  const downloadRewardCosReconciliationReport = async () => {
    setAdminDownloadError('');
    setIsRewardCosReconciliationDownloading(true);
    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');

      const result = await buildRewardCosReconciliationReport(token);
      if (!result.url) {
        throw new Error('No download link returned');
      }
      window.location.assign(result.url);
    } catch (error) {
      console.error('Reward COS reconciliation report download failed', error);
      setAdminDownloadError(
        buildAdminDownloadErrorMessage(
          'Unable to download the reconciliation report right now.',
          error,
        ),
      );
    } finally {
      setIsRewardCosReconciliationDownloading(false);
    }
  };

  const handleOpenAdminDownloadModal = () => {
    setAdminDownloadError('');
    setReportPeriodFilterError('');
    if (tenant === 'scs') {
      setAdminDownloadReportYear(selectedReportYear);
      setAdminDownloadReportQuarter(selectedReportQuarter);
      setAdminDownloadScsStatus(selectedScsStatus);
    }
    setIsAdminDownloadModalOpen(true);
  };

  const handleCloseAdminDownloadModal = () => {
    if (activeAdminDownload.length > 0) return;
    setIsAdminDownloadModalOpen(false);
  };

  const handleOpenUnapproveModal = (form: FormListItem) => {
    setUnapproveForm(form);
    setUnapproveReason('');
    setUnapproveError('');
  };

  const handleCloseUnapproveModal = () => {
    if (isUnapproving) return;
    setUnapproveForm(null);
    setUnapproveReason('');
    setUnapproveError('');
  };

  const handleUnapproveForm = async () => {
    const formId = String(unapproveForm?.formId || '').trim();
    const reason = unapproveReason.trim();
    if (!formId) {
      setUnapproveError('Unable to reject this form right now.');
      return;
    }
    if (!reason) {
      setUnapproveError('Enter a reason for rejection');
      return;
    }

    setUnapproveError('');
    setIsUnapproving(true);
    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');
      await approveForm(token, { action: 'unapprove', comments: reason }, { form_id: formId });
      setUnapproveForm(null);
      setUnapproveReason('');
      await queryClient.invalidateQueries({ queryKey: [Query.FormData] });
      await refetch();
    } catch (error) {
      console.error(`Unapprove failed for form [${formId}]`, error);
      setUnapproveError(
        buildAdminDownloadErrorMessage('Unable to reject this form right now.', error),
      );
    } finally {
      setIsUnapproving(false);
    }
  };

  const handleMyFormsDownload = async () => {
    const formId = String(myFormsDownloadForm?.formId || '').trim();
    const selectedFormat = selectedMyFormsDownloadFormat as 'excel' | 'html' | 'pdf';

    if (!formId) {
      setMyFormsDownloadError('Unable to download this form right now.');
      return;
    }
    if (!selectedFormat) {
      setMyFormsDownloadError('Select a file type');
      return;
    }
    if (!myFormsDownloadFormats.includes(selectedFormat)) {
      setMyFormsDownloadError('Selected file type is not available for this form');
      return;
    }

    setMyFormsDownloadError('');
    setIsMyFormsDownloadSubmitting(true);

    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');

      const filenameBase = toExportFilenameBase(formConfig?.title || '', 'answers');

      if (selectedFormat === 'excel') {
        const { blob, filename } = await downloadExportExcel(token, { form_id: formId });
        triggerFileDownload(blob, filename || `${filenameBase}.xlsx`);
        resetMyFormsDownloadState();
        return;
      }

      const formResponse = await fetchForm(token, { form_id: formId });
      const answers =
        formResponse?.answers && typeof formResponse.answers === 'object'
          ? (formResponse.answers as Record<string, unknown>)
          : {};
      const drafts =
        formResponse?.drafts && typeof formResponse.drafts === 'object'
          ? (formResponse.drafts as Record<string, string[]>)
          : {};
      const enabledSubsectionsForExport = getEnabledSubsections(formConfig, formResponse);
      const submittedAt =
        typeof formResponse?.submittedAt === 'string' && formResponse.submittedAt.trim()
          ? formResponse.submittedAt
          : typeof (formResponse as any)?.submitted_at === 'string' &&
              String((formResponse as any).submitted_at).trim()
            ? String((formResponse as any).submitted_at).trim()
            : String(myFormsDownloadForm?.submittedAt || '').trim() || undefined;
      const html = buildExportHtml(
        tenant,
        formConfig?.title,
        submittedAt,
        formConfig?.sections || [],
        answers,
        drafts,
        enabledSubsectionsForExport,
        formId,
        formResponse?.rewardPanelDecision,
        formResponse?.rewardPanelNotes,
        formResponse,
      );
      const formVersion = formResponse?.formVersion || (formResponse as any)?.form_version;

      if (selectedFormat === 'html') {
        const { blob, filename } = await downloadExportRenderedHtml(token, {
          html,
          filename: filenameBase,
          formVersion,
        });
        triggerFileDownload(blob, filename || `${filenameBase}.html`);
        resetMyFormsDownloadState();
        return;
      }

      const { blob, filename } = await convertExportHtmlToPdf(token, {
        html,
        filename: filenameBase,
        formVersion,
      });
      triggerFileDownload(blob, filename || `${filenameBase}.pdf`);
      resetMyFormsDownloadState();
    } catch (error) {
      console.error(`My Forms download failed for form [${formId}]`, error);
      setMyFormsDownloadError(
        buildAdminDownloadErrorMessage('Unable to download this form right now.', error),
      );
    } finally {
      setIsMyFormsDownloadSubmitting(false);
    }
  };

  const handleMyFormsDelete = async () => {
    const formId = String(myFormsDeleteForm?.formId || '').trim();
    if (!formId) {
      setMyFormsDeleteError('Unable to delete this form right now.');
      return;
    }

    setMyFormsDeleteError('');
    setIsMyFormsDeleting(true);
    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');
      await deleteForm(token, { form_id: formId });
      if (selectedFormId === formId) {
        setSelectedFormIdInSession(tenant || '', null);
      }
      resetMyFormsDeleteState();
      await queryClient.invalidateQueries({ queryKey: [Query.FormData] });
      await refetch();
    } catch (error) {
      console.error(`My Forms delete failed for form [${formId}]`, error);
      setMyFormsDeleteError(
        buildAdminDownloadErrorMessage('Unable to delete this form right now.', error),
      );
    } finally {
      setIsMyFormsDeleting(false);
    }
  };

  const resolveAdminBulkDownloadContext = async (token: string) => {
    const progressRows: Array<Record<string, unknown>> =
      tenant === 'scs'
        ? adminDownloadFormsInScope.map((form) => ({
            version: form.formVersion || form.form_version,
          }))
        : await fetchAdminProgressStatsRows(token, {
            reportFilters: adminDownloadReportFilters,
            departmentFilters: selectedDepartmentFilters,
          });
    const formVersionLabel = resolveDownloadFormVersionLabelFromRows(progressRows, 'version');
    const downloadableStates = getAdminDownloadableStates(
      includeAdminDownloadAwaitingApproval,
      includeAdminDownloadInProgress,
    );
    const formIdsInScope = new Set(
      adminDownloadFormsInScope.map((form) => String(form.formId || '').trim()).filter(Boolean),
    );
    const shouldApplyClientDownloadScope =
      tenant === 'scs' || (tenant === 'psfa' && Boolean(selectedOrganisation));
    const eligibleFormIds =
      tenant === 'scs'
        ? Array.from(formIdsInScope)
        : Array.from(
            new Set(
              progressRows
                .filter((row) => {
                  const formId = String(row?.form_id || '').trim();
                  if (!formId) return false;
                  if (shouldApplyClientDownloadScope && !formIdsInScope.has(formId)) return false;
                  const state = String(row?.form_status || '')
                    .trim()
                    .toLowerCase();
                  return downloadableStates.has(state);
                })
                .map((row) => String(row?.form_id || '').trim())
                .filter((formId) => formId.length > 0),
            ),
          );
    if (eligibleFormIds.length === 0) {
      throw new Error('No forms found for the selected filters.');
    }

    const titleForFilename =
      typeof formConfig?.title === 'string' && formConfig.title.trim()
        ? formConfig.title.trim()
        : 'answers';
    const filteredSuffix = adminDownloadReportFilters.length > 0 ? '-filtered' : '';
    const knownFormsById = new Map(
      sortedForms.map((form) => [String(form.formId || '').trim(), form] as const),
    );

    return {
      eligibleFormIds,
      filteredSuffix,
      formVersionLabel,
      knownFormsById,
      titleForFilename,
    };
  };

  const handleAdminBulkPdfDownload = async (token: string) => {
    const { eligibleFormIds, filteredSuffix, formVersionLabel, knownFormsById, titleForFilename } =
      await resolveAdminBulkDownloadContext(token);

    setAdminHtmlDownloadProgress(0);
    const zip = new JSZip();
    let processedFormCount = 0;

    const generatedPdfs = await mapAdminBulkFilesWithConcurrency(
      eligibleFormIds,
      async (formId) => {
        try {
          const formResponse = await fetchForm(token, { form_id: formId });
          const answers =
            formResponse?.answers && typeof formResponse.answers === 'object'
              ? (formResponse.answers as Record<string, unknown>)
              : {};
          const drafts =
            formResponse?.drafts && typeof formResponse.drafts === 'object'
              ? (formResponse.drafts as Record<string, string[]>)
              : {};
          const submittedAt =
            typeof formResponse?.submittedAt === 'string' && formResponse.submittedAt.trim()
              ? formResponse.submittedAt
              : knownFormsById.get(formId)?.submittedAt;
          const enabledSubsectionsForExport = getEnabledSubsections(formConfig, formResponse);

          const html = buildExportHtml(
            tenant,
            formConfig?.title,
            submittedAt,
            formConfig?.sections || [],
            answers,
            drafts,
            enabledSubsectionsForExport,
            formId,
            formResponse?.rewardPanelDecision,
            formResponse?.rewardPanelNotes,
            formResponse,
          );

          const fileBaseName = toExportFilenameBase(`${titleForFilename}-${formId}`, 'answers');
          const formVersion =
            formResponse?.formVersion ||
            (formResponse as any)?.form_version ||
            knownFormsById.get(formId)?.formVersion ||
            (knownFormsById.get(formId) as any)?.form_version;
          const { blob } = await convertExportHtmlToPdf(token, {
            html,
            filename: fileBaseName,
            formVersion,
          });
          return { blob, formId };
        } catch (error) {
          console.error(`Admin PDF export failed for form [${formId}]`, error);
          return null;
        } finally {
          processedFormCount += 1;
          setAdminHtmlDownloadProgress(
            Math.round((processedFormCount / eligibleFormIds.length) * 100),
          );
        }
      },
    );

    const successfulPdfs = generatedPdfs.filter(
      (result): result is { blob: Blob; formId: string } => result !== null,
    );
    const filenameFallbackErrors: string[] = [];
    successfulPdfs.forEach(({ blob, formId }, index) => {
      zip.file(
        buildAdminBulkZipEntryFilename({
          formId,
          index,
          extension: 'pdf',
          tenant,
          form: knownFormsById.get(formId),
          formCloseAt,
          strictDeadline,
          onFallback: (message) => filenameFallbackErrors.push(message),
        }),
        blob,
      );
    });

    if (successfulPdfs.length === 0) {
      throw new Error('Unable to export submitted or approved forms as PDF right now.');
    }

    const archiveName = buildDownloadFilename({
      tenant: tenant || 'tenant',
      formVersion: formVersionLabel,
      filename: `submitted-approved-pdf${filteredSuffix}`,
      extension: 'zip',
    });
    const archiveBlob = await zip.generateAsync({ type: 'blob' });
    setAdminHtmlDownloadProgress(100);
    triggerFileDownload(archiveBlob, archiveName);
    if (filenameFallbackErrors.length > 0) {
      setAdminDownloadError(
        `Download completed, but some filenames could not be generated. Form IDs were used instead. Cause: ${filenameFallbackErrors.join('; ')}`,
      );
    }
  };

  const handleAdminBulkHtmlDownload = async (token: string) => {
    const { eligibleFormIds, filteredSuffix, formVersionLabel, knownFormsById } =
      await resolveAdminBulkDownloadContext(token);

    const zip = new JSZip();

    const generatedHtmlFiles = await mapAdminBulkFilesWithConcurrency(
      eligibleFormIds,
      async (formId) => {
        try {
          const formResponse = await fetchForm(token, { form_id: formId });
          const answers =
            formResponse?.answers && typeof formResponse.answers === 'object'
              ? (formResponse.answers as Record<string, unknown>)
              : {};
          const drafts =
            formResponse?.drafts && typeof formResponse.drafts === 'object'
              ? (formResponse.drafts as Record<string, string[]>)
              : {};
          const submittedAt =
            typeof formResponse?.submittedAt === 'string' && formResponse.submittedAt.trim()
              ? formResponse.submittedAt
              : knownFormsById.get(formId)?.submittedAt;
          const enabledSubsectionsForExport = getEnabledSubsections(formConfig, formResponse);

          const html = buildExportHtml(
            tenant,
            formConfig?.title,
            submittedAt,
            formConfig?.sections || [],
            answers,
            drafts,
            enabledSubsectionsForExport,
            formId,
            formResponse?.rewardPanelDecision,
            formResponse?.rewardPanelNotes,
            formResponse,
          );
          return { formId, html };
        } catch (error) {
          console.error(`Admin HTML export failed for form [${formId}]`, error);
          return null;
        }
      },
    );

    const successfulHtmlFiles = generatedHtmlFiles.filter(
      (result): result is { formId: string; html: string } => result !== null,
    );
    const filenameFallbackErrors: string[] = [];
    successfulHtmlFiles.forEach(({ formId, html }, index) => {
      zip.file(
        buildAdminBulkZipEntryFilename({
          formId,
          index,
          extension: 'html',
          tenant,
          form: knownFormsById.get(formId),
          formCloseAt,
          strictDeadline,
          onFallback: (message) => filenameFallbackErrors.push(message),
        }),
        html,
      );
    });

    if (successfulHtmlFiles.length === 0) {
      throw new Error('Unable to export submitted or approved forms as HTML right now.');
    }

    const archiveName = buildDownloadFilename({
      tenant: tenant || 'tenant',
      formVersion: formVersionLabel,
      filename: `submitted-approved-html${filteredSuffix}`,
      extension: 'zip',
    });
    const archiveBlob = await zip.generateAsync({ type: 'blob' });
    triggerFileDownload(archiveBlob, archiveName);
    if (filenameFallbackErrors.length > 0) {
      setAdminDownloadError(
        `Download completed, but some filenames could not be generated. Form IDs were used instead. Cause: ${filenameFallbackErrors.join('; ')}`,
      );
    }
  };

  const handleAdminBulkDownload = async (format: AdminBulkDownloadFormat) => {
    setAdminDownloadError('');
    if (tenant !== 'scs' && hasReportPeriodQuestion && adminDownloadReportFilters.length === 0) {
      setReportPeriodFilterError(
        useScsReportingYearFilter
          ? 'Please select a reporting year'
          : 'Please select a reporting period',
      );
      return;
    }
    if (adminDownloadCount === 0) return;
    setActiveAdminDownload(`bulk-${format}`);
    if (format === 'pdf') {
      setAdminHtmlDownloadProgress(0);
    }
    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');

      if (format === 'pdf') {
        await handleAdminBulkPdfDownload(token);
        setIsAdminDownloadModalOpen(false);
        return;
      }
      if (format === 'html') {
        await handleAdminBulkHtmlDownload(token);
        setIsAdminDownloadModalOpen(false);
        return;
      }

      const job = await startAdminBulkExcelExport(token, {
        reportFilters: adminDownloadReportFilters,
        departmentFilters: selectedDepartmentFilters,
        formIds: adminDownloadFormsInScope
          .map((form) => String(form.formId || '').trim())
          .filter((formId) => formId.length > 0),
        includeAwaitingApproval: includeAdminDownloadAwaitingApproval,
        includeInProgress: includeAdminDownloadInProgress,
        exportType: format === 'hybrid' ? 'hybrid' : 'relational',
      });
      if (!job.job_id) {
        throw new ApiError(content.errorApi, 'Export job did not return a job ID');
      }
      const completedJob = await waitForAdminBulkExcelExportCompletion(token, job.job_id);
      const { blob, filename } = await downloadAdminBulkExcelExport(token, job.job_id);
      const fallbackFilename = buildDownloadFilename({
        tenant: tenant || 'tenant',
        formVersion: 'unknown',
        filename:
          format === 'excel'
            ? 'submitted-approved-excel'
            : format === 'hybrid'
              ? 'submitted-approved-hybrid-excel'
              : format === 'html'
                ? 'submitted-approved-html'
                : 'submitted-approved-pdf',
        extension: format === 'excel' || format === 'hybrid' ? 'xlsx' : 'zip',
      });
      triggerFileDownload(blob, filename || completedJob.download_filename || fallbackFilename);
      setIsAdminDownloadModalOpen(false);
    } catch (error) {
      console.error('Admin bulk download failed', error);
      setAdminDownloadError(
        buildAdminDownloadErrorMessage('Unable to download responses right now.', error),
      );
    } finally {
      setActiveAdminDownload('');
      setAdminHtmlDownloadProgress(null);
    }
  };

  const handleAdminProgressDownload = async () => {
    setAdminDownloadError('');
    if (hasReportPeriodQuestion && selectedReportFilters.length === 0) {
      setReportPeriodFilterError('Please select a reporting period');
      return;
    }
    if (tenant === 'scs' && scsFilteredFormIds.length === 0) {
      setAdminDownloadError('No forms found for the selected filters.');
      return;
    }
    setActiveAdminDownload('progress');
    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');

      const { blob, filename } = await downloadAdminProgressStats(token, {
        reportFilters: selectedReportFilters,
        departmentFilters: selectedDepartmentFilters,
        requireIdentifier: true,
        ...(tenant === 'scs' ? { formIds: scsFilteredFormIds } : {}),
        ...(useScsReportingYearFilter && selectedReportQuarter
          ? { reportingQuarter: selectedReportQuarter }
          : {}),
      });
      triggerFileDownload(
        blob,
        filename ||
          buildDownloadFilename({
            tenant: tenant || 'tenant',
            formVersion: 'unknown',
            filename: 'progress-report',
            extension: 'xlsx',
          }),
      );
    } catch (error) {
      console.error('Admin progress stats download failed', error);
      setAdminDownloadError(
        buildAdminDownloadErrorMessage('Unable to download progress report right now.', error),
      );
    } finally {
      setActiveAdminDownload('');
    }
  };

  useEffect(() => {
    if (!isAdminView || !hasAdminAccess) {
      setHasFeedbackEntries(false);
      setHasLoadedFeedbackAvailability(false);
      setHasDataErrorEntries(false);
      setHasLoadedDataErrorAvailability(false);
      return;
    }

    let cancelled = false;

    const loadAdminRecordAvailability = async () => {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');

      const [feedbackResult, dataErrorResult] = await Promise.allSettled([
        fetchFeedback(token),
        shouldLoadDataErrorAvailability
          ? fetchDataErrors(token)
          : Promise.resolve<DataErrorEntry[]>([]),
      ]);

      if (cancelled) return;

      if (feedbackResult.status === 'fulfilled') {
        setFeedbackEntries(feedbackResult.value);
        setHasFeedbackEntries(feedbackResult.value.length > 0);
      } else {
        console.error('Failed to check feedback availability', feedbackResult.reason);
        setHasFeedbackEntries(false);
      }
      setHasLoadedFeedbackAvailability(true);

      if (!shouldLoadDataErrorAvailability) {
        setDataErrorEntries([]);
        setHasDataErrorEntries(false);
      } else if (dataErrorResult.status === 'fulfilled') {
        setDataErrorEntries(dataErrorResult.value);
        setHasDataErrorEntries(hasActiveDataErrorEntries(dataErrorResult.value));
      } else {
        console.error('Failed to check data error availability', dataErrorResult.reason);
        setHasDataErrorEntries(false);
      }
      setHasLoadedDataErrorAvailability(shouldLoadDataErrorAvailability);
    };

    void loadAdminRecordAvailability().catch((error) => {
      console.error('Failed to check admin record availability', error);
      if (cancelled) return;
      setHasFeedbackEntries(false);
      setHasLoadedFeedbackAvailability(true);
      setHasDataErrorEntries(false);
      setHasLoadedDataErrorAvailability(shouldLoadDataErrorAvailability);
    });

    return () => {
      cancelled = true;
    };
  }, [fetchJWT, hasAdminAccess, isAdminView, shouldLoadDataErrorAvailability]);

  useEffect(() => {
    setDataErrorPageIndex((current) => {
      const lastPageIndex = Math.max(
        Math.ceil(dataErrorEntries.length / DATA_ERROR_PAGE_SIZE) - 1,
        0,
      );
      return current > lastPageIndex ? lastPageIndex : current;
    });
  }, [dataErrorEntries.length]);

  useEffect(() => {
    if (!myFormsDownloadForm || isMyFormsDownloadSubmitting) return;
    myFormsDownloadSelectRef.current?.focus();
  }, [isMyFormsDownloadSubmitting, myFormsDownloadForm]);

  const handleOpenFeedbackModal = async () => {
    setFeedbackAdminError('');
    setIsFeedbackModalOpen(true);
    setIsFeedbackLoading(true);
    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');
      const items = await fetchFeedback(token);
      setFeedbackEntries(items);
      setHasFeedbackEntries(items.length > 0);
    } catch (error) {
      console.error('Failed to load feedback', error);
      setFeedbackAdminError('Unable to load feedback right now.');
    } finally {
      setIsFeedbackLoading(false);
    }
  };

  const handleCloseFeedbackModal = () => {
    if (deletingFeedbackAt) return;
    setIsFeedbackModalOpen(false);
    setFeedbackAdminError('');
  };

  const handleDeleteFeedbackEntry = async (submittedAt: string) => {
    if (!submittedAt) return;
    setFeedbackAdminError('');
    setDeletingFeedbackAt(submittedAt);
    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');
      await deleteFeedback(token, submittedAt);
      setFeedbackEntries((current) => {
        const next = current.filter(
          (entry) => String(entry.submittedAt || '').trim() !== submittedAt,
        );
        setHasFeedbackEntries(next.length > 0);
        if (next.length === 0) {
          setIsFeedbackModalOpen(false);
        }
        return next;
      });
    } catch (error) {
      console.error('Failed to delete feedback', error);
      setFeedbackAdminError('Unable to delete feedback right now.');
    } finally {
      setDeletingFeedbackAt('');
    }
  };

  const handleOpenDataErrorModal = async () => {
    if (isRewardTenant && !canManageRewardQaDataErrors) return;
    setDataErrorAdminError('');
    setRewardQaDataErrorNotes({});
    setRewardQaDataErrorDecisions({});
    setShowHistoricDataErrors(false);
    setDataErrorPageIndex(0);
    setIsDataErrorModalOpen(true);
    setIsDataErrorLoading(true);
    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');
      const items = await fetchDataErrors(token);
      setDataErrorEntries(items);
      setHasDataErrorEntries(hasActiveDataErrorEntries(items));
    } catch (error) {
      console.error('Failed to load data errors', error);
      setDataErrorAdminError('Unable to load data errors right now.');
    } finally {
      setIsDataErrorLoading(false);
    }
  };

  const handleToggleHistoricDataErrors = async (checked: boolean) => {
    setShowHistoricDataErrors(checked);
    setDataErrorPageIndex(0);
    setDataErrorAdminError('');
    setIsDataErrorLoading(true);
    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');
      const items = await fetchDataErrors(token, checked ? { includeDeleted: true } : undefined);
      setDataErrorEntries(items);
      setHasDataErrorEntries(hasActiveDataErrorEntries(items, rewardQaDataErrorDecisions));
    } catch (error) {
      console.error('Failed to load data errors', error);
      setDataErrorAdminError('Unable to load data errors right now.');
    } finally {
      setIsDataErrorLoading(false);
    }
  };

  const handleCloseDataErrorModal = () => {
    if (deletingDataErrorAt || recordingRewardQaDataErrorAt) return;
    setIsDataErrorModalOpen(false);
    setDataErrorAdminError('');
    setShowHistoricDataErrors(false);
    setDataErrorPageIndex(0);
  };

  const handleOpenFunctionMembersModal = async () => {
    setFunctionMemberFeedback(null);
    setIsFunctionMembersModalOpen(true);
    await functionMembersQuery.refetch();
  };

  const handleCloseFunctionMembersModal = () => {
    if (addFunctionMemberMutation.isPending || removeFunctionMemberMutation.isPending) return;
    setIsFunctionMembersModalOpen(false);
    setFunctionMemberEmail('');
    setFunctionMemberFeedback(null);
  };

  const handleAddFunctionMember = async () => {
    setFunctionMemberFeedback(null);
    await addFunctionMemberMutation.mutateAsync();
  };

  const handleDeleteDataErrorEntry = async (submittedAt: string) => {
    if (!submittedAt) return;
    setDataErrorAdminError('');
    setDeletingDataErrorAt(submittedAt);
    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');
      await deleteDataError(token, submittedAt);
      setDataErrorEntries((current) => {
        const next = current.filter(
          (entry) => String(entry.submittedAt || '').trim() !== submittedAt,
        );
        setHasDataErrorEntries(next.length > 0);
        if (next.length === 0) {
          setIsDataErrorModalOpen(false);
        }
        return next;
      });
    } catch (error) {
      console.error('Failed to delete data error', error);
      setDataErrorAdminError('Unable to delete data error right now.');
    } finally {
      setDeletingDataErrorAt('');
    }
  };

  const handleRecordRewardQaDataErrorDecision = async (
    entry: DataErrorEntry,
    outcome: 'accepted' | 'rejected',
  ) => {
    const submittedAt = String(entry.submittedAt || '').trim();
    if (!submittedAt) return;
    const formId = String(entry.formId || '').trim();
    setDataErrorAdminError('');
    setRecordingRewardQaDataErrorAt(submittedAt);
    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');
      await recordRewardQaDataErrorAudit(token, {
        submittedAt,
        record: entry.record,
        selected: entry.selected,
        notes: rewardQaDataErrorNotes[submittedAt] || '',
        outcome,
        ...(formId ? { formId } : {}),
      });
      await deleteDataError(token, submittedAt);
      const nextDecisions = {
        ...rewardQaDataErrorDecisions,
        [submittedAt]: outcome,
      };
      setRewardQaDataErrorDecisions(nextDecisions);
      setHasDataErrorEntries(hasActiveDataErrorEntries(dataErrorEntries, nextDecisions));
    } catch (error) {
      console.error('Failed to record QA data error decision', error);
      setDataErrorAdminError('Unable to record QA data error decision right now.');
    } finally {
      setRecordingRewardQaDataErrorAt('');
    }
  };

  const isAdminBulkDownloadPreparing = activeAdminDownload.startsWith('bulk-');
  const isAdminPdfHighlighted =
    activeAdminDownload === 'bulk-pdf' &&
    adminHtmlDownloadProgress !== null &&
    adminHtmlDownloadProgress > 0 &&
    adminHtmlDownloadProgress < 100;
  const showAdminPdfPercentage = activeAdminDownload === 'bulk-pdf';
  const adminPdfSpinnerProgress =
    adminHtmlDownloadProgress === null
      ? 0
      : adminHtmlDownloadProgress > adminPdfDisplayProgress
        ? adminHtmlDownloadProgress
        : adminPdfDisplayProgress;
  const dataErrorTotalPages = Math.max(
    1,
    Math.ceil(dataErrorEntries.length / DATA_ERROR_PAGE_SIZE),
  );
  const visibleDataErrorEntries = dataErrorEntries.slice(
    dataErrorPageIndex * DATA_ERROR_PAGE_SIZE,
    dataErrorPageIndex * DATA_ERROR_PAGE_SIZE + DATA_ERROR_PAGE_SIZE,
  );
  const rewardVoucherFields = rewardVoucherIssueForm
    ? [
        {
          label: 'Nominee Name',
          copyLabel: 'nominee name',
          value: rewardVoucherIssueForm.rewardCosNomineeName || EMPTY_VALUE,
        },
        {
          label: 'Nominee Grade',
          copyLabel: 'nominee grade',
          value: rewardVoucherIssueForm.rewardCosNomineeGrade || EMPTY_VALUE,
        },
        {
          label: 'Nominee SOP Staff Number',
          copyLabel: 'nominee SOP staff number',
          value: rewardVoucherIssueForm.rewardCosNomineeStaffNumber || EMPTY_VALUE,
        },
        {
          label: 'Nominee Email address',
          copyLabel: 'nominee email address',
          value: rewardVoucherIssueForm.rewardCosNomineeEmail || EMPTY_VALUE,
        },
        {
          label: 'Award Type',
          copyLabel: 'award type',
          value: rewardVoucherIssueForm.rewardCosAwardType || EMPTY_VALUE,
        },
        {
          label: 'Award Amount',
          copyLabel: 'award amount',
          value:
            rewardVoucherIssueForm.rewardCosAwardAmount === undefined ||
            rewardVoucherIssueForm.rewardCosAwardAmount === null
              ? EMPTY_VALUE
              : String(rewardVoucherIssueForm.rewardCosAwardAmount),
        },
      ]
    : [];
  const rewardCosModalIsInYearBonus = isRewardCosInYearBonusForm(rewardVoucherIssueForm);
  const rewardCosModalTitle = rewardCosModalIsInYearBonus
    ? 'Confirm in year bonus'
    : 'Issue voucher';
  const rewardCosModalCheckboxLabel = rewardCosModalIsInYearBonus
    ? 'This in year bonus is confirmed as being on the ledger'
    : 'This voucher has been manually issued';
  const rewardCosModalManualInitialChecked =
    isRewardCosVoucherManualIssueChecked(rewardVoucherIssueForm);
  const rewardCosModalHasChanges = rewardVoucherIssueChecked !== rewardCosModalManualInitialChecked;
  const rewardVoucherIssueHasError = rewardVoucherIssueError && !rewardVoucherIssueChecked;
  const showRewardCosAuditButton = Boolean(
    String(rewardVoucherIssueForm?.formId || '').trim() &&
      String(rewardVoucherIssueForm?.rewardCosNomineeId || '').trim(),
  );
  const primaryDateHeaderLabel = isRewardCosAdminView
    ? 'Date Approved'
    : isRewardAdminView
      ? 'Date Submitted'
      : 'Date Started';
  const renderSortableTableHeader = (column: OverviewTableSortColumn, label: string) => {
    const indicator = getOverviewTableSortIndicator(overviewTableSort, column);
    return (
      <SortHeaderButton
        type="button"
        onClick={() => setOverviewTableSort((current) => getNextOverviewTableSort(current, column))}
      >
        <span>{label}</span>
        {indicator && <SortHeaderIndicator aria-hidden="true">{indicator}</SortHeaderIndicator>}
      </SortHeaderButton>
    );
  };

  return (
    <>
      {isAdminBulkDownloadPreparing && (
        <PdfLoadingOverlay
          role="status"
          aria-live="polite"
          aria-label="Preparing files..."
          data-testid="admin-download-loading-overlay"
        >
          <div>
            <PdfLoadingSpinner>
              <PdfLoadingSpinnerRing />
              {showAdminPdfPercentage && (
                <PdfLoadingSpinnerValue>{`${adminPdfSpinnerProgress}%`}</PdfLoadingSpinnerValue>
              )}
            </PdfLoadingSpinner>
            <PdfLoadingLabel className="govuk-body-l">Preparing files...</PdfLoadingLabel>
          </div>
        </PdfLoadingOverlay>
      )}
      {isSqlReportsModalOpen && (
        <AdminFeedbackBackdrop>
          {isSqlReportEditorOpen ? (
            <SqlReportEditorDialog
              className="govuk-!-padding-6"
              role="dialog"
              aria-modal="true"
              aria-labelledby="sql-report-editor-title"
            >
              <AdminFeedbackHeader>
                <div>
                  <h2
                    id="sql-report-editor-title"
                    className="govuk-heading-m govuk-!-margin-bottom-2"
                  >
                    Edit SQL Report
                  </h2>
                  {sqlReportDetail && (
                    <p className="govuk-body govuk-!-margin-bottom-0">
                      {sqlReportDetail.report_name}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  className="govuk-button govuk-button--secondary govuk-!-margin-bottom-0"
                  data-module="govuk-button"
                  onClick={handleCloseSqlReportsModal}
                  disabled={isSqlReportSaving}
                >
                  Close
                </button>
              </AdminFeedbackHeader>
              {sqlReportEditorError && (
                <Notification
                  header="Error"
                  content={sqlReportEditorError}
                  className="govuk-notification-banner--error"
                />
              )}
              {isSqlReportSaving && (
                <SqlReportCheckingStatus
                  className="govuk-body"
                  role="status"
                  aria-live="polite"
                >
                  <SqlReportCheckingSpinner aria-hidden="true" data-testid="sql-report-spinner" />
                  Checking SQL...
                </SqlReportCheckingStatus>
              )}
              {isSqlReportEditorLoading ? (
                <p className="govuk-body govuk-!-margin-bottom-0">Loading report...</p>
              ) : sqlReportDetail ? (
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    void handleSaveSqlReport();
                  }}
                >
                  <div className="govuk-inset-text govuk-!-margin-top-0">
                    <p className="govuk-body govuk-!-margin-bottom-3">Available placeholders:</p>
                    <SqlReportPlaceholderList>
                      {sqlReportDetail.placeholders.map((placeholder) => (
                        <Fragment key={placeholder.value}>
                          <dt>
                            <code>{placeholder.value}</code>
                          </dt>
                          <dd>
                            {placeholder.description}
                            {' Current value: '}
                            <code>
                              {placeholder.value === '{{environment}}'
                                ? sqlReportDetail.environment
                                : selectedSqlReportVersion}
                            </code>
                          </dd>
                        </Fragment>
                      ))}
                    </SqlReportPlaceholderList>
                  </div>
                  <div className="govuk-form-group">
                    <label className="govuk-label govuk-label--s" htmlFor="sql-report-query">
                      SQL query
                    </label>
                    <SqlReportQueryEditorWithHighlights
                      value={sqlReportDraft}
                      onChange={setSqlReportDraft}
                      disabled={isSqlReportSaving}
                    />
                  </div>
                  <SqlReportActions>
                    <button
                      type="submit"
                      className="govuk-button govuk-button--green"
                      data-module="govuk-button"
                      disabled={isSqlReportSaving}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="govuk-button govuk-button--secondary"
                      data-module="govuk-button"
                      onClick={handleCancelSqlReportEdit}
                      disabled={isSqlReportSaving}
                    >
                      Cancel
                    </button>
                  </SqlReportActions>
                </form>
              ) : null}
            </SqlReportEditorDialog>
          ) : (
            <SqlReportsDialog
              className="govuk-!-padding-6"
              role="dialog"
              aria-modal="true"
              aria-labelledby="sql-reports-title"
            >
              <AdminFeedbackHeader>
                <h2 id="sql-reports-title" className="govuk-heading-m govuk-!-margin-bottom-0">
                  SQL Reports
                </h2>
                <button
                  type="button"
                  className="govuk-button govuk-button--secondary govuk-!-margin-bottom-0"
                  data-module="govuk-button"
                  onClick={handleCloseSqlReportsModal}
                  disabled={isSqlReportRunning}
                >
                  Close
                </button>
              </AdminFeedbackHeader>
              {sqlReportError && (
                <Notification
                  header="Error"
                  content={sqlReportError}
                  className="govuk-notification-banner--error"
                />
              )}
              {isSqlReportsLoading ? (
                <p className="govuk-body govuk-!-margin-bottom-0">Loading reports...</p>
              ) : (
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    void handleRunSqlReport();
                  }}
                >
                  <div className="govuk-form-group">
                    <label className="govuk-label" htmlFor="sql-report-select">
                      Report
                    </label>
                    <select
                      className="govuk-select"
                      id="sql-report-select"
                      value={selectedSqlReportName}
                      onChange={(event) => handleSqlReportSelection(event.target.value)}
                      disabled={isSqlReportRunning}
                    >
                      <option value="">Please select a report</option>
                      {sqlReportDefinitions.map((report) => (
                        <option key={report.report_name} value={report.report_name}>
                          {report.report_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="govuk-form-group">
                    <label className="govuk-label" htmlFor="sql-report-version-select">
                      Form version
                    </label>
                    <select
                      className="govuk-select"
                      id="sql-report-version-select"
                      value={selectedSqlReportVersion}
                      onChange={(event) => setSelectedSqlReportVersion(event.target.value)}
                      disabled={!selectedSqlReportName || isSqlReportRunning}
                    >
                      <option value="">Please select a form version</option>
                      {selectedSqlReportVersions.map((version) => (
                        <option key={version} value={version}>
                          {version}
                        </option>
                      ))}
                    </select>
                  </div>
                  <SqlReportActions>
                    <button
                      type="submit"
                      className="govuk-button govuk-button--green"
                      data-module="govuk-button"
                      disabled={
                        isSqlReportRunning || !selectedSqlReportName || !selectedSqlReportVersion
                      }
                    >
                      {isSqlReportRunning ? 'Running...' : 'Run'}
                    </button>
                    <button
                      type="button"
                      className="govuk-button govuk-button--secondary"
                      data-module="govuk-button"
                      onClick={() => void handleEditSqlReport()}
                      disabled={isSqlReportRunning || !selectedSqlReportName}
                    >
                      Edit
                    </button>
                  </SqlReportActions>
                </form>
              )}
            </SqlReportsDialog>
          )}
        </AdminFeedbackBackdrop>
      )}
      {isFeedbackModalOpen && (
        <AdminFeedbackBackdrop>
          <AdminFeedbackDialog
            className="govuk-!-padding-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-feedback-title"
          >
            <AdminFeedbackHeader>
              <div>
                <h2 id="admin-feedback-title" className="govuk-heading-m govuk-!-margin-bottom-2">
                  Tenant feedback
                </h2>
                <p className="govuk-body govuk-!-margin-bottom-0">
                  {isFeedbackLoading
                    ? 'Loading feedback...'
                    : `${feedbackEntries.length} feedback entr${feedbackEntries.length === 1 ? 'y' : 'ies'}`}
                </p>
              </div>
              <button
                type="button"
                className="govuk-button govuk-button--secondary govuk-!-margin-bottom-0"
                data-module="govuk-button"
                onClick={handleCloseFeedbackModal}
                disabled={deletingFeedbackAt.length > 0}
              >
                Close
              </button>
            </AdminFeedbackHeader>
            {feedbackAdminError && (
              <Notification
                header="Error"
                content={feedbackAdminError}
                className="govuk-notification-banner--error"
              />
            )}
            {!isFeedbackLoading && feedbackEntries.length === 0 && !feedbackAdminError && (
              <p className="govuk-body govuk-!-margin-bottom-0">
                No feedback has been submitted for this tenant yet.
              </p>
            )}
            {!isFeedbackLoading && feedbackEntries.length > 0 && (
              <AdminFeedbackList>
                {feedbackEntries.map((entry) => {
                  const submittedAt = String(entry.submittedAt || '').trim();
                  const currentUrl = String(entry.currentUrl || '').trim();
                  const feedbackUser = String(entry.submitterEmail || entry.dcfId || '').trim();
                  const formattedSubmittedAt =
                    formatLondonDateTime(submittedAt) || submittedAt || EMPTY_VALUE;
                  const isDeleting = deletingFeedbackAt === submittedAt;

                  return (
                    <AdminFeedbackCard key={submittedAt || `${feedbackUser}-${currentUrl}`}>
                      <AdminFeedbackCardHeader>
                        <div>
                          <h3 className="govuk-heading-s govuk-!-margin-bottom-1">
                            {formattedSubmittedAt}
                          </h3>
                          <p className="govuk-body-s govuk-!-margin-bottom-0">
                            User: {feedbackUser || EMPTY_VALUE}
                          </p>
                        </div>
                        <button
                          type="button"
                          className="govuk-button govuk-button--warning govuk-!-margin-bottom-0"
                          data-module="govuk-button"
                          onClick={() => void handleDeleteFeedbackEntry(submittedAt)}
                          disabled={isDeleting || !submittedAt}
                        >
                          {isDeleting ? 'Deleting...' : 'Delete'}
                        </button>
                      </AdminFeedbackCardHeader>
                      <p className="govuk-body govuk-!-margin-top-3 govuk-!-margin-bottom-3">
                        {entry.feedback || EMPTY_VALUE}
                      </p>
                      <p className="govuk-body-s govuk-!-margin-bottom-1">
                        Form ID: {entry.formId || EMPTY_VALUE}
                      </p>
                      <p className="govuk-body-s govuk-!-margin-bottom-0">
                        Page:{' '}
                        {currentUrl ? (
                          <a
                            className="govuk-link"
                            href={currentUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {currentUrl}
                          </a>
                        ) : (
                          EMPTY_VALUE
                        )}
                      </p>
                    </AdminFeedbackCard>
                  );
                })}
              </AdminFeedbackList>
            )}
          </AdminFeedbackDialog>
        </AdminFeedbackBackdrop>
      )}
      {isDataErrorModalOpen && (
        <AdminFeedbackBackdrop>
          <AdminFeedbackDialog
            className="govuk-!-padding-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-data-errors-title"
          >
            <AdminFeedbackHeader>
              <div>
                <h2
                  id="admin-data-errors-title"
                  className="govuk-heading-m govuk-!-margin-bottom-2"
                >
                  {tenant === 'reward' ? 'User reported QA data errors' : 'Tenant data errors'}
                </h2>
                <p className="govuk-body govuk-!-margin-bottom-0">
                  {isDataErrorLoading
                    ? 'Loading data errors...'
                    : `${dataErrorEntries.length} data error entr${dataErrorEntries.length === 1 ? 'y' : 'ies'}`}
                </p>
                <div className="govuk-checkboxes govuk-checkboxes--small govuk-!-margin-top-2">
                  <div className="govuk-checkboxes__item">
                    <input
                      className="govuk-checkboxes__input"
                      id="show-historic-data-errors"
                      type="checkbox"
                      checked={showHistoricDataErrors}
                      onChange={(event) =>
                        void handleToggleHistoricDataErrors(event.target.checked)
                      }
                      disabled={
                        isDataErrorLoading ||
                        deletingDataErrorAt.length > 0 ||
                        recordingRewardQaDataErrorAt.length > 0
                      }
                    />
                    <label
                      className="govuk-label govuk-checkboxes__label"
                      htmlFor="show-historic-data-errors"
                    >
                      Show historic records
                    </label>
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="govuk-button govuk-button--secondary govuk-!-margin-bottom-0"
                data-module="govuk-button"
                onClick={handleCloseDataErrorModal}
                disabled={deletingDataErrorAt.length > 0 || recordingRewardQaDataErrorAt.length > 0}
              >
                Close
              </button>
            </AdminFeedbackHeader>
            {dataErrorAdminError && (
              <Notification
                header="Error"
                content={dataErrorAdminError}
                className="govuk-notification-banner--error"
              />
            )}
            {!isDataErrorLoading && dataErrorEntries.length === 0 && !dataErrorAdminError && (
              <p className="govuk-body govuk-!-margin-bottom-0">
                {canManageRewardQaDataErrors
                  ? 'No new submissions'
                  : 'No data errors have been submitted for this tenant yet.'}
              </p>
            )}
            {!isDataErrorLoading && dataErrorEntries.length > 0 && (
              <>
                <AdminFeedbackList>
                  {visibleDataErrorEntries.map((entry, index) => {
                    const submittedAt = String(entry.submittedAt || '').trim();
                    const formattedSubmittedAt =
                      formatLondonDateTime(submittedAt) || submittedAt || EMPTY_VALUE;
                    const deletedAt = String(entry.deletedAt || '').trim();
                    const formattedDeletedAt =
                      formatLondonDateTime(deletedAt) || deletedAt || EMPTY_VALUE;
                    const isDeleting = deletingDataErrorAt === submittedAt;
                    const isRecordingRewardQaDecision =
                      recordingRewardQaDataErrorAt === submittedAt;
                    const isHistoricDataError = deletedAt.length > 0;
                    const rewardQaDecision =
                      rewardQaDataErrorDecisions[submittedAt] || getDataErrorOutcome(entry);
                    const showHistoricAcceptOnly =
                      isHistoricDataError && rewardQaDecision === 'accepted';
                    const showHistoricRejectOnly =
                      isHistoricDataError && rewardQaDecision === 'rejected';
                    const showAcceptDataErrorDecision = !showHistoricRejectOnly;
                    const showRejectDataErrorDecision = !showHistoricAcceptOnly;
                    const cosNotesValue =
                      rewardQaDataErrorNotes[submittedAt] ?? String(entry.cosNotes || '');
                    const notesInputId = `cos-data-error-notes-${
                      dataErrorPageIndex * DATA_ERROR_PAGE_SIZE + index
                    }`;

                    return (
                      <AdminFeedbackCard
                        key={submittedAt || `${entry.raisedBy}-${entry.record}`}
                        data-decision={rewardQaDecision || undefined}
                      >
                        <AdminFeedbackCardHeader>
                          <div>
                            <h3 className="govuk-heading-s govuk-!-margin-bottom-1">
                              {formattedSubmittedAt}
                            </h3>
                            <p className="govuk-body-s govuk-!-margin-bottom-0">
                              Raised by: {entry.raisedBy || EMPTY_VALUE}
                            </p>
                          </div>
                          {canManageRewardQaDataErrors ? (
                            <DataErrorActionButtons>
                              {showAcceptDataErrorDecision && (
                                <DataErrorDecisionButton
                                  type="button"
                                  variant="accept"
                                  aria-label="Accept QA error"
                                  title="Accept QA error"
                                  onClick={() =>
                                    void handleRecordRewardQaDataErrorDecision(entry, 'accepted')
                                  }
                                  disabled={
                                    recordingRewardQaDataErrorAt.length > 0 ||
                                    isHistoricDataError ||
                                    !submittedAt
                                  }
                                >
                                  ✓
                                </DataErrorDecisionButton>
                              )}
                              {showRejectDataErrorDecision && (
                                <DataErrorDecisionButton
                                  type="button"
                                  variant="reject"
                                  aria-label="Reject QA error"
                                  title="Reject QA error"
                                  onClick={() =>
                                    void handleRecordRewardQaDataErrorDecision(entry, 'rejected')
                                  }
                                  disabled={
                                    recordingRewardQaDataErrorAt.length > 0 ||
                                    isHistoricDataError ||
                                    !submittedAt
                                  }
                                >
                                  ✕
                                </DataErrorDecisionButton>
                              )}
                            </DataErrorActionButtons>
                          ) : (
                            <button
                              type="button"
                              className="govuk-button govuk-button--warning govuk-!-margin-bottom-0"
                              data-module="govuk-button"
                              onClick={() => void handleDeleteDataErrorEntry(submittedAt)}
                              disabled={isDeleting || isHistoricDataError || !submittedAt}
                            >
                              {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                          )}
                        </AdminFeedbackCardHeader>
                        <p className="govuk-body govuk-!-margin-top-3 govuk-!-margin-bottom-2">
                          Error record: {entry.record || EMPTY_VALUE}
                        </p>
                        <p className="govuk-body govuk-!-margin-bottom-2">
                          Incorrect data: {entry.selected || EMPTY_VALUE}
                        </p>
                        <p className="govuk-body govuk-!-margin-bottom-3">
                          Notes: {entry.notes || EMPTY_VALUE}
                        </p>
                        {isHistoricDataError && (
                          <p className="govuk-body-s govuk-!-margin-bottom-3">
                            Deleted at: {formattedDeletedAt}
                          </p>
                        )}
                        {canManageRewardQaDataErrors && (
                          <div className="govuk-form-group govuk-!-margin-bottom-0">
                            <label className="govuk-label govuk-label--s" htmlFor={notesInputId}>
                              COS Notes
                            </label>
                            <input
                              id={notesInputId}
                              className="govuk-input"
                              type="text"
                              value={cosNotesValue}
                              onChange={(event) => {
                                const value = event.target.value;
                                setRewardQaDataErrorNotes((current) => ({
                                  ...current,
                                  [submittedAt]: value,
                                }));
                              }}
                              disabled={
                                isRecordingRewardQaDecision || isHistoricDataError || !submittedAt
                              }
                            />
                          </div>
                        )}
                      </AdminFeedbackCard>
                    );
                  })}
                </AdminFeedbackList>
                {dataErrorTotalPages > 1 && (
                  <div className="govuk-button-group govuk-!-margin-top-4">
                    <button
                      type="button"
                      className="govuk-button govuk-button--secondary govuk-!-margin-bottom-0"
                      data-module="govuk-button"
                      aria-label="Previous page"
                      onClick={() => setDataErrorPageIndex((current) => Math.max(current - 1, 0))}
                      disabled={dataErrorPageIndex === 0}
                    >
                      Previous
                    </button>
                    <span className="govuk-body govuk-!-margin-bottom-0">
                      Page {dataErrorPageIndex + 1} of {dataErrorTotalPages}
                    </span>
                    <button
                      type="button"
                      className="govuk-button govuk-button--secondary govuk-!-margin-bottom-0"
                      data-module="govuk-button"
                      aria-label="Next page"
                      onClick={() =>
                        setDataErrorPageIndex((current) =>
                          Math.min(current + 1, dataErrorTotalPages - 1),
                        )
                      }
                      disabled={dataErrorPageIndex >= dataErrorTotalPages - 1}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </AdminFeedbackDialog>
        </AdminFeedbackBackdrop>
      )}
      {isRewardTransferOwnershipModalOpen && (
        <AdminFeedbackBackdrop>
          <AdminFeedbackDialog
            className="govuk-!-padding-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="reward-transfer-ownership-title"
          >
            <AdminFeedbackHeader>
              <div>
                <h2
                  id="reward-transfer-ownership-title"
                  className="govuk-heading-m govuk-!-margin-bottom-2"
                >
                  Transfer ownership
                </h2>
                <p className="govuk-body govuk-!-margin-bottom-0">
                  {isRewardTransferOwnershipLoading
                    ? 'Loading nominations...'
                    : `${rewardTransferOwnershipRows.length} nomination${rewardTransferOwnershipRows.length === 1 ? '' : 's'}`}
                </p>
              </div>
              <button
                type="button"
                className="govuk-button govuk-button--secondary govuk-!-margin-bottom-0"
                data-module="govuk-button"
                onClick={handleCloseRewardTransferOwnershipModal}
                disabled={Boolean(rewardTransferSavingFormId) || isRewardTransferOwnershipLoading}
              >
                Close
              </button>
            </AdminFeedbackHeader>
            {rewardTransferOwnershipError && (
              <Notification
                header="Error"
                content={rewardTransferOwnershipError}
                className="govuk-notification-banner--error"
              />
            )}
            {isRewardTransferOwnershipLoading ? (
              <p className="govuk-body govuk-!-margin-bottom-0">Loading nominations...</p>
            ) : rewardTransferOwnershipRows.length === 0 ? (
              <p className="govuk-body govuk-!-margin-bottom-0">
                No nominations are awaiting panel decision.
              </p>
            ) : (
              <table className="govuk-table">
                <thead className="govuk-table__head">
                  <tr className="govuk-table__row">
                    <th scope="col" className="govuk-table__header">
                      Submitted date
                    </th>
                    <th scope="col" className="govuk-table__header">
                      Identifier
                    </th>
                    <th scope="col" className="govuk-table__header">
                      Type
                    </th>
                    <th scope="col" className="govuk-table__header">
                      Amount
                    </th>
                    <th scope="col" className="govuk-table__header">
                      Current function
                    </th>
                    <th scope="col" className="govuk-table__header">
                      <span className="govuk-visually-hidden">Transfer</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="govuk-table__body">
                  {rewardTransferOwnershipRows.map((row) => {
                    const formId = String(row.formId || '').trim();
                    const isEditing = rewardTransferEditingFormId === formId;
                    const isSaving = rewardTransferSavingFormId === formId;
                    const currentFunction = String(row.currentFunction || '').trim();
                    const transferFunctionOptions = rewardTransferFunctionOptions.filter(
                      (option) =>
                        normalizeRewardFunction(option) !==
                        normalizeRewardFunction(currentFunction),
                    );

                    return (
                      <tr className="govuk-table__row" key={formId || row.identifier}>
                        <td className="govuk-table__cell">{formatDate(row.submittedAt)}</td>
                        <td className="govuk-table__cell">{row.identifier || EMPTY_VALUE}</td>
                        <td className="govuk-table__cell">{row.rewardType || EMPTY_VALUE}</td>
                        <td className="govuk-table__cell">{row.amount || EMPTY_VALUE}</td>
                        <td className="govuk-table__cell">{currentFunction || EMPTY_VALUE}</td>
                        <td className="govuk-table__cell">
                          {isEditing ? (
                            <div className="govuk-form-group govuk-!-margin-bottom-0">
                              <label
                                className="govuk-label govuk-visually-hidden"
                                htmlFor={`reward-transfer-function-${formId}`}
                              >
                                New function
                              </label>
                              <select
                                id={`reward-transfer-function-${formId}`}
                                className="govuk-select govuk-!-margin-bottom-2"
                                value={rewardTransferSelectedFunction}
                                onChange={(event) => {
                                  setRewardTransferSelectedFunction(event.target.value);
                                  if (rewardTransferOwnershipError) {
                                    setRewardTransferOwnershipError('');
                                  }
                                }}
                                disabled={isSaving}
                              >
                                <option value="">Select function</option>
                                {transferFunctionOptions.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                              <div className="govuk-button-group govuk-!-margin-bottom-0">
                                <button
                                  type="button"
                                  className="govuk-button govuk-button--green govuk-!-margin-bottom-0"
                                  data-module="govuk-button"
                                  onClick={() => void handleConfirmRewardTransfer(row)}
                                  disabled={
                                    isSaving ||
                                    !rewardTransferSelectedFunction ||
                                    transferFunctionOptions.length === 0
                                  }
                                >
                                  {isSaving ? 'Transferring...' : 'Confirm transfer'}
                                </button>
                                <button
                                  type="button"
                                  className="govuk-button govuk-button--warning govuk-!-margin-bottom-0"
                                  data-module="govuk-button"
                                  onClick={handleCancelRewardTransfer}
                                  disabled={isSaving}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="govuk-button govuk-button--secondary govuk-!-margin-bottom-0"
                              data-module="govuk-button"
                              onClick={() => {
                                setRewardTransferEditingFormId(formId);
                                setRewardTransferSelectedFunction('');
                                setRewardTransferOwnershipError('');
                              }}
                              disabled={Boolean(rewardTransferSavingFormId) || !formId}
                            >
                              Transfer
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </AdminFeedbackDialog>
        </AdminFeedbackBackdrop>
      )}
      {isFunctionMembersModalOpen && (
        <AdminFeedbackBackdrop>
          <AdminFeedbackDialog
            className="govuk-!-padding-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="function-members-title"
          >
            <AdminFeedbackHeader>
              <div>
                <h2 id="function-members-title" className="govuk-heading-m govuk-!-margin-bottom-2">
                  Function Members
                </h2>
                <p className="govuk-body govuk-!-margin-bottom-0">
                  {functionMembersQuery.isLoading
                    ? 'Loading function members...'
                    : `${functionMembers.length} member${functionMembers.length === 1 ? '' : 's'}`}
                </p>
              </div>
              <button
                type="button"
                className="govuk-button govuk-button--secondary govuk-!-margin-bottom-0"
                data-module="govuk-button"
                onClick={handleCloseFunctionMembersModal}
                disabled={
                  addFunctionMemberMutation.isPending || removeFunctionMemberMutation.isPending
                }
              >
                Close
              </button>
            </AdminFeedbackHeader>
            {functionMemberFeedback && (
              <Notification
                header={functionMemberFeedback.type === 'success' ? 'Success' : 'Error'}
                content={functionMemberFeedback.message}
                className={
                  functionMemberFeedback.type === 'success'
                    ? 'govuk-notification-banner--success'
                    : 'govuk-notification-banner--error'
                }
              />
            )}
            <div className="share-form-row govuk-!-margin-bottom-5">
              <div className="share-add-user-box">
                <div className="share-add-user-grid">
                  <div className="share-form-inline">
                    <div className="share-form-field share-form-field--email">
                      <label className="govuk-label" htmlFor="function-member-email">
                        Email
                      </label>
                      <input
                        id="function-member-email"
                        type="email"
                        className="govuk-input"
                        placeholder="name@domain.gov.uk"
                        value={functionMemberEmail}
                        onChange={(event) => {
                          setFunctionMemberEmail(event.target.value);
                          setFunctionMemberFeedback(null);
                        }}
                        disabled={addFunctionMemberMutation.isPending}
                      />
                    </div>
                    <div className="share-form-field share-form-field--role">
                      <label className="govuk-label" htmlFor="function-member-function">
                        Function
                      </label>
                      {functionMemberFunctions.length > 1 ? (
                        <select
                          id="function-member-function"
                          className="govuk-select"
                          value={selectedFunctionForMembers}
                          onChange={(event) => {
                            setSelectedFunctionMemberFunction(event.target.value);
                            setFunctionMemberFeedback(null);
                          }}
                          disabled={addFunctionMemberMutation.isPending}
                        >
                          {functionMemberFunctions.map((functionName) => (
                            <option key={functionName} value={functionName}>
                              {functionName}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <p className="govuk-body govuk-!-margin-top-2 govuk-!-margin-bottom-0">
                          {selectedFunctionForMembers || EMPTY_VALUE}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="share-form-actions">
                    <button
                      type="button"
                      className="govuk-button govuk-button--green"
                      data-module="govuk-button"
                      data-prevent-double-click="true"
                      onClick={() => void handleAddFunctionMember()}
                      disabled={
                        addFunctionMemberMutation.isPending ||
                        !functionMemberEmail.trim() ||
                        !selectedFunctionForMembers
                      }
                    >
                      <span>
                        Add
                        <br />
                        member
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {functionMembersQuery.isError && (
              <Notification
                header="Error"
                content="Unable to load function members right now."
                className="govuk-notification-banner--error"
              />
            )}
            {!functionMembersQuery.isLoading && !functionMembersQuery.isError && (
              <table className="govuk-table">
                <thead className="govuk-table__head">
                  <tr className="govuk-table__row">
                    <th className="govuk-table__header" scope="col">
                      Username
                    </th>
                    <th className="govuk-table__header" scope="col">
                      Last Active
                    </th>
                    <th className="govuk-table__header" scope="col">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="govuk-table__body">
                  {functionMembers.length === 0 ? (
                    <tr className="govuk-table__row">
                      <td className="govuk-table__cell" colSpan={3}>
                        No function members found.
                      </td>
                    </tr>
                  ) : (
                    functionMembers.map((member) => (
                      <tr className="govuk-table__row" key={member.username}>
                        <td className="govuk-table__cell">{member.email || member.username}</td>
                        <td className="govuk-table__cell">
                          {formatLondonDateTime(member.lastActive || '') || EMPTY_VALUE}
                        </td>
                        <td className="govuk-table__cell">
                          <button
                            type="button"
                            className="govuk-button govuk-button--warning govuk-!-margin-bottom-0"
                            data-module="govuk-button"
                            onClick={() => void removeFunctionMemberMutation.mutateAsync(member)}
                            disabled={removeFunctionMemberMutation.isPending}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </AdminFeedbackDialog>
        </AdminFeedbackBackdrop>
      )}
      {myFormsDownloadForm && (
        <AdminFeedbackBackdrop>
          <MyFormsDownloadDialog
            className="govuk-!-padding-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="my-forms-download-title"
          >
            <h2 id="my-forms-download-title" className="govuk-heading-m govuk-!-margin-bottom-3">
              Download your form
            </h2>
            <p className="govuk-body govuk-!-margin-bottom-4">
              Please select a file type and click the download button to download a copy of your
              form.
            </p>
            {myFormsDownloadError && (
              <p className="govuk-error-message" role="alert">
                <span className="govuk-visually-hidden">Error:</span> {myFormsDownloadError}
              </p>
            )}
            <MyFormsDownloadActionRow>
              <div className="govuk-form-group govuk-!-margin-bottom-0">
                <label
                  className="govuk-label govuk-visually-hidden"
                  htmlFor="my-forms-download-format"
                >
                  File type
                </label>
                <select
                  ref={myFormsDownloadSelectRef}
                  id="my-forms-download-format"
                  className="govuk-select"
                  value={selectedMyFormsDownloadFormat}
                  onChange={(event) => {
                    setSelectedMyFormsDownloadFormat(
                      String(event.target.value || '')
                        .trim()
                        .toLowerCase(),
                    );
                    if (myFormsDownloadError) {
                      setMyFormsDownloadError('');
                    }
                  }}
                  disabled={isMyFormsDownloadSubmitting}
                >
                  <option value="">Select file type</option>
                  {myFormsDownloadFormats.includes('html') && <option value="html">HTML</option>}
                  {myFormsDownloadFormats.includes('pdf') && <option value="pdf">PDF</option>}
                  {myFormsDownloadFormats.includes('excel') && <option value="excel">Excel</option>}
                </select>
              </div>
              <button
                type="button"
                className="govuk-button"
                data-module="govuk-button"
                onClick={() => {
                  void handleMyFormsDownload();
                }}
                disabled={isMyFormsDownloadSubmitting || selectedMyFormsDownloadFormat.length === 0}
              >
                {isMyFormsDownloadSubmitting ? 'Downloading...' : 'Download'}
              </button>
            </MyFormsDownloadActionRow>
            <MyFormsDownloadCancelRow>
              <button
                type="button"
                className="govuk-button govuk-button--secondary"
                data-module="govuk-button"
                onClick={closeMyFormsDownloadModal}
                disabled={isMyFormsDownloadSubmitting}
              >
                Cancel
              </button>
            </MyFormsDownloadCancelRow>
          </MyFormsDownloadDialog>
        </AdminFeedbackBackdrop>
      )}
      {myFormsDeleteForm && (
        <AdminFeedbackBackdrop>
          <MyFormsDownloadDialog
            className="govuk-!-padding-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="my-forms-delete-title"
          >
            <h2 id="my-forms-delete-title" className="govuk-heading-m govuk-!-margin-bottom-3">
              Are you sure you want to delete this form?
            </h2>
            {myFormsDeleteError && (
              <p className="govuk-error-message" role="alert">
                <span className="govuk-visually-hidden">Error:</span> {myFormsDeleteError}
              </p>
            )}
            <MyFormsDownloadActionRow>
              <button
                type="button"
                className="govuk-button govuk-button--warning"
                data-module="govuk-button"
                onClick={() => {
                  void handleMyFormsDelete();
                }}
                disabled={isMyFormsDeleting}
              >
                {isMyFormsDeleting ? 'Deleting...' : 'Yes'}
              </button>
              <button
                type="button"
                className="govuk-button govuk-button--secondary"
                data-module="govuk-button"
                onClick={closeMyFormsDeleteModal}
                disabled={isMyFormsDeleting}
              >
                No
              </button>
            </MyFormsDownloadActionRow>
          </MyFormsDownloadDialog>
        </AdminFeedbackBackdrop>
      )}
      {isAdminDownloadModalOpen && (
        <AdminFeedbackBackdrop>
          <MyFormsDownloadDialog
            className="govuk-!-padding-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-download-title"
          >
            <h2 id="admin-download-title" className="govuk-heading-m govuk-!-margin-bottom-3">
              Download responses
            </h2>
            {adminDownloadError && (
              <p className="govuk-error-message" role="alert">
                <span className="govuk-visually-hidden">Error:</span> {adminDownloadError}
              </p>
            )}
            {tenant === 'scs' ? (
              <div className="govuk-form-group">
                <div
                  style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}
                >
                  <div style={{ width: 180, maxWidth: '100%' }}>
                    <label className="govuk-label" htmlFor="admin-download-reporting-year-filter">
                      Reporting Year Filter
                    </label>
                    <select
                      id="admin-download-reporting-year-filter"
                      className="govuk-select"
                      style={{ width: '100%', minWidth: 0, boxSizing: 'border-box' }}
                      value={adminDownloadReportYear}
                      onChange={(event) => {
                        setAdminDownloadReportYear(String(event.target.value || '').trim());
                      }}
                      disabled={activeAdminDownload.length > 0}
                    >
                      <option value="">Select year</option>
                      {reportYearOptions.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ width: 200, maxWidth: '100%' }}>
                    <label
                      className="govuk-label"
                      htmlFor="admin-download-reporting-quarter-filter"
                    >
                      Reporting Quarter Filter
                    </label>
                    <select
                      id="admin-download-reporting-quarter-filter"
                      className="govuk-select"
                      style={{ width: '100%', minWidth: 0, boxSizing: 'border-box' }}
                      value={adminDownloadReportQuarter}
                      onChange={(event) => {
                        setAdminDownloadReportQuarter(String(event.target.value || '').trim());
                      }}
                      disabled={activeAdminDownload.length > 0}
                    >
                      <option value="">Select quarter</option>
                      {scsAdminDownloadQuarterOptions.map((quarter) => (
                        <option key={quarter} value={quarter}>
                          {quarter}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ width: 218, maxWidth: '100%' }}>
                    <label className="govuk-label" htmlFor="admin-download-status-filter">
                      Status Filter
                    </label>
                    <select
                      id="admin-download-status-filter"
                      className="govuk-select"
                      style={{ width: '100%', minWidth: 0, boxSizing: 'border-box' }}
                      value={adminDownloadScsStatus}
                      onChange={(event) => {
                        setAdminDownloadScsStatus(String(event.target.value || '').trim());
                      }}
                      disabled={activeAdminDownload.length > 0}
                    >
                      <option value="">Select status</option>
                      {scsAdminDownloadStatusOptions.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ) : (
              hasReportPeriodQuestion && (
                <div
                  className={`govuk-form-group${reportPeriodFilterError ? ' govuk-form-group--error' : ''}`}
                >
                  <fieldset className="govuk-fieldset">
                    <legend className="govuk-fieldset__legend govuk-fieldset__legend--s">
                      Reporting period
                    </legend>
                    {reportPeriodFilterError && (
                      <p className="govuk-error-message">
                        <span className="govuk-visually-hidden">Error:</span>{' '}
                        {reportPeriodFilterError}
                      </p>
                    )}
                    <div
                      style={{
                        display: 'flex',
                        gap: 12,
                        alignItems: 'flex-start',
                        flexWrap: 'wrap',
                      }}
                    >
                      {!useScsReportingYearFilter && (
                        <div>
                          <label
                            className="govuk-label"
                            htmlFor="admin-download-report-period-quarter"
                          >
                            Quarter
                          </label>
                          <select
                            id="admin-download-report-period-quarter"
                            className="govuk-select"
                            value={selectedReportQuarter}
                            onChange={(event) => {
                              setSelectedReportQuarter(String(event.target.value || '').trim());
                            }}
                            disabled={activeAdminDownload.length > 0}
                          >
                            <option value="">Select quarter</option>
                            {reportQuarterOptions.map((quarter) => (
                              <option key={quarter} value={quarter}>
                                {quarter}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div>
                        <label className="govuk-label" htmlFor="admin-download-report-period-year">
                          Reporting Year
                        </label>
                        <select
                          id="admin-download-report-period-year"
                          className="govuk-select"
                          value={selectedReportYear}
                          onChange={(event) => {
                            setSelectedReportYear(String(event.target.value || '').trim());
                          }}
                          disabled={activeAdminDownload.length > 0}
                        >
                          <option value="">Select year</option>
                          {reportYearOptions.map((year) => (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </fieldset>
                </div>
              )
            )}
            {tenant !== 'scs' && (
              <div className="govuk-form-group">
                <div className="govuk-checkboxes govuk-checkboxes--small">
                  <div className="govuk-checkboxes__item">
                    <input
                      className="govuk-checkboxes__input"
                      id="admin-include-awaiting-approval"
                      name="admin-include-awaiting-approval"
                      type="checkbox"
                      checked={includeAdminAwaitingApprovalDownloads}
                      onChange={(event) => {
                        setIncludeAdminAwaitingApprovalDownloads(event.target.checked);
                      }}
                      disabled={activeAdminDownload.length > 0}
                    />
                    <label
                      className="govuk-label govuk-checkboxes__label"
                      htmlFor="admin-include-awaiting-approval"
                    >
                      Include Awaiting Approval
                    </label>
                  </div>
                  <div className="govuk-checkboxes__item">
                    <input
                      className="govuk-checkboxes__input"
                      id="admin-include-in-progress"
                      name="admin-include-in-progress"
                      type="checkbox"
                      checked={includeAdminInProgressDownloads}
                      onChange={(event) => {
                        setIncludeAdminInProgressDownloads(event.target.checked);
                      }}
                      disabled={activeAdminDownload.length > 0}
                    />
                    <label
                      className="govuk-label govuk-checkboxes__label"
                      htmlFor="admin-include-in-progress"
                    >
                      Include In Progress
                    </label>
                  </div>
                </div>
              </div>
            )}
            <p className="govuk-body govuk-!-font-weight-bold" aria-live="polite">
              {tenant === 'scs'
                ? `${adminDownloadCount} ${adminDownloadCount === 1 ? 'item' : 'items'} selected`
                : adminDownloadCount === 1
                  ? '1 download found'
                  : `${adminDownloadCount} downloads found`}
            </p>
            <MyFormsDownloadActionRow>
              <div className="govuk-form-group govuk-!-margin-bottom-0">
                <label className="govuk-label" htmlFor="admin-download-format">
                  Download format
                </label>
                <select
                  id="admin-download-format"
                  className="govuk-select"
                  value={selectedAdminDownloadFormat}
                  onChange={(event) => {
                    setSelectedAdminDownloadFormat(String(event.target.value || '').trim());
                  }}
                  disabled={activeAdminDownload.length > 0}
                >
                  <option value="">Select format</option>
                  <option value="excel">Excel (Relational)</option>
                  <option value="hybrid">Excel (Normal)</option>
                  <option value="html">HTML Files</option>
                  <option value="pdf">PDF Files</option>
                </select>
              </div>
              <button
                type="button"
                className="govuk-button"
                data-module="govuk-button"
                onClick={() =>
                  void handleAdminBulkDownload(
                    selectedAdminDownloadFormat === 'pdf'
                      ? 'pdf'
                      : selectedAdminDownloadFormat === 'html'
                        ? 'html'
                        : selectedAdminDownloadFormat === 'hybrid'
                          ? 'hybrid'
                          : 'excel',
                  )
                }
                disabled={
                  activeAdminDownload.length > 0 ||
                  adminDownloadCount === 0 ||
                  selectedAdminDownloadFormat.length === 0
                }
              >
                {activeAdminDownload === 'bulk-pdf'
                  ? 'Preparing files...'
                  : activeAdminDownload.startsWith('bulk-')
                    ? 'Downloading...'
                    : 'Download'}
              </button>
            </MyFormsDownloadActionRow>
            <MyFormsDownloadCancelRow>
              <button
                type="button"
                className="govuk-button govuk-button--secondary"
                data-module="govuk-button"
                onClick={handleCloseAdminDownloadModal}
                disabled={activeAdminDownload.length > 0}
              >
                Cancel
              </button>
            </MyFormsDownloadCancelRow>
          </MyFormsDownloadDialog>
        </AdminFeedbackBackdrop>
      )}
      {unapproveForm && (
        <AdminFeedbackBackdrop>
          <AdminFeedbackDialog
            className="govuk-!-padding-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="unapprove-form-title"
          >
            <AdminFeedbackHeader>
              <div>
                <h2 id="unapprove-form-title" className="govuk-heading-m govuk-!-margin-bottom-2">
                  Reject form
                </h2>
                <p className="govuk-body govuk-!-margin-bottom-0">
                  {unapproveForm.identifier || unapproveForm.formId || EMPTY_VALUE}
                </p>
              </div>
              <button
                type="button"
                className="govuk-button govuk-button--secondary govuk-!-margin-bottom-0"
                data-module="govuk-button"
                onClick={handleCloseUnapproveModal}
                disabled={isUnapproving}
              >
                Close
              </button>
            </AdminFeedbackHeader>
            {unapproveError && (
              <Notification
                header="Error"
                content={unapproveError}
                className="govuk-notification-banner--error"
              />
            )}
            <div
              className={`govuk-form-group${unapproveError && !unapproveReason.trim() ? ' govuk-form-group--error' : ''}`}
            >
              <label className="govuk-label" htmlFor="unapprove-reason">
                Reason for rejection
              </label>
              <textarea
                id="unapprove-reason"
                className="govuk-textarea"
                rows={5}
                value={unapproveReason}
                onChange={(event) => {
                  setUnapproveReason(event.target.value);
                  if (unapproveError) setUnapproveError('');
                }}
                disabled={isUnapproving}
              />
            </div>
            <MyFormsDownloadActionRow>
              <button
                type="button"
                className="govuk-button govuk-button--warning"
                data-module="govuk-button"
                data-prevent-double-click="true"
                onClick={() => void handleUnapproveForm()}
                disabled={isUnapproving}
              >
                {isUnapproving ? 'Rejecting...' : 'Reject'}
              </button>
              <button
                type="button"
                className="govuk-button govuk-button--secondary"
                data-module="govuk-button"
                onClick={handleCloseUnapproveModal}
                disabled={isUnapproving}
              >
                Cancel
              </button>
            </MyFormsDownloadActionRow>
          </AdminFeedbackDialog>
        </AdminFeedbackBackdrop>
      )}
      {isVoucherBulkUploadModalOpen && (
        <AdminFeedbackBackdrop>
          <MyFormsDownloadDialog
            className="govuk-!-padding-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="voucher-bulk-upload-title"
          >
            <h2 id="voucher-bulk-upload-title" className="govuk-heading-m">
              Voucher bulk upload
            </h2>
            {voucherBulkUploadError && (
              <p className="govuk-error-message" role="alert">
                <span className="govuk-visually-hidden">Error:</span> {voucherBulkUploadError}
              </p>
            )}
            <p className="govuk-body govuk-!-font-weight-bold" aria-live="polite">
              {voucherBulkUploadRows.length === 1
                ? '1 issue voucher response found'
                : `${voucherBulkUploadRows.length} issue voucher responses found`}
            </p>
            <button
              type="button"
              className="govuk-button govuk-button--secondary"
              data-module="govuk-button"
              onClick={() => void downloadRewardVoucherBulkUpload()}
              disabled={
                isVoucherBulkUploadDownloading ||
                isVoucherBulkUploadSaving ||
                voucherBulkUploadRows.length === 0
              }
            >
              {isVoucherBulkUploadDownloading ? 'Downloading...' : 'Download bulk download form'}
            </button>
            <div
              className={`govuk-form-group${voucherBulkUploadError && !voucherBulkUploadChecked ? ' govuk-form-group--error' : ''}`}
            >
              <div className="govuk-checkboxes govuk-checkboxes--small">
                <div className="govuk-checkboxes__item">
                  <input
                    className="govuk-checkboxes__input"
                    id="voucher-bulk-upload-complete"
                    name="voucher-bulk-upload-complete"
                    type="checkbox"
                    checked={voucherBulkUploadChecked}
                    onChange={(event) => {
                      setVoucherBulkUploadChecked(event.target.checked);
                      if (voucherBulkUploadError) setVoucherBulkUploadError('');
                    }}
                    disabled={isVoucherBulkUploadDownloading || isVoucherBulkUploadSaving}
                  />
                  <label
                    className="govuk-label govuk-checkboxes__label"
                    htmlFor="voucher-bulk-upload-complete"
                  >
                    Mark these forms as 'bulk uploaded'
                  </label>
                </div>
              </div>
            </div>
            <MyFormsDownloadActionRow>
              <button
                type="button"
                className="govuk-button govuk-button--secondary"
                data-module="govuk-button"
                onClick={closeVoucherBulkUploadModal}
                disabled={isVoucherBulkUploadDownloading || isVoucherBulkUploadSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="govuk-button govuk-button--green"
                data-module="govuk-button"
                data-prevent-double-click="true"
                onClick={() => void saveRewardVoucherBulkUpload()}
                disabled={
                  isVoucherBulkUploadDownloading ||
                  isVoucherBulkUploadSaving ||
                  !voucherBulkUploadChecked ||
                  voucherBulkUploadRows.length === 0
                }
              >
                {isVoucherBulkUploadSaving ? 'Saving...' : 'Save'}
              </button>
            </MyFormsDownloadActionRow>
          </MyFormsDownloadDialog>
        </AdminFeedbackBackdrop>
      )}
      {rewardVoucherIssueForm && (
        <AdminFeedbackBackdrop>
          <MyFormsDownloadDialog
            className="govuk-!-padding-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="reward-voucher-issue-title"
          >
            <h2 id="reward-voucher-issue-title" className="govuk-heading-m">
              {rewardCosModalTitle}
            </h2>
            {rewardVoucherIssueError && (
              <p className="govuk-error-message" role="alert">
                <span className="govuk-visually-hidden">Error:</span> {rewardVoucherIssueError}
              </p>
            )}
            <RewardVoucherFieldRows>
              {rewardVoucherFields.map((field) => (
                <div key={field.label} style={{ display: 'contents' }}>
                  <dt>{field.label}</dt>
                  <dd>
                    <RewardVoucherValue>
                      <RewardVoucherValueInput
                        className="govuk-input"
                        type="text"
                        value={field.value}
                        readOnly
                        aria-label={field.label}
                      />
                      <CopyValueButton
                        type="button"
                        className="govuk-button govuk-button--secondary govuk-!-margin-bottom-0"
                        aria-label={`Copy ${field.copyLabel}`}
                        title={`Copy ${field.copyLabel}`}
                        onClick={() =>
                          void handleCopyRewardVoucherValue(field.copyLabel, field.value)
                        }
                        disabled={field.value === EMPTY_VALUE}
                      >
                        <svg
                          aria-hidden="true"
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect x="9" y="9" width="13" height="13" rx="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                      </CopyValueButton>
                    </RewardVoucherValue>
                  </dd>
                </div>
              ))}
            </RewardVoucherFieldRows>
            <MyFormsDownloadActionRow className="govuk-!-margin-bottom-4">
              <button
                type="button"
                className="govuk-button govuk-button--secondary"
                data-module="govuk-button"
                onClick={() => void downloadRewardVoucherNominationPdf()}
                disabled={isRewardVoucherPdfDownloading || isRewardVoucherIssueSaving}
              >
                {isRewardVoucherPdfDownloading
                  ? 'Downloading...'
                  : 'Download full nomination (PDF)'}
              </button>
              {showRewardCosAuditButton && (
                <button
                  type="button"
                  className="govuk-button govuk-button--secondary"
                  data-module="govuk-button"
                  onClick={() => void openRewardCosAuditModal()}
                  disabled={isRewardCosAuditLoading || isRewardVoucherIssueSaving}
                >
                  {isRewardCosAuditLoading ? 'Loading audit...' : 'Show audit'}
                </button>
              )}
            </MyFormsDownloadActionRow>
            {rewardCosAuditError && (
              <p className="govuk-error-message" role="alert">
                <span className="govuk-visually-hidden">Error:</span> {rewardCosAuditError}
              </p>
            )}
            <div
              className={`govuk-form-group${rewardVoucherIssueHasError ? ' govuk-form-group--error' : ''}`}
            >
              <div className="govuk-checkboxes govuk-checkboxes--small">
                <div className="govuk-checkboxes__item">
                  <input
                    className="govuk-checkboxes__input"
                    id="reward-voucher-issued"
                    name="reward-voucher-issued"
                    type="checkbox"
                    checked={rewardVoucherIssueChecked}
                    onChange={(event) => {
                      setRewardVoucherIssueChecked(event.target.checked);
                      if (rewardVoucherIssueError) setRewardVoucherIssueError('');
                    }}
                    disabled={isRewardVoucherIssueSaving || isRewardVoucherPdfDownloading}
                  />
                  <label
                    className="govuk-label govuk-checkboxes__label"
                    htmlFor="reward-voucher-issued"
                  >
                    {rewardCosModalCheckboxLabel}
                  </label>
                  {!rewardCosModalIsInYearBonus && rewardVoucherIssueChecked && (
                    <span className="govuk-tag govuk-tag--green govuk-!-margin-left-2">
                      Voucher manually issued
                    </span>
                  )}
                </div>
              </div>
            </div>
            <MyFormsDownloadActionRow>
              <button
                type="button"
                className="govuk-button govuk-button--secondary"
                data-module="govuk-button"
                onClick={closeRewardVoucherIssueModal}
                disabled={isRewardVoucherIssueSaving || isRewardVoucherPdfDownloading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="govuk-button govuk-button--green"
                data-module="govuk-button"
                data-prevent-double-click="true"
                onClick={() => void saveRewardVoucherIssued()}
                disabled={
                  isRewardVoucherIssueSaving ||
                  isRewardVoucherPdfDownloading ||
                  !rewardCosModalHasChanges
                }
              >
                {isRewardVoucherIssueSaving ? 'Saving...' : 'Save'}
              </button>
            </MyFormsDownloadActionRow>
          </MyFormsDownloadDialog>
          {rewardCosAuditDialog && (
            <AdminFeedbackBackdrop>
              <MyFormsDownloadDialog
                className="govuk-!-padding-6"
                role="dialog"
                aria-modal="true"
                aria-labelledby="reward-cos-audit-title"
                aria-describedby="reward-cos-audit-subtitle"
              >
                <h2 id="reward-cos-audit-title" className="govuk-heading-m govuk-!-margin-bottom-2">
                  {rewardCosAuditDialog.identifier}
                </h2>
                <p className="govuk-hint govuk-!-font-size-14">
                  Form ID: {rewardCosAuditDialog.formId}
                </p>
                <p id="reward-cos-audit-subtitle" className="govuk-body govuk-!-font-weight-bold">
                  {rewardCosAuditDialog.awardType}
                </p>
                <table className="govuk-table">
                  <thead className="govuk-table__head">
                    <tr className="govuk-table__row">
                      <th scope="col" className="govuk-table__header">
                        Action date
                      </th>
                      <th scope="col" className="govuk-table__header">
                        Action
                      </th>
                      <th scope="col" className="govuk-table__header">
                        Completed by
                      </th>
                    </tr>
                  </thead>
                  <tbody className="govuk-table__body">
                    {rewardCosAuditDialog.audit.length === 0 ? (
                      <tr className="govuk-table__row">
                        <td className="govuk-table__cell" colSpan={3}>
                          No audit records found
                        </td>
                      </tr>
                    ) : (
                      rewardCosAuditDialog.audit.map((row, index) => (
                        <tr
                          className="govuk-table__row"
                          key={`${row.actionDate || 'audit'}-${row.action || index}`}
                        >
                          <td className="govuk-table__cell">
                            {formatAuditDateTime(row.actionDate)}
                          </td>
                          <td className="govuk-table__cell">{row.action || EMPTY_VALUE}</td>
                          <td className="govuk-table__cell">{row.completedBy || EMPTY_VALUE}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                <MyFormsDownloadActionRow>
                  <button
                    type="button"
                    className="govuk-button govuk-button--secondary"
                    data-module="govuk-button"
                    onClick={() => setRewardCosAuditDialog(null)}
                  >
                    Close audit
                  </button>
                </MyFormsDownloadActionRow>
              </MyFormsDownloadDialog>
            </AdminFeedbackBackdrop>
          )}
        </AdminFeedbackBackdrop>
      )}
      {psfaAuditDialog && (
        <AdminFeedbackBackdrop>
          <MyFormsDownloadDialog
            className="govuk-!-padding-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="psfa-audit-title"
          >
            <h2 id="psfa-audit-title" className="govuk-heading-m govuk-!-margin-bottom-2">
              {psfaAuditDialog.identifier}
            </h2>
            <p className="govuk-hint govuk-!-font-size-14">Form ID: {psfaAuditDialog.formId}</p>
            <table className="govuk-table">
              <thead className="govuk-table__head">
                <tr className="govuk-table__row">
                  <th scope="col" className="govuk-table__header">
                    Action date
                  </th>
                  <th scope="col" className="govuk-table__header">
                    Action
                  </th>
                  <th scope="col" className="govuk-table__header">
                    Completed by
                  </th>
                </tr>
              </thead>
              <tbody className="govuk-table__body">
                {psfaAuditDialog.audit.length === 0 ? (
                  <tr className="govuk-table__row">
                    <td className="govuk-table__cell" colSpan={3}>
                      No audit records found
                    </td>
                  </tr>
                ) : (
                  psfaAuditDialog.audit.map((row, index) => (
                    <tr
                      className="govuk-table__row"
                      key={`${row.actionDate || 'audit'}-${row.action || index}`}
                    >
                      <td className="govuk-table__cell">{formatAuditDateTime(row.actionDate)}</td>
                      <td className="govuk-table__cell">{row.action || EMPTY_VALUE}</td>
                      <td className="govuk-table__cell">
                        {row.completedBy || EMPTY_VALUE}
                        {shouldShowAdminAuditSentTo(row) && (
                          <p className="govuk-body-s govuk-!-margin-bottom-0">
                            (sent to {row.sentTo})
                          </p>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <MyFormsDownloadActionRow>
              <button
                type="button"
                className="govuk-button govuk-button--secondary"
                data-module="govuk-button"
                onClick={() => setPsfaAuditDialog(null)}
              >
                Close audit
              </button>
            </MyFormsDownloadActionRow>
          </MyFormsDownloadDialog>
        </AdminFeedbackBackdrop>
      )}
      {psfaAccessDialog && (
        <AdminFeedbackBackdrop>
          <MyFormsDownloadDialog
            className="govuk-!-padding-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="psfa-access-title"
            style={tenant === 'scs' ? { width: 'min(768px, 100%)' } : undefined}
          >
            <h2 id="psfa-access-title" className="govuk-heading-m govuk-!-margin-bottom-2">
              {psfaAccessDialog.identifier}
            </h2>
            <table className="govuk-table">
              <thead className="govuk-table__head">
                <tr className="govuk-table__row">
                  <th scope="col" className="govuk-table__header">
                    Email address
                  </th>
                  <th scope="col" className="govuk-table__header">
                    Role
                  </th>
                  <th scope="col" className="govuk-table__header">
                    Last viewed
                  </th>
                  <th scope="col" className="govuk-table__header">
                    Last edited
                  </th>
                </tr>
              </thead>
              <tbody className="govuk-table__body">
                {psfaAccessDialog.users.length === 0 ? (
                  <tr className="govuk-table__row">
                    <td className="govuk-table__cell" colSpan={4}>
                      No users found
                    </td>
                  </tr>
                ) : (
                  PSFA_ACCESS_GROUPS.map((group) => {
                    const groupUsers = psfaAccessDialog.users.filter(
                      (user) => getPsfaAccessGroupKey(user) === group.key,
                    );
                    if (groupUsers.length === 0) return null;
                    return (
                      <Fragment key={group.key}>
                        <tr className="govuk-table__row">
                          <th className="govuk-table__header" colSpan={4} scope="rowgroup">
                            {group.label}
                          </th>
                        </tr>
                        {groupUsers.map((user, index) => (
                          <tr
                            className="govuk-table__row"
                            key={`${group.key}-${getPsfaAccessPerson(user)}-${index}`}
                          >
                            <td className="govuk-table__cell">{getPsfaAccessPerson(user)}</td>
                            <td className="govuk-table__cell">{formatRoleLabel(user.role)}</td>
                            <td className="govuk-table__cell">
                              {formatAuditDateTime(user.lastViewedAt)}
                            </td>
                            <td className="govuk-table__cell">
                              {formatAuditDateTime(user.lastEditedAt)}
                            </td>
                          </tr>
                        ))}
                      </Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
            <MyFormsDownloadActionRow>
              <button
                type="button"
                className="govuk-button govuk-button--secondary"
                data-module="govuk-button"
                onClick={() => setPsfaAccessDialog(null)}
              >
                Close users
              </button>
            </MyFormsDownloadActionRow>
          </MyFormsDownloadDialog>
        </AdminFeedbackBackdrop>
      )}
      {isLoading && !isAnyFastToggleBusy && !suppressOverviewLoading && !cdrFiles ? (
        <PageTemplate title="">
          <h2 className="govuk-heading-m">Loading...</h2>
        </PageTemplate>
      ) : (
        <PageTemplate
          title={showMyForms ? (isAdminView ? 'Admin' : 'My Forms') : title}
          titleSuffix={
            tenant === 'psfa' &&
            !showMyForms &&
            !isPreviewMode &&
            selectedFormId &&
            ['owner', 'editor', 'admin'].includes(String(data?.role || '').toLowerCase()) &&
            ['active', 'rejected'].includes(String(formState || '').toLowerCase()) &&
            !isPastLondon(formCloseAt) && (
              <>
                <InlineExcelActionButton
                  type="button"
                  className="govuk-!-margin-left-2"
                  aria-label="Upload CDR workbook"
                  title="Upload CDR workbooks or drop XLSX files here"
                  onClick={() => setCdrUploadFiles([])}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault();
                    setCdrUploadFiles(Array.from(event.dataTransfer.files));
                  }}
                >
                  <UploadIcon />
                </InlineExcelActionButton>
              </>
            )
          }
          subTitle={showMyForms ? undefined : showSubTitle}
          suppressLoadingOverlay={isAnyFastToggleBusy || suppressOverviewLoading || !!cdrFiles}
          titleRight={
            showMyForms && isAdminView
              ? adminRoleFormId && (
                  <Link
                    className="govuk-button govuk-button--green"
                    data-module="govuk-button"
                    data-prevent-double-click="true"
                    to={adminPreviewPath}
                    onClick={() => setSelectedFormIdInSession(tenant || '', adminRoleFormId)}
                  >
                    Preview Form
                  </Link>
                )
              : undefined
          }
          beforeTitle={
            !showMyForms &&
              (showPerformanceCaptureBanner ? (
                  <Notification
                    header="Performance Capture"
                    content="Your performance capture form is up to date"
                  >
                    {isActiveScsQuarterAwaitingApproval && (
                      <p className="govuk-body">
                        This form is awaiting approval by {scsApproverEmail}. To change the approver,
                        select{' '}
                        <Link
                          to={`${tenantBasePath}/${config.urls.summary}?check_answers=1&formId=${encodeURIComponent(scsBannerForm.formId)}`}
                          className="govuk-link"
                        >
                          Check your answers
                        </Link>{' '}
                        and submit the form again.
                      </p>
                    )}
                  </Notification>
                ) : (
                  formattedDeadline && (
                    <Notification
                      header="Submission Deadline"
                      content={
                        strictDeadline
                          ? `This form will close on ${formattedDeadline}`
                          : submissionDeadlineBannerText ||
                            `The submission deadline is ${formattedDeadline}`
                      }
                      className={deadlineBannerClassName || undefined}
                    />
                  )
                ))
          }
        >
          <div data-testid="overview">
            {cdrUploadFiles && (
              <ExcelUploadDialog
                initialFiles={cdrUploadFiles}
                onClose={() => setCdrUploadFiles(undefined)}
                onFiles={setCdrFiles}
              />
            )}
            {cdrFiles && (
              <PsfaCdrImport
                files={cdrFiles}
                onClose={() => setCdrFiles(undefined)}
                onImported={() => setCdrFiles(undefined)}
              />
            )}
            {data?.notification?.header && data?.notification?.content && (
              <Notification header={data.notification.header} content={data.notification.content} />
            )}

            {showMyForms ? (
              <>
                {isAdminView && <ManageValuesButton />}
                {!isAdminView && (
                  <button
                    type="button"
                    className="govuk-button govuk-button--green"
                    data-module="govuk-button"
                    data-prevent-double-click="true"
                    onClick={handleStartNewForm}
                    disabled={isStrictDeadlineExpired}
                  >
                    Start a new form
                  </button>
                )}
                {!isAdminView && awaitingSignOffCount > 0 && (
                  <Notification
                    header="Awaiting sign-off"
                    content={awaitingSignOffMessage}
                    className="govuk-notification-banner--info"
                  />
                )}
                {isAdminView &&
                  ((hasLoadedFeedbackAvailability && hasFeedbackEntries) ||
                    showSqlReportsButton ||
                    showFunctionMembersButton ||
                    showDataErrorButton ||
                    showRewardTransferOwnershipButton) && (
                    <AdminActionButtons>
                      {showSqlReportsButton && (
                        <button
                          type="button"
                          className="govuk-button govuk-button--secondary govuk-!-margin-bottom-0"
                          data-module="govuk-button"
                          onClick={() => void handleOpenSqlReportsModal()}
                          disabled={isSqlReportsLoading}
                        >
                          {isSqlReportsLoading && !isSqlReportsModalOpen
                            ? 'Loading...'
                            : 'SQL Reports'}
                        </button>
                      )}
                      {hasLoadedFeedbackAvailability && hasFeedbackEntries && (
                        <button
                          type="button"
                          className="govuk-button govuk-button--secondary govuk-!-margin-bottom-0"
                          data-module="govuk-button"
                          onClick={() => void handleOpenFeedbackModal()}
                          disabled={isFeedbackLoading}
                        >
                          {isFeedbackLoading && !isFeedbackModalOpen
                            ? 'Loading...'
                            : 'View Feedback'}
                        </button>
                      )}
                      {showFunctionMembersButton && (
                        <button
                          type="button"
                          className="govuk-button govuk-button--secondary govuk-!-margin-bottom-0"
                          data-module="govuk-button"
                          onClick={() => void handleOpenFunctionMembersModal()}
                          disabled={functionMembersQuery.isFetching}
                        >
                          {functionMembersQuery.isFetching && !isFunctionMembersModalOpen
                            ? 'Loading...'
                            : 'Function Members'}
                        </button>
                      )}
                      {showDataErrorButton && (
                        <button
                          type="button"
                          className="govuk-button govuk-button--secondary govuk-!-margin-bottom-0"
                          data-module="govuk-button"
                          onClick={() => void handleOpenDataErrorModal()}
                          disabled={isDataErrorLoading}
                        >
                          {isDataErrorLoading && !isDataErrorModalOpen
                            ? 'Loading...'
                            : dataErrorButtonLabel}
                        </button>
                      )}
                      {showRewardTransferOwnershipButton && (
                        <button
                          type="button"
                          className="govuk-button govuk-button--secondary govuk-!-margin-bottom-0"
                          data-module="govuk-button"
                          onClick={() => void handleOpenRewardTransferOwnershipModal()}
                          disabled={isRewardTransferOwnershipLoading}
                        >
                          {isRewardTransferOwnershipLoading && !isRewardTransferOwnershipModalOpen
                            ? 'Loading...'
                            : 'Transfer ownership'}
                        </button>
                      )}
                    </AdminActionButtons>
                  )}
                {isAdminView && (
                  <details
                    className="govuk-details govuk-!-margin-bottom-5"
                    open
                    data-testid="admin-kpi-details"
                  >
                    <summary className="govuk-details__summary">
                      <span className="govuk-details__summary-text">KPI summary</span>
                    </summary>
                    <div className="govuk-details__text">
                      {showRewardSpendKpis && (
                        <div className="govuk-form-group govuk-!-margin-bottom-4">
                          <label
                            className="govuk-label govuk-label--s"
                            htmlFor="reward-kpi-financial-year"
                          >
                            Financial year
                          </label>
                          <select
                            id="reward-kpi-financial-year"
                            className="govuk-select"
                            value={selectedRewardFinancialYear}
                            onChange={(event) => {
                              setSelectedRewardFinancialYear(Number(event.target.value));
                            }}
                          >
                            {rewardFinancialYearOptions.map((year) => (
                              <option key={year} value={year}>
                                {formatRewardFinancialYear(year)}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      <AdminKpiGrid>
                        {showPsfaAdminKpis &&
                          psfaAdminKpiCards.map((card) => (
                            <AdminKpiCard key={card.status}>
                              <AdminKpiLabel className="govuk-heading-s">
                                {card.label}
                              </AdminKpiLabel>
                              <AdminKpiValue className="govuk-heading-xl" data-testid={card.testId}>
                                {card.count}
                              </AdminKpiValue>
                            </AdminKpiCard>
                          ))}
                        {showScsAdminKpis &&
                          scsAdminKpiCards.map((card) => (
                            <AdminKpiCard key={`${card.label}-${card.status}`}>
                              <AdminKpiLabel className="govuk-heading-s">
                                {card.label}
                              </AdminKpiLabel>
                              <AdminKpiValue className="govuk-heading-xl" data-testid={card.testId}>
                                {card.count}
                              </AdminKpiValue>
                            </AdminKpiCard>
                          ))}
                        {showInProgressKpi && (
                          <AdminKpiCard>
                            <AdminKpiLabel className="govuk-heading-s">In Progress</AdminKpiLabel>
                            <AdminKpiValue
                              className="govuk-heading-xl"
                              data-testid="admin-kpi-in-progress"
                            >
                              {inProgressKpiCount}
                            </AdminKpiValue>
                          </AdminKpiCard>
                        )}
                        {showAwaitingApprovalKpi && (
                          <AdminKpiCard>
                            <AdminKpiLabel className="govuk-heading-s">
                              {awaitingApprovalKpiLabel}
                            </AdminKpiLabel>
                            <AdminKpiValue
                              className="govuk-heading-xl"
                              data-testid="admin-kpi-awaiting-approval"
                            >
                              {awaitingApprovalKpiCount}
                            </AdminKpiValue>
                          </AdminKpiCard>
                        )}
                        {!showPsfaAdminKpis && !showScsAdminKpis && (
                          <AdminKpiCard>
                            <AdminKpiLabel className="govuk-heading-s">
                              {completeKpiLabel}
                            </AdminKpiLabel>
                            <AdminKpiValue
                              className="govuk-heading-xl"
                              data-testid="admin-kpi-complete"
                            >
                              {completeKpiCount}
                            </AdminKpiValue>
                          </AdminKpiCard>
                        )}
                        {showRejectedKpi && (
                          <AdminKpiCard>
                            <AdminKpiLabel className="govuk-heading-s">
                              Rejected forms
                            </AdminKpiLabel>
                            <AdminKpiValue
                              className="govuk-heading-xl"
                              data-testid="admin-kpi-rejected"
                            >
                              {rejectedKpiCount}
                            </AdminKpiValue>
                          </AdminKpiCard>
                        )}
                        {showRewardSpendKpis && (
                          <>
                            {showRewardPotKpi && (
                              <AdminKpiCard>
                                <AdminKpiLabel className="govuk-heading-s">
                                  Left in R&amp;R Pot
                                </AdminKpiLabel>
                                <AdminKpiCostValue
                                  className="govuk-heading-xl"
                                  data-testid="admin-kpi-reward-overall-spend"
                                >
                                  {formatRewardSpendAmount(rewardPotRemaining)}
                                </AdminKpiCostValue>
                                <AdminKpiBreakdown aria-label="R&R pot working">
                                  <AdminKpiBreakdownLine>
                                    Budget: {formatRewardSpendAmount(REWARD_RR_POT_TOTAL)}
                                  </AdminKpiBreakdownLine>
                                  <AdminKpiBreakdownLine>- Approved Spend</AdminKpiBreakdownLine>
                                  <ul className="govuk-!-margin-top-0 govuk-!-margin-bottom-2">
                                    <li>Paid - {formatRewardSpendAmount(rewardPaidSpendTotal)}</li>
                                    <li>
                                      Pending Payment -{' '}
                                      {formatRewardSpendAmount(rewardPendingPaymentTotal)}
                                    </li>
                                  </ul>
                                  <AdminKpiBreakdownLine>
                                    (Pending Review:{' '}
                                    {formatRewardSpendAmount(rewardOverallPendingSpend)})
                                  </AdminKpiBreakdownLine>
                                </AdminKpiBreakdown>
                              </AdminKpiCard>
                            )}
                            {rewardFunctionSpendCards.map((card) => (
                              <AdminKpiCard key={card.rewardFunction}>
                                <AdminKpiFunctionLabel className="govuk-heading-s">
                                  {card.rewardFunction}
                                </AdminKpiFunctionLabel>
                                <AdminKpiSubLabel>Total R&amp;R spend</AdminKpiSubLabel>
                                <AdminKpiCostValue
                                  className="govuk-heading-xl"
                                  data-testid={`admin-kpi-reward-function-spend-${card.rewardFunction}`}
                                >
                                  {formatRewardSpendAmount(card.approved)}
                                </AdminKpiCostValue>
                                <AdminKpiBreakdown
                                  aria-label={`${card.rewardFunction} spend working`}
                                >
                                  <AdminKpiBreakdownLine>
                                    Approved Spend {formatRewardSpendAmount(card.approved)}
                                  </AdminKpiBreakdownLine>
                                  <ul className="govuk-!-margin-top-0 govuk-!-margin-bottom-2">
                                    <li>Paid: {formatRewardSpendAmount(card.paid)}</li>
                                    <li>
                                      Pending Payment:{' '}
                                      {formatRewardSpendAmount(card.approved - card.paid)}
                                    </li>
                                  </ul>
                                  <AdminKpiBreakdownLine>
                                    (Pending Review: {formatRewardSpendAmount(card.pending)})
                                  </AdminKpiBreakdownLine>
                                </AdminKpiBreakdown>
                              </AdminKpiCard>
                            ))}
                          </>
                        )}
                      </AdminKpiGrid>
                    </div>
                  </details>
                )}
                {adminDownloadError && (
                  <Notification
                    header="Error"
                    content={adminDownloadError}
                    className="govuk-notification-banner--error"
                  />
                )}
                {psfaAuditError && (
                  <Notification
                    header="Error"
                    content={psfaAuditError}
                    className="govuk-notification-banner--error"
                  />
                )}
                {psfaAccessError && (
                  <Notification
                    header="Error"
                    content={psfaAccessError}
                    className="govuk-notification-banner--error"
                  />
                )}
                {showRewardRoleSelect && (
                  <div className="govuk-form-group govuk-!-margin-bottom-4">
                    <label className="govuk-label" htmlFor="reward-admin-role">
                      Role
                    </label>
                    <select
                      id="reward-admin-role"
                      className="govuk-select"
                      value={effectiveRewardAdminRole}
                      onChange={(event) => {
                        const role = event.target.value === 'cos' ? 'cos' : 'function';
                        setSelectedRewardAdminRole(role);
                        setStoredRewardAdminRole(role);
                      }}
                    >
                      <option value="function">Function approver</option>
                      <option value="cos">COS approver</option>
                    </select>
                  </div>
                )}
                {isRewardAdminView && (
                  <div className="govuk-form-group govuk-!-margin-bottom-4">
                    <label className="govuk-label" htmlFor="reward-admin-search">
                      Search
                    </label>
                    <div id="reward-admin-search-hint" className="govuk-hint">
                      Search by form ID, nominee, nominator, identifier or status
                    </div>
                    <input
                      id="reward-admin-search"
                      className="govuk-input govuk-!-width-one-half"
                      type="search"
                      value={rewardAdminSearchTerm}
                      onChange={(event) => setRewardAdminSearchTerm(event.target.value)}
                      aria-describedby="reward-admin-search-hint"
                    />
                  </div>
                )}
                {showScsAdminKpis && (
                  <div className="govuk-form-group govuk-!-margin-bottom-4">
                    <label className="govuk-label" htmlFor="scs-admin-search">
                      Search
                    </label>
                    <div id="scs-admin-search-hint" className="govuk-hint">
                      Search by Staff Name, reporting period or status
                    </div>
                    <input
                      id="scs-admin-search"
                      className="govuk-input govuk-!-width-one-half"
                      type="search"
                      value={scsAdminSearchTerm}
                      onChange={(event) => setScsAdminSearchTerm(event.target.value)}
                      aria-describedby="scs-admin-search-hint"
                    />
                  </div>
                )}
                {showRewardFunctionFilterSelect && (
                  <div
                    className="govuk-!-margin-bottom-4"
                    style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}
                  >
                    <div className="govuk-form-group govuk-!-margin-bottom-0">
                      <label className="govuk-label" htmlFor="reward-function-filter">
                        Function
                      </label>
                      <select
                        id="reward-function-filter"
                        className="govuk-select"
                        value={selectedRewardFunctionFilter}
                        onChange={(event) => {
                          setSelectedRewardFunctionFilter(String(event.target.value || '').trim());
                        }}
                      >
                        <option value={REWARD_FUNCTION_ADMIN_ALL_VALUE}>
                          {rewardFunctionAllOptionLabel}
                        </option>
                        {rewardFunctionFilterOptions.map((rewardFunction) => (
                          <option key={rewardFunction} value={rewardFunction}>
                            {rewardFunction}
                          </option>
                        ))}
                      </select>
                    </div>
                    {showRewardTypeFilterSelect && (
                      <div className="govuk-form-group govuk-!-margin-bottom-0">
                        <label className="govuk-label" htmlFor="reward-type-filter">
                          Type
                        </label>
                        <select
                          id="reward-type-filter"
                          className="govuk-select"
                          value={selectedRewardTypeFilter}
                          onChange={(event) => {
                            setSelectedRewardTypeFilter(String(event.target.value || '').trim());
                          }}
                        >
                          <option value={REWARD_TYPE_FILTER_ALL_VALUE}>All reward types</option>
                          <option value={REWARD_TYPE_FILTER_VOUCHER_VALUE}>Voucher</option>
                          <option value={REWARD_TYPE_FILTER_IYB_VALUE}>In Year Bonus</option>
                        </select>
                      </div>
                    )}
                  </div>
                )}
                {isAdminView &&
                  (hasReportPeriodQuestion || showDepartmentFilter || showOrganisationFilter) && (
                    <div
                      className={`govuk-form-group${reportPeriodFilterError ? ' govuk-form-group--error' : ''}`}
                    >
                      <h2 className="govuk-heading-s govuk-!-margin-bottom-2">Filters</h2>
                      {hasReportPeriodQuestion && (
                        <>
                          {reportPeriodFilterError && (
                            <p className="govuk-error-message">
                              <span className="govuk-visually-hidden">Error:</span>{' '}
                              {reportPeriodFilterError}
                            </p>
                          )}
                        </>
                      )}
                      <div
                        style={{
                          display: 'flex',
                          gap: 12,
                          alignItems: 'flex-start',
                          flexWrap: 'wrap',
                        }}
                      >
                        {hasReportPeriodQuestion && (
                          <>
                            {!useScsReportingYearFilter && (
                              <div>
                                <label
                                  className="govuk-label"
                                  htmlFor="admin-report-period-quarter"
                                >
                                  Quarter
                                </label>
                                <select
                                  id="admin-report-period-quarter"
                                  className="govuk-select"
                                  value={selectedReportQuarter}
                                  onChange={(event) => {
                                    setSelectedReportQuarter(
                                      String(event.target.value || '').trim(),
                                    );
                                  }}
                                >
                                  <option value="">Select quarter</option>
                                  {reportQuarterOptions.map((quarter) => (
                                    <option key={quarter} value={quarter}>
                                      {quarter}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                            <div
                              style={
                                useScsReportingYearFilter
                                  ? { width: 180, maxWidth: '100%' }
                                  : undefined
                              }
                            >
                              <label className="govuk-label" htmlFor="admin-report-period-year">
                                Reporting Year
                              </label>
                              <select
                                id="admin-report-period-year"
                                className="govuk-select"
                                style={
                                  useScsReportingYearFilter
                                    ? { width: '100%', minWidth: 0, boxSizing: 'border-box' }
                                    : undefined
                                }
                                value={selectedReportYear}
                                onChange={(event) => {
                                  setSelectedReportYear(String(event.target.value || '').trim());
                                }}
                              >
                                <option value="">Select year</option>
                                {reportYearOptions.map((year) => (
                                  <option key={year} value={year}>
                                    {year}
                                  </option>
                                ))}
                              </select>
                            </div>
                            {useScsReportingYearFilter && (
                              <>
                                <div style={{ width: 200, maxWidth: '100%' }}>
                                  <label
                                    className="govuk-label"
                                    htmlFor="admin-report-period-reporting-quarter"
                                  >
                                    Reporting Quarter
                                  </label>
                                  <select
                                    id="admin-report-period-reporting-quarter"
                                    className="govuk-select"
                                    style={{ width: '100%', minWidth: 0, boxSizing: 'border-box' }}
                                    value={selectedReportQuarter}
                                    onChange={(event) => {
                                      setSelectedReportQuarter(
                                        String(event.target.value || '').trim(),
                                      );
                                    }}
                                  >
                                    <option value="">Select quarter</option>
                                    {scsReportQuarterOptions.map((quarter) => (
                                      <option key={quarter} value={quarter}>
                                        {quarter}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div style={{ width: 218, maxWidth: '100%' }}>
                                  <label className="govuk-label" htmlFor="scs-admin-status-filter">
                                    Status
                                  </label>
                                  <select
                                    id="scs-admin-status-filter"
                                    className="govuk-select"
                                    style={{ width: '100%', minWidth: 0, boxSizing: 'border-box' }}
                                    value={selectedScsStatus}
                                    onChange={(event) => {
                                      setSelectedScsStatus(String(event.target.value || '').trim());
                                    }}
                                  >
                                    <option value="">Select status</option>
                                    {scsStatusOptions.map((status) => (
                                      <option key={status} value={status}>
                                        {status}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </>
                            )}
                          </>
                        )}
                        {showDepartmentFilter && (
                          <div>
                            <label className="govuk-label" htmlFor="admin-department-filter">
                              {adminDepartmentFilterLabel}
                            </label>
                            <select
                              id="admin-department-filter"
                              className="govuk-select"
                              value={selectedDepartment}
                              onChange={(event) => {
                                setSelectedDepartment(String(event.target.value || '').trim());
                              }}
                            >
                              <option value="">{adminDepartmentFilterPlaceholder}</option>
                              {departmentOptions.map((department) => (
                                <option key={department} value={department}>
                                  {department}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        {showOrganisationFilter && (
                          <div>
                            <label className="govuk-label" htmlFor="admin-organisation-filter">
                              {adminOrganisationFilterLabel}
                            </label>
                            <select
                              id="admin-organisation-filter"
                              className="govuk-select"
                              value={selectedOrganisation}
                              onChange={(event) => {
                                setSelectedOrganisation(String(event.target.value || '').trim());
                              }}
                            >
                              <option value="">{adminOrganisationFilterPlaceholder}</option>
                              {organisationOptions.map((organisation) => (
                                <option key={organisation} value={organisation}>
                                  {organisation}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                      {hasReportPeriodQuestion && (
                        <p className="govuk-hint govuk-!-margin-top-2 govuk-!-margin-bottom-4">
                          {filteredResponseHint}
                        </p>
                      )}
                    </div>
                  )}
                {isAdminView && (
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {effectiveRewardAdminRole !== 'cos' && tenant !== 'scs' && (
                      <div className="govuk-checkboxes govuk-checkboxes--small">
                        <div className="govuk-checkboxes__item">
                          <input
                            className="govuk-checkboxes__input"
                            id={isRewardTenant ? 'admin-hide-approved' : 'admin-show-in-progress'}
                            name={isRewardTenant ? 'admin-hide-approved' : 'admin-show-in-progress'}
                            type="checkbox"
                            checked={
                              isRewardTenant ? hideRewardApprovedForms : showAdminInProgressForms
                            }
                            onChange={(event) => {
                              if (isRewardTenant) {
                                const checked = event.target.checked;
                                setHideRewardApprovedForms(checked);
                                setStoredRewardPendingApprovalOnly(checked);
                              } else {
                                setShowAdminInProgressForms(event.target.checked);
                              }
                            }}
                          />
                          <label
                            className="govuk-label govuk-checkboxes__label"
                            htmlFor={
                              isRewardTenant ? 'admin-hide-approved' : 'admin-show-in-progress'
                            }
                          >
                            {isRewardTenant ? 'Pending approval only' : 'Show in progress'}
                          </label>
                        </div>
                      </div>
                    )}
                    {showVoucherBulkUploadButton && (
                      <button
                        type="button"
                        className="govuk-button govuk-button--green"
                        data-module="govuk-button"
                        onClick={handleOpenVoucherBulkUploadModal}
                        disabled={
                          voucherBulkUploadRows.length === 0 ||
                          isVoucherBulkUploadDownloading ||
                          isVoucherBulkUploadSaving
                        }
                      >
                        Voucher bulk upload
                      </button>
                    )}
                    {tenant === 'scs' && isAdminView && (
                      <ScsReports
                        completionForms={scsAdminDownloadFilterSourceForms.map((form) => ({
                          ...form,
                          activeQuarter: getLatestAvailableScsQuarter(form, sections),
                          currentStatus: getScsStatusLabel(
                            form,
                            formConfig.formCloseAt ?? form.formCloseAt,
                            strictDeadline,
                            sections,
                          ),
                        }))}
                        year={selectedReportYear}
                        quarter={selectedReportQuarter}
                        years={reportYearOptions}
                        quartersForYear={(year) => {
                          const quarters = new Set(
                            scsAdminDownloadFilterSourceForms
                              .filter(
                                (form) =>
                                  !year ||
                                  filterPeriodsFromForm(form).some(
                                    (period) => parseReportPeriodYear(period) === year,
                                  ),
                              )
                              .flatMap((form) =>
                                scsReportQuartersFromForm(
                                  form,
                                  year,
                                  formConfig.formCloseAt ?? form.formCloseAt,
                                  strictDeadline,
                                  sections,
                                ),
                              ),
                          );
                          return SCS_REPORTING_QUARTER_OPTIONS.filter((value) =>
                            quarters.has(value),
                          );
                        }}
                        formIdsForPeriod={(year, quarter) =>
                          scsAdminDownloadFilterSourceForms
                            .filter(
                              (form) =>
                                filterPeriodsFromForm(form).some(
                                  (period) => parseReportPeriodYear(period) === year,
                                ) &&
                                scsReportQuartersFromForm(
                                  form,
                                  year,
                                  formConfig.formCloseAt ?? form.formCloseAt,
                                  strictDeadline,
                                  sections,
                                ).includes(quarter),
                            )
                            .map((form) => form.formId)
                        }
                        token={async () => {
                          const token = (await fetchJWT()).idToken;
                          if (!token) throw new Error('Not authenticated');
                          return token;
                        }}
                      />
                    )}
                    <AdminDownloadButton
                      type="button"
                      className="govuk-button govuk-button--secondary"
                      data-module="govuk-button"
                      data-highlighted={isAdminPdfHighlighted ? 'true' : 'false'}
                      onClick={handleOpenAdminDownloadModal}
                      highlighted={isAdminPdfHighlighted}
                    >
                      {activeAdminDownload === 'bulk-pdf'
                        ? 'Preparing files...'
                        : activeAdminDownload.startsWith('bulk-')
                          ? 'Downloading...'
                          : 'Download Responses'}
                    </AdminDownloadButton>
                    {isRewardTenant && effectiveRewardAdminRole === 'cos' && (
                      <button
                        type="button"
                        className="govuk-button govuk-button--secondary"
                        data-module="govuk-button"
                        onClick={() => void downloadRewardCosReconciliationReport()}
                        disabled={isRewardCosReconciliationDownloading}
                      >
                        {isRewardCosReconciliationDownloading
                          ? 'Downloading...'
                          : 'Reconciliation Report'}
                      </button>
                    )}
                    {!isRewardTenant && (
                      <button
                        type="button"
                        className="govuk-button"
                        data-module="govuk-button"
                        onClick={() => void handleAdminProgressDownload()}
                        disabled={activeAdminDownload.length > 0}
                      >
                        {activeAdminDownload === 'progress' ? 'Downloading...' : 'Progress Report'}
                      </button>
                    )}
                  </div>
                )}
                {createFormError && (
                  <Notification
                    header="Error"
                    content={createFormError}
                    className="govuk-notification-banner--error"
                  />
                )}
                <table
                  id={isRewardAdminView ? 'reward-admin-results' : undefined}
                  className="govuk-table overview-forms-table"
                >
                  <thead className="govuk-table__head">
                    <tr className="govuk-table__row">
                      <th
                        scope="col"
                        className="govuk-table__header"
                        aria-sort={getOverviewTableHeaderSort(overviewTableSort, 'primaryDate')}
                      >
                        {renderSortableTableHeader('primaryDate', primaryDateHeaderLabel)}
                      </th>
                      {isRewardAdminView && (
                        <th scope="col" className="govuk-table__header">
                          Submitted By
                        </th>
                      )}
                      {isRewardAdminView ? (
                        <th scope="col" className="govuk-table__header">
                          Nominee
                        </th>
                      ) : (
                        showIdentifierColumn && (
                          <th scope="col" className="govuk-table__header">
                            Identifier
                          </th>
                        )
                      )}
                      {isRewardAdminView && (
                        <th scope="col" className="govuk-table__header">
                          Type/Amount
                        </th>
                      )}
                      {showReportingPeriodColumn && (
                        <th scope="col" className="govuk-table__header">
                          Reporting Period
                        </th>
                      )}
                      {!isAdminView && (
                        <th scope="col" className="govuk-table__header">
                          Role
                        </th>
                      )}
                      <th scope="col" className="govuk-table__header">
                        Status
                      </th>
                      {showLastActiveColumn && (
                        <th
                          scope="col"
                          className="govuk-table__header"
                          aria-sort={getOverviewTableHeaderSort(overviewTableSort, 'lastActive')}
                        >
                          {renderSortableTableHeader('lastActive', 'Last Active')}
                        </th>
                      )}
                      {isAdminView ? (
                        <>
                          <th scope="col" className="govuk-table__header">
                            <span className="govuk-visually-hidden">Options</span>
                          </th>
                          {showAdminAuditColumn && (
                            <th scope="col" className="govuk-table__header">
                              <span className="govuk-visually-hidden">Audit</span>
                            </th>
                          )}
                          {showAdminUsersColumn && (
                            <th scope="col" className="govuk-table__header">
                              <span className="govuk-visually-hidden">Users</span>
                            </th>
                          )}
                          {allowUnapprove && (
                            <th scope="col" className="govuk-table__header">
                              <span className="govuk-visually-hidden">Reject</span>
                            </th>
                          )}
                          <th scope="col" className="govuk-table__header">
                            <span className="govuk-visually-hidden">Download</span>
                          </th>
                        </>
                      ) : (
                        <th scope="col" className="govuk-table__header">
                          &nbsp;
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="govuk-table__body">
                    {paginatedVisibleTableForms.map((form) => {
                      const closeAt = formConfig.formCloseAt ?? form.formCloseAt;
                      const rewardTeamNomineeNames = getRewardTeamNomineeNames(form);
                      const isRewardTeamNomination = rewardTeamNomineeNames.length > 0;
                      const rewardNominationName = getRewardNomineeNameFromIdentifier(
                        form.identifier,
                      );
                      const rewardCosNomineeName =
                        form.rewardCosNomineeName || rewardNominationName;
                      const rewardNomineeName =
                        isRewardCosAdminView && !isRewardTeamNomination
                          ? rewardCosNomineeName
                          : rewardNominationName;
                      const showUnapproveButton = allowUnapprove && canUnapproveForm(form);
                      const isRewardInfoOnlyRow = tenant === 'reward' && isRewardInfoOnlyForm(form);
                      const isRewardCosVoucherRow = isRewardCosVoucherForm(form);
                      const isRewardCosBulkUploadPendingRow =
                        isRewardCosVoucherBulkUploadPending(form);
                      const isPsfaAuditLoadingForRow =
                        isPsfaAuditLoading && psfaAuditLoadingFormId === form.formId;
                      const isPsfaAccessLoadingForRow =
                        isPsfaAccessLoading && psfaAccessLoadingFormId === form.formId;
                      const rewardCosRowActionLabel = isRewardCosVoucherRow
                        ? getRewardCosActionLabel(form)
                        : '';
                      return (
                        <tr
                          key={`${form.formId}-${form.rewardCosNomineeId || ''}`}
                          className="govuk-table__row"
                          style={isRewardInfoOnlyRow ? { backgroundColor: '#f3f2f1' } : undefined}
                        >
                          <td className="govuk-table__cell">
                            {formatDate(
                              isRewardCosAdminView
                                ? form.approvedAt
                                : isRewardAdminView
                                  ? form.submittedAt
                                  : form.createdAt,
                            )}
                          </td>
                          {isRewardAdminView && (
                            <td className="govuk-table__cell">
                              {form.rewardNominatorName || EMPTY_VALUE}
                            </td>
                          )}
                          {isRewardAdminView ? (
                            <td className="govuk-table__cell">
                              <strong>{isRewardTeamNomination ? 'Team:' : 'Individual:'}</strong>
                              <div>{rewardNomineeName || EMPTY_VALUE}</div>
                              {isRewardTeamNomination && isRewardCosAdminView && (
                                <div>{rewardCosNomineeName || EMPTY_VALUE}</div>
                              )}
                              {isRewardTeamNomination && !isRewardCosAdminView && (
                                <ul className="govuk-list govuk-list--bullet govuk-!-margin-bottom-0">
                                  {rewardTeamNomineeNames.map((name) => (
                                    <li key={name}>{name}</li>
                                  ))}
                                </ul>
                              )}
                            </td>
                          ) : (
                            showIdentifierColumn && (
                              <td className="govuk-table__cell">
                                {form.identifier || EMPTY_VALUE}
                              </td>
                            )
                          )}
                          {isRewardAdminView && (
                            <td className="govuk-table__cell">
                              <div>
                                <strong>{getRewardTableAwardTypeLabel(form)}</strong>
                              </div>
                              <div>{getRewardTableAmount(form, isRewardCosAdminView)}</div>
                            </td>
                          )}
                          {showReportingPeriodColumn && (
                            <td className="govuk-table__cell">{reportPeriodLabelFromForm(form)}</td>
                          )}
                          {!isAdminView && (
                            <td className="govuk-table__cell">{formatRoleLabel(form.role)}</td>
                          )}
                          <td className="govuk-table__cell">
                            {(() => {
                              if (isRewardCosVoucherRow) {
                                return (
                                  <span
                                    className={`govuk-tag ${getRewardCosVoucherStatusTagClass(form)}`}
                                  >
                                    <StatusTagDate>
                                      {getRewardCosVoucherStatusDate(form)}
                                    </StatusTagDate>
                                    <br />
                                    <span>{getRewardCosVoucherStatusLabel(form)}</span>
                                  </span>
                                );
                              }
                              const baseLabel = getDefaultStatusLabel(
                                form,
                                formConfig.allowUnlock,
                                hasAdminApprovalTarget,
                                closeAt,
                                strictDeadline,
                                tenant,
                                sections,
                              );
                              const label = getStatusLabel(
                                form,
                                formConfig.allowUnlock,
                                hasAdminApprovalTarget,
                                formConfig.statusOverride,
                                closeAt,
                                strictDeadline,
                                tenant,
                                sections,
                              );
                              const statusDisplay = getPsfaAdminStatusDisplay(
                                form,
                                label,
                                getStatusTagClass(baseLabel),
                                tenant,
                                isAdminView,
                              );
                              return renderStatusTag(
                                form,
                                statusDisplay.label,
                                statusDisplay.className,
                                isAdminView,
                                tenant,
                              );
                            })()}
                          </td>
                          {showLastActiveColumn && (
                            <td className="govuk-table__cell">
                              {formatDaysAgo(form.lastActive, form.createdAt)}
                            </td>
                          )}
                          {isAdminView ? (
                            <>
                              <td className="govuk-table__cell">
                                {isRewardCosVoucherRow && isRewardCosBulkUploadPendingRow ? (
                                  <div style={{ display: 'inline-flex', gap: 8 }}>
                                    <button
                                      type="button"
                                      className="govuk-button govuk-button--green"
                                      data-module="govuk-button"
                                      aria-label="Mark issued via bulk upload"
                                      title="Mark issued via bulk upload"
                                      style={{
                                        minWidth: 40,
                                        width: 40,
                                        height: 40,
                                        padding: 0,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                      onClick={() =>
                                        void saveRewardCosBulkUploadOutcome(form, 'issued')
                                      }
                                      disabled={isRewardVoucherIssueSaving}
                                    >
                                      <svg
                                        aria-hidden="true"
                                        viewBox="0 0 24 24"
                                        width="20"
                                        height="20"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <path d="M20 6 9 17l-5-5" />
                                      </svg>
                                    </button>
                                    <button
                                      type="button"
                                      className="govuk-button govuk-button--warning"
                                      data-module="govuk-button"
                                      aria-label="Mark bulk upload error"
                                      title="Mark bulk upload error"
                                      style={{
                                        minWidth: 40,
                                        width: 40,
                                        height: 40,
                                        padding: 0,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                      onClick={() =>
                                        void saveRewardCosBulkUploadOutcome(form, 'error')
                                      }
                                      disabled={isRewardVoucherIssueSaving}
                                    >
                                      <svg
                                        aria-hidden="true"
                                        viewBox="0 0 24 24"
                                        width="20"
                                        height="20"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <path d="M18 6 6 18" />
                                        <path d="m6 6 12 12" />
                                      </svg>
                                    </button>
                                  </div>
                                ) : isRewardCosVoucherRow ? (
                                  <button
                                    type="button"
                                    className="govuk-button govuk-button--secondary"
                                    data-module="govuk-button"
                                    aria-label={rewardCosRowActionLabel}
                                    title={rewardCosRowActionLabel}
                                    style={{
                                      minWidth: 40,
                                      width: 40,
                                      height: 40,
                                      padding: 0,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                    onClick={() => handleOpenRewardVoucherIssueModal(form)}
                                  >
                                    <svg
                                      aria-hidden="true"
                                      viewBox="0 0 24 24"
                                      width="18"
                                      height="18"
                                      fill="currentColor"
                                    >
                                      <circle cx="5" cy="12" r="2" />
                                      <circle cx="12" cy="12" r="2" />
                                      <circle cx="19" cy="12" r="2" />
                                    </svg>
                                  </button>
                                ) : (
                                  <Link
                                    className="govuk-button govuk-button--secondary"
                                    data-module="govuk-button"
                                    data-prevent-double-click="true"
                                    aria-label={getActionLabel(
                                      form,
                                      formConfig.allowUnlock,
                                      hasAdminApprovalTarget,
                                      closeAt,
                                      strictDeadline,
                                      formConfig.approvalCloseOnQuarter,
                                      tenant,
                                    )}
                                    title={getActionLabel(
                                      form,
                                      formConfig.allowUnlock,
                                      hasAdminApprovalTarget,
                                      closeAt,
                                      strictDeadline,
                                      formConfig.approvalCloseOnQuarter,
                                      tenant,
                                    )}
                                    style={{
                                      minWidth: 40,
                                      width: 40,
                                      height: 40,
                                      padding: 0,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                    to={withQueryParam(
                                      withFormIdParam(
                                        getFormLink(
                                          form,
                                          formConfig.allowUnlock,
                                          hasAdminApprovalTarget,
                                          closeAt,
                                          tenant || '',
                                          strictDeadline,
                                          formConfig.approvalCloseOnQuarter,
                                        ),
                                        form.formId,
                                      ),
                                      'g',
                                      selectedApprovalGroupParam,
                                    )}
                                    state={{ returnTo: returnToFormsPath }}
                                    onClick={() =>
                                      setSelectedFormIdInSession(tenant || '', form.formId)
                                    }
                                  >
                                    <svg
                                      aria-hidden="true"
                                      viewBox="0 0 24 24"
                                      width="18"
                                      height="18"
                                      fill="currentColor"
                                    >
                                      <circle cx="5" cy="12" r="2" />
                                      <circle cx="12" cy="12" r="2" />
                                      <circle cx="19" cy="12" r="2" />
                                    </svg>
                                  </Link>
                                )}
                              </td>
                              {showAdminAuditColumn && (
                                <td className="govuk-table__cell">
                                  <button
                                    type="button"
                                    className="govuk-button govuk-button--secondary"
                                    data-module="govuk-button"
                                    aria-label={
                                      isPsfaAuditLoadingForRow ? 'Loading audit' : 'Show audit'
                                    }
                                    title={
                                      isPsfaAuditLoadingForRow ? 'Loading audit' : 'Show audit'
                                    }
                                    style={{
                                      minWidth: 40,
                                      width: 40,
                                      height: 40,
                                      padding: 0,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                    onClick={() => void openPsfaAuditModal(form)}
                                    disabled={isPsfaAuditLoadingForRow}
                                  >
                                    <svg
                                      aria-hidden="true"
                                      viewBox="0 0 24 24"
                                      width="18"
                                      height="18"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <path d="M4 6h16" />
                                      <path d="M4 12h16" />
                                      <path d="M4 18h10" />
                                      <path d="M18 16v4" />
                                      <path d="M16 18h4" />
                                    </svg>
                                  </button>
                                </td>
                              )}
                              {showAdminUsersColumn && (
                                <td className="govuk-table__cell">
                                  <button
                                    type="button"
                                    className="govuk-button govuk-button--secondary"
                                    data-module="govuk-button"
                                    aria-label={
                                      isPsfaAccessLoadingForRow ? 'Loading users' : 'Show users'
                                    }
                                    title={
                                      isPsfaAccessLoadingForRow ? 'Loading users' : 'Show users'
                                    }
                                    style={{
                                      minWidth: 40,
                                      width: 40,
                                      height: 40,
                                      padding: 0,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                    onClick={() => void openPsfaAccessModal(form)}
                                    disabled={isPsfaAccessLoadingForRow}
                                  >
                                    <svg
                                      aria-hidden="true"
                                      viewBox="0 0 24 24"
                                      width="18"
                                      height="18"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <circle cx="12" cy="8" r="4" />
                                      <path d="M4 21a8 8 0 0 1 16 0" />
                                    </svg>
                                  </button>
                                </td>
                              )}
                              {allowUnapprove && (
                                <td className="govuk-table__cell">
                                  {showUnapproveButton ? (
                                    <button
                                      type="button"
                                      className="govuk-button govuk-button--warning"
                                      data-module="govuk-button"
                                      aria-label="Reject"
                                      title="Reject"
                                      style={{
                                        minWidth: 40,
                                        width: 40,
                                        height: 40,
                                        padding: 0,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                      onClick={() => handleOpenUnapproveModal(form)}
                                      disabled={isUnapproving}
                                    >
                                      <svg
                                        aria-hidden="true"
                                        viewBox="0 0 24 24"
                                        width="20"
                                        height="20"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <circle cx="12" cy="12" r="9" />
                                        <path d="m9 9 6 6" />
                                        <path d="m15 9-6 6" />
                                      </svg>
                                    </button>
                                  ) : null}
                                </td>
                              )}
                              <td className="govuk-table__cell">
                                <button
                                  type="button"
                                  className="govuk-button govuk-button--secondary"
                                  data-module="govuk-button"
                                  aria-label="Download"
                                  title="Download"
                                  style={{
                                    minWidth: 40,
                                    width: 40,
                                    height: 40,
                                    padding: 0,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                  disabled={
                                    activeAdminDownload.length > 0 || isMyFormsDownloadSubmitting
                                  }
                                  onClick={() => handleOpenMyFormsDownloadModal(form)}
                                >
                                  <svg
                                    aria-hidden="true"
                                    viewBox="0 0 24 24"
                                    width="18"
                                    height="18"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path d="M12 3v11" />
                                    <path d="m8 10 4 4 4-4" />
                                    <path d="M4 20h16" />
                                  </svg>
                                </button>
                              </td>
                            </>
                          ) : (
                            <td className="govuk-table__cell">
                              <MyFormsRowActions>
                                <Link
                                  className="govuk-button"
                                  data-module="govuk-button"
                                  data-prevent-double-click="true"
                                  to={withQueryParam(
                                    withFormIdParam(
                                      getFormLink(
                                        form,
                                        formConfig.allowUnlock,
                                        hasAdminApprovalTarget,
                                        closeAt,
                                        tenant || '',
                                        strictDeadline,
                                        formConfig.approvalCloseOnQuarter,
                                      ),
                                      form.formId,
                                    ),
                                    'g',
                                    selectedApprovalGroupParam,
                                  )}
                                  state={{ returnTo: returnToFormsPath }}
                                  onClick={() =>
                                    setSelectedFormIdInSession(tenant || '', form.formId)
                                  }
                                >
                                  {getActionLabel(
                                    form,
                                    formConfig.allowUnlock,
                                    hasAdminApprovalTarget,
                                    closeAt,
                                    strictDeadline,
                                    formConfig.approvalCloseOnQuarter,
                                    tenant,
                                  )}
                                </Link>
                                <span style={{ display: 'inline-flex', gap: 12 }}>
                                  {tenant === 'psfa' && (
                                    <button
                                      type="button"
                                      className="govuk-button govuk-button--secondary"
                                      data-module="govuk-button"
                                      aria-label="Download"
                                      title="Download"
                                      style={{
                                        minWidth: 40,
                                        width: 40,
                                        height: 40,
                                        padding: 0,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                      disabled={isMyFormsDownloadSubmitting}
                                      onClick={() => handleOpenMyFormsDownloadModal(form)}
                                    >
                                      <svg
                                        aria-hidden="true"
                                        viewBox="0 0 24 24"
                                        width="18"
                                        height="18"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <path d="M12 3v11" />
                                        <path d="m8 10 4 4 4-4" />
                                        <path d="M4 20h16" />
                                      </svg>
                                    </button>
                                  )}
                                  {canDeleteMyFormsDraft(form) ? (
                                    <MyFormsDeleteButton
                                      type="button"
                                      className="govuk-button govuk-button--warning"
                                      data-module="govuk-button"
                                      aria-label="Delete form"
                                      title="Delete form"
                                      onClick={() => handleOpenMyFormsDeleteModal(form)}
                                      disabled={isMyFormsDeleting}
                                    >
                                      <svg
                                        aria-hidden="true"
                                        viewBox="0 0 24 24"
                                        width="18"
                                        height="18"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <path d="M3 6h18" />
                                        <path d="M8 6V4h8v2" />
                                        <path d="M19 6l-1 14H6L5 6" />
                                        <path d="M10 11v6" />
                                        <path d="M14 11v6" />
                                      </svg>
                                    </MyFormsDeleteButton>
                                  ) : tenant !== 'psfa' ? (
                                    <button
                                      type="button"
                                      className="govuk-button govuk-button--secondary"
                                      data-module="govuk-button"
                                      onClick={() => handleOpenMyFormsDownloadModal(form)}
                                    >
                                      Download
                                    </button>
                                  ) : null}
                                </span>
                              </MyFormsRowActions>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {isRewardAdminView && (
                  <div className="reward-admin-pagination" data-testid="reward-admin-pagination">
                    <div className="reward-admin-pagination__page-size">
                      <div className="govuk-form-group govuk-!-margin-bottom-2">
                        <label
                          className="govuk-label govuk-label--s"
                          htmlFor="reward-admin-page-size"
                        >
                          Responses per page
                        </label>
                        <select
                          id="reward-admin-page-size"
                          className="govuk-select"
                          value={rewardAdminPageSize}
                          onChange={(event) => {
                            setRewardAdminPageSize(
                              Number(event.target.value) as RewardAdminPageSize,
                            );
                            setRewardAdminPage(1);
                          }}
                        >
                          {REWARD_ADMIN_PAGE_SIZES.map((pageSize) => (
                            <option key={pageSize} value={pageSize}>
                              {pageSize}
                            </option>
                          ))}
                        </select>
                      </div>
                      <p
                        className="govuk-body-s govuk-!-margin-bottom-0"
                        aria-live="polite"
                        data-testid="reward-admin-pagination-summary"
                      >
                        Showing {rewardAdminFirstResult} to {rewardAdminLastResult} of{' '}
                        {visibleTableForms.length} responses
                      </p>
                    </div>
                    {rewardAdminTotalPages > 1 && (
                      <nav className="govuk-pagination" aria-label="Response pages">
                        {rewardAdminPage > 1 && (
                          <div className="govuk-pagination__prev">
                            <button
                              type="button"
                              className="govuk-link govuk-pagination__link reward-admin-pagination__link-button"
                              onClick={() => setRewardAdminPage((page) => page - 1)}
                            >
                              <span className="govuk-pagination__link-title">Previous</span>
                            </button>
                          </div>
                        )}
                        <ul className="govuk-pagination__list">
                          {rewardAdminPaginationItems.map((item, index) =>
                            item === 'ellipsis' ? (
                              <li
                                key={`ellipsis-${index}`}
                                className="govuk-pagination__item govuk-pagination__item--ellipses"
                              >
                                …
                              </li>
                            ) : (
                              <li
                                key={item}
                                className={`govuk-pagination__item${
                                  item === rewardAdminPage ? ' govuk-pagination__item--current' : ''
                                }`}
                              >
                                <button
                                  type="button"
                                  className="govuk-link govuk-pagination__link reward-admin-pagination__link-button"
                                  aria-label={`Page ${item}`}
                                  aria-current={item === rewardAdminPage ? 'page' : undefined}
                                  onClick={() => setRewardAdminPage(item)}
                                >
                                  {item}
                                </button>
                              </li>
                            ),
                          )}
                        </ul>
                        {rewardAdminPage < rewardAdminTotalPages && (
                          <div className="govuk-pagination__next">
                            <button
                              type="button"
                              className="govuk-link govuk-pagination__link reward-admin-pagination__link-button"
                              onClick={() => setRewardAdminPage((page) => page + 1)}
                            >
                              <span className="govuk-pagination__link-title">Next</span>
                            </button>
                          </div>
                        )}
                      </nav>
                    )}
                  </div>
                )}
              </>
            ) : (
              <ol className="govuk-list" data-testid="overview-sectionList">
                {nilSectionError && (
                  <li>
                    <p role="alert" className="govuk-error-message">
                      {nilSectionError}
                    </p>
                  </li>
                )}
                {visibleSections.map((section, sectionIndex) => (
                  <SectionListWrapper
                    showSections={showSections}
                    section={section}
                    sectionIndex={sectionIndex}
                    key={section.slug}
                    sectionAction={renderSectionNilReturn(section)}
                  >
                    {section.subsections
                      .filter((subsection) =>
                        lockedSections[section.slug] === true ||
                        lockedSubsections[subsection.slug] === true
                          ? true
                          : hasVisiblePagesForSubsection(subsection),
                      )
                      .map((subsection, subsectionIndex) => {
                        const isSubsectionLocked =
                          lockedSections[section.slug] === true ||
                          lockedSubsections[subsection.slug] === true;
                        const status = progressData[subsection.slug] || Status.NotStarted;
                        const isNotRequired = isSubsectionNotRequired(subsection, effectiveData);
                        const enabledPagesForSlug =
                          enabledSubsections[subsection.slug]?.pages || subsection.pages || [];
                        const allPagesForSlug = subsection.pages || [];
                        const enabledVisiblePagesForSlug = enabledPagesForSlug.filter(
                          (page) => !isWorkflowOnlyPageName(page.name),
                        );
                        const visibleListPages = enabledVisiblePagesForSlug.filter(
                          (page): page is PageList => page.pageType === 'list',
                        );
                        const visibleListResponseCount = visibleListPages.reduce(
                          (total, page) => total + getListPageResponseCount(page, answers),
                          0,
                        );
                        const hasListResponses = visibleListResponseCount > 0;
                        const conditionalRouting = getConditionalChildRouting(
                          allPagesForSlug,
                          enabledVisiblePagesForSlug,
                          effectiveData || { answers: {} },
                          subsection.slug,
                        );

                        index += 1;

                        const suffix =
                          enabledVisiblePagesForSlug.length > 1
                            ? 'summary'
                            : enabledVisiblePagesForSlug[0]?.name || 'summary';
                        const hasRepeatableQuestion = enabledVisiblePagesForSlug.some(
                          (page) => page.pageType === 'list',
                        );

                        const hasAnyAnswers = enabledVisiblePagesForSlug.some((p) =>
                          Object.keys(answers).some((key) => key.startsWith(`${p.name}-`)),
                        );

                        const hasCompletedPageWithSummaryNav = enabledVisiblePagesForSlug.some(
                          (page) => {
                            if (page.navigateToSummaryOnComplete && 'questions' in page) {
                              const questions = page.questions || [];
                              return isPageCompleted(questions, answers);
                            }
                            return false;
                          },
                        );

                        const defaultHrefBase =
                          hasListResponses || hasCompletedPageWithSummaryNav
                            ? `${tenantBasePath}/${section.slug}/${subsection.slug}/summary`
                            : !hasAnyAnswers
                              ? `${tenantBasePath}/${section.slug}/${subsection.slug}`
                              : `${tenantBasePath}/${section.slug}/${subsection.slug}/${suffix}`;
                        const conditionalRoutePageName = conditionalRouting.hasChangeTypeButton
                          ? conditionalRouting.controllerStatus !== Status.Completed
                            ? conditionalRouting.controllerPageName
                            : conditionalRouting.currentChildPageName
                          : undefined;
                        const hrefBase = hasListResponses
                          ? defaultHrefBase
                          : conditionalRoutePageName
                            ? `${tenantBasePath}/${section.slug}/${subsection.slug}/${conditionalRoutePageName}`
                            : defaultHrefBase;
                        const href = isPreviewMode ? `${hrefBase}?admin=1` : hrefBase;

                        const draftsForSubsection = effectiveData?.drafts?.[subsection.slug] || [];

                        const hasDraftForPage = (pageName: string) =>
                          draftsForSubsection.some((draftId) => draftId.split(':')[0] === pageName);

                        if (isSubsectionLocked) {
                          return (
                            <li
                              key={subsection.slug}
                              className="app-task-list__item app-task-list__item--locked"
                            >
                              <span
                                data-testid={`overview-subsectionList-${subsection.slug}Locked`}
                              >
                                <span className="app-task-list__task-name--plain">
                                  {showSections
                                    ? `${sectionIndex + 1}.${subsectionIndex + 1}`
                                    : `${index}.`}
                                  &nbsp;
                                </span>
                                {subsection.title}
                              </span>
                              <strong className="app-task-list__tag govuk-tag govuk-tag--grey">
                                {status === Status.NotDue ? Status.NotDue : 'Locked'}
                              </strong>
                            </li>
                          );
                        }

                        if (isNotRequired) {
                          return (
                            <li key={subsection.slug} className="app-task-list__item">
                              <span data-testid={`overview-subsectionList-${subsection.slug}NotRequired`}>
                                <span className="app-task-list__task-name--plain">
                                  {showSections
                                    ? `${sectionIndex + 1}.${subsectionIndex + 1}`
                                    : `${index}.`}
                                  &nbsp;
                                </span>
                                {subsection.title}
                              </span>
                              <strong className="app-task-list__tag govuk-tag govuk-tag--green">
                                Not Required
                              </strong>
                            </li>
                          );
                        }

                        if (subsection.dynamicPages && enabledPagesForSlug.length > 0) {
                          return enabledPagesForSlug.map((page, dynamicPageIndex) => {
                            const extractedQuestions =
                              'questions' in page
                                ? extractConditionalQuestions(page.questions || [])
                                : [];
                            const hasResponses = extractedQuestions.some(({ name }) =>
                              Object.keys(answers).some((key) => key.endsWith(name)),
                            );
                            const pageHasDraft = hasDraftForPage(page.name);
                            const pageHasListResponses =
                              page.pageType === 'list' &&
                              getListPageResponseCount(page, answers) > 0;
                            const pageHref = pageHasListResponses
                              ? `${tenantBasePath}/${section.slug}/${subsection.slug}/summary`
                              : `${tenantBasePath}/${section.slug}/${subsection.slug}/${page.name}`;

                            const pageStatus =
                              page.pageType === 'information'
                                ? Status.Completed
                                : pageHasDraft
                                  ? Status.InProgress
                                  : hasResponses
                                    ? Status.Completed
                                    : Status.NotStarted;

                            if (!showSections) {
                              index += 1;
                            }

                            const pageLink = (
                              <Link
                                to={pageHref}
                                data-testid={`overview-subsectionList-${subsection.slug}-${page.name}Link`}
                              >
                                <span className="app-task-list__task-name">
                                  {showSections
                                    ? `${sectionIndex + 1}.${dynamicPageIndex + 1}`
                                    : `${index}.`}
                                  &nbsp;
                                </span>
                                {page.title}
                              </Link>
                            );

                            return (
                              <li
                                key={`${subsection.slug}-${page.name}`}
                                className="app-task-list__item"
                              >
                                {page.pageType === 'list' ? (
                                  <div className="app-task-list__item-main app-task-list__item-main--with-repeatable">
                                    {pageLink}
                                    <RepeatableQuestionIndicator
                                      testId={`overview-repeatable-question-${subsection.slug}-${page.name}`}
                                    />
                                  </div>
                                ) : (
                                  pageLink
                                )}
                                <Tag status={pageStatus} />
                              </li>
                            );
                          });
                        }

                        const nilPage = subsection.pages.find((p) => p.type === 'nilreturn');
                        const hasDisableCondition =
                          !!nilPage?.disabled &&
                          nilPage.disabled.length > 0 &&
                          !!nilPage.disabled[0];
                        const controllingField = nilPage?.disabled?.[0]?.field;
                        const controllingAnswer = controllingField
                          ? answers[controllingField]
                          : undefined;
                        const controllingAnswered = controllingField
                          ? hasValue(controllingAnswer)
                          : false;
                        const isNilReturnSelected = String(controllingAnswer ?? '').trim() === 'No';
                        const nilPageEnabled =
                          !!nilPage && !isPageDisabled(nilPage, { answers } as any);
                        const isFastToggleBusy =
                          fastToggleBusyBySubsection[subsection.slug] === true;
                        const nilPageHasResponses =
                          !!nilPage &&
                          'questions' in nilPage &&
                          Array.isArray(nilPage.questions) &&
                          (() => {
                            const extracted = extractConditionalQuestions(nilPage.questions);
                            if (nilPage.pageType === 'list') {
                              const grouped = getListAnswers(answers, extracted);
                              const entries = Object.values(grouped).filter((entry) =>
                                entry.some(({ answer }) => hasValue(answer)),
                              );
                              return entries.length > 0;
                            }
                            return extracted.some(({ name }) =>
                              Object.entries(answers).some(
                                ([key, value]) => key.endsWith(name) && hasValue(value),
                              ),
                            );
                          })();
                        const showFastToggle =
                          !!nilPage?.fastToggle &&
                          hasDisableCondition &&
                          !!controllingField &&
                          !nilPageHasResponses &&
                          !isPreviewMode &&
                          !isReadOnlyRole &&
                          !isApprovalActorRole;
                        const itemNumber = showSections
                          ? `${sectionIndex + 1}.${subsectionIndex + 1}`
                          : `${index}.`;
                        const subsectionLink = (
                          <Link
                            to={href}
                            data-testid={`overview-subsectionList-${subsection.slug}Link`}
                          >
                            <span className="app-task-list__task-name">
                              {itemNumber}
                              &nbsp;
                            </span>
                            {subsection.title}
                          </Link>
                        );

                        const renderSubsectionTask = (statusNode: ReactNode) => (
                          <li key={subsection.slug} className="app-task-list__item">
                            {showFastToggle ? (
                              <div
                                className={`app-task-list__item-main${
                                  hasRepeatableQuestion
                                    ? ' app-task-list__item-main--with-repeatable'
                                    : ''
                                }`}
                              >
                                {subsectionLink}
                                {hasRepeatableQuestion && (
                                  <RepeatableQuestionIndicator
                                    testId={`overview-repeatable-question-${subsection.slug}`}
                                  />
                                )}
                                <button
                                  type="button"
                                  className="app-task-list__fast-toggle"
                                  aria-label={
                                    isNilReturnSelected
                                      ? 'Clear nil return selection'
                                      : 'Mark as nil return'
                                  }
                                  onClick={() => {
                                    if (!controllingField) return;
                                    void handleNilFastToggle(
                                      subsection.slug,
                                      subsection.pages,
                                      controllingField,
                                      isNilReturnSelected,
                                    );
                                  }}
                                  disabled={isFastToggleBusy || nilSectionBusy}
                                  data-testid={`overview-fast-toggle-${subsection.slug}`}
                                >
                                  <img
                                    src={
                                      isNilReturnSelected
                                        ? NIL_FAST_TOGGLE_YES_IMAGE
                                        : NIL_FAST_TOGGLE_NO_IMAGE
                                    }
                                    alt={
                                      isNilReturnSelected ? 'Nil return selected' : 'Set nil return'
                                    }
                                  />
                                </button>
                              </div>
                            ) : hasRepeatableQuestion ? (
                              <div className="app-task-list__item-main app-task-list__item-main--with-repeatable">
                                {subsectionLink}
                                <RepeatableQuestionIndicator
                                  testId={`overview-repeatable-question-${subsection.slug}`}
                                />
                              </div>
                            ) : (
                              subsectionLink
                            )}
                            {statusNode}
                          </li>
                        );

                        let finalStatus = status;

                        if (
                          conditionalRouting.hasChangeTypeButton &&
                          conditionalRouting.controllerStatus !== Status.Completed
                        ) {
                          finalStatus = conditionalRouting.controllerStatus;
                        }

                        if (nilPage) {
                          // No disable condition: always Nil Return
                          if (!hasDisableCondition) {
                            return renderSubsectionTask(<Tag status={Status.NilReturn} />);
                          }

                          if (!controllingAnswered) {
                            return renderSubsectionTask(<Tag status={Status.NotStarted} />);
                          }

                          if (!nilPageEnabled) {
                            const disabledNilStatus =
                              isNilReturnSelected && !nilPageHasResponses
                                ? Status.NilReturn
                                : getDisabledNilReturnStatus(finalStatus);
                            return renderSubsectionTask(<Tag status={disabledNilStatus} />);
                          }

                          if (
                            nilPage &&
                            'questions' in nilPage &&
                            Array.isArray(nilPage.questions)
                          ) {
                            const extracted = extractConditionalQuestions(nilPage.questions);
                            const pageHasDraft = hasDraftForPage(nilPage.name);

                            if (nilPage.pageType === 'list') {
                              const grouped = getListAnswers(answers, extracted);
                              const entries = Object.values(grouped).filter((entry) =>
                                entry.some(({ answer }) => hasValue(answer)),
                              );
                              finalStatus = getListSubsectionStatus(
                                finalStatus,
                                entries.length > 0,
                                pageHasDraft,
                              );
                            } else {
                              const hasResponses = extracted.some(({ name }) =>
                                Object.keys(answers).some((key) => key.endsWith(name)),
                              );
                              finalStatus =
                                hasResponses && !pageHasDraft
                                  ? Status.Completed
                                  : Status.InProgress;
                            }
                          }
                        }

                        // --------------------------------------------------------------------
                        // If no nilreturn early-returned, reconcile status using enabled pages
                        // --------------------------------------------------------------------
                        if (!nilPage) {
                          const allEnabledCompleted =
                            enabledPagesForSlug.length > 0 &&
                            enabledPagesForSlug.every((page) => {
                              if (page.pageType === 'information') return true;
                              if ('questions' in page) {
                                const extracted = extractConditionalQuestions(page.questions || []);

                                if (page.pageType === 'list') {
                                  const grouped = getListAnswers(answers, extracted);
                                  // drop empty entries so deleted/blank items don't block completion
                                  const entries = Object.values(grouped).filter((entry) =>
                                    entry.some(({ answer }) => hasValue(answer)),
                                  );
                                  const pageHasDraft = hasDraftForPage(page.name);
                                  // New rule: any non-empty response + no draft marker => complete
                                  return entries.length > 0 && !pageHasDraft;
                                }

                                const pageHasDraft = hasDraftForPage(page.name);
                                return isPageCompleted(extracted, answers) && !pageHasDraft;
                              }
                              return false;
                            });

                          if (allEnabledCompleted) {
                            finalStatus = Status.Completed;
                          }
                        }

                        const listPages = enabledPagesForSlug.filter(
                          (page): page is PageList => page.pageType === 'list',
                        );
                        const listPageHasIncompleteAnswers = (page: PageList) => {
                          const extracted = extractConditionalQuestions(page.questions || []);
                          const grouped = getListAnswers(answers, extracted);
                          const entries = Object.values(grouped).filter((entry) =>
                            entry.some(({ answer }) => hasValue(answer)),
                          );
                          if (
                            page.minimumRequiredListItems &&
                            entries.length < page.minimumRequiredListItems
                          ) {
                            return true;
                          }
                          if (entries.length === 0) return false;
                          const missingRequired = entries.some((entry) =>
                            entry.some(({ question, answer }) =>
                              question?.rules?.required === true ? !hasValue(answer) : false,
                            ),
                          );
                          if (missingRequired) return true;

                          if (
                            page.name === 'points-of-contact' &&
                            extracted.some((q) => q.name === 'contacts-type')
                          ) {
                            const hasQueriesContact = Object.values(grouped).some((group) =>
                              group.some(({ question, answer }) => {
                                if (question.name !== 'contacts-type') return false;
                                if (typeof answer === 'string')
                                  return answer === 'Point of contact for queries';
                                return false;
                              }),
                            );
                            return !hasQueriesContact;
                          }

                          return false;
                        };
                        const listResponseCount = listPages.reduce(
                          (total, page) => total + getListPageResponseCount(page, answers),
                          0,
                        );
                        const listLabel =
                          listPages.find((page) => page.listLabel)?.listLabel || 'Response';
                        const listLabelText = listResponseCount === 1 ? listLabel : `${listLabel}s`;
                        const listHasDrafts = listPages.some((page) => hasDraftForPage(page.name));
                        const listHasIncomplete =
                          listHasDrafts ||
                          listPages.some((page) => listPageHasIncompleteAnswers(page));

                        const statusNode =
                          conditionalRouting.hasChangeTypeButton &&
                          conditionalRouting.controllerStatus !== Status.Completed ? (
                            <Tag status={finalStatus} />
                          ) : listResponseCount > 0 ? (
                            shouldShowListInProgress(finalStatus, listHasIncomplete) ? (
                              <Tag status={Status.InProgress} />
                            ) : (
                              <span className="govuk-tag govuk-tag--green">
                                {listResponseCount} {listLabelText}
                              </span>
                            )
                          ) : (
                            <Tag status={finalStatus} />
                          );

                        return renderSubsectionTask(statusNode);
                      })}
                  </SectionListWrapper>
                ))}

                {!!visibleSections.length && (
                  <DeclarationWrapper
                    showSections={showSections}
                    sections={visibleSections}
                    titleOverride={formConfig.submitSectionTitle || 'Submit'}
                  >
                    <li className="app-task-list__item">
                      {hasIncompleteSubsections && !isTestSubmissionMode && !isPreviewMode ? (
                        <Link
                          to={`${tenantBasePath}/${config.urls.summary}${
                            location.search
                              ? `${location.search}&check_answers=1`
                              : '?check_answers=1'
                          }`}
                          className="govuk-link"
                        >
                          {content.summaryLink}
                        </Link>
                      ) : isPreviewMode ? null : (
                        <Link
                          to={`${tenantBasePath}/${config.urls.summary}${location.search || ''}`}
                          className="govuk-link"
                        >
                          {requiresApproval &&
                          formState === 'active' &&
                          !isApprovalActorRole &&
                          !isReadOnlyRole
                            ? content.submitForApprovalLink
                            : content.summaryLink}
                        </Link>
                      )}
                    </li>
                  </DeclarationWrapper>
                )}
              </ol>
            )}
          </div>
        </PageTemplate>
      )}
    </>
  );
}
